/* Source: /home/sidsolaiyappan/skooch_ai/shared/store.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { useState, useEffect } from "react";
import { aiWelcomeMessage } from "./ai_message_helpers.js";
const _jac = {
  int: {
    bit_length(n) {
      if (n === 0) return 0;
      return Math.floor(Math.log2(Math.abs(n))) + 1;
    },
    bit_count(n) {
      let x = Math.abs(n);
      let count = 0;
      while (x) { count += x & 1; x >>= 1; }
      return count;
    },
    to_bytes(n, length, byteorder) {
      const bytes = [];
      let val = n < 0 ? (1 << (length * 8)) + n : n;
      for (let i = 0; i < length; i++) {
        bytes.push(val & 0xff);
        val >>= 8;
      }
      if (byteorder === "big") bytes.reverse();
      return bytes;
    },
    from_bytes(bytes, byteorder) {
      const b = byteorder === "big" ? bytes : [...bytes].reverse();
      let result = 0;
      for (const byte of b) result = (result << 8) | byte;
      return result;
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  float: {
    as_integer_ratio(f) {
      if (!isFinite(f)) throw new _jac.exc.ValueError("cannot convert " + f + " to integer ratio");
      if (f === 0) return [0, 1];
      let num = f, den = 1;
      while (num !== Math.floor(num)) { num *= 2; den *= 2; }
      const g = _jac.float._gcd(Math.abs(num), den);
      return [num / g, den / g];
    },
    _gcd(a, b) { while (b) { [a, b] = [b, a % b]; } return a; },
    hex(f) {
      return f.toString(16);
    },
    fromhex(s) {
      return parseFloat(s);
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  complex: {
    conjugate(c) { return {re: c.re, im: -c.im}; },
    add(a, b) { return {re: a.re + b.re, im: a.im + b.im}; },
    sub(a, b) { return {re: a.re - b.re, im: a.im - b.im}; },
    mul(a, b) { return {re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re}; },
    truediv(a, b) {
      const d = b.re * b.re + b.im * b.im;
      return {re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d};
    },
    pow(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      if (b.im === 0 && Number.isInteger(b.re)) {
        let result = {re: 1, im: 0};
        for (let i = 0; i < Math.abs(b.re); i++) result = _jac.complex.mul(result, a);
        return b.re < 0 ? _jac.complex.truediv({re: 1, im: 0}, result) : result;
      }
      const r = Math.sqrt(a.re * a.re + a.im * a.im);
      const theta = Math.atan2(a.im, a.re);
      const lnr = Math.log(r);
      const newR = Math.exp(lnr * b.re - theta * b.im);
      const newTheta = lnr * b.im + theta * b.re;
      return {re: newR * Math.cos(newTheta), im: newR * Math.sin(newTheta)};
    },
    eq(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      return a.re === b.re && a.im === b.im;
    },
    neg(a) { return {re: -a.re, im: -a.im}; },
    pos(a) { return {re: +a.re, im: +a.im}; }
  },

  str: {
    capitalize(s) {
      if (s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    title(s) {
      return s.replace(/\b\w/g, c => c.toUpperCase());
    },
    swapcase(s) {
      return [...s].map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join("");
    },
    count(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      if (sub === "") return t.length + 1;
      let count = 0, pos = 0;
      while ((pos = t.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
      return count;
    },
    find(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.indexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    rfind(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.lastIndexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    index(s, sub, start, end) {
      const idx = _jac.str.find(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(s, sub, start, end) {
      const idx = _jac.str.rfind(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(s, prefix, start, end) {
      return s.slice(start || 0, end).startsWith(prefix);
    },
    endswith(s, suffix, start, end) {
      return s.slice(start || 0, end).endsWith(suffix);
    },
    replace(s, old, new_, count) {
      if (count === undefined) return s.split(old).join(new_);
      let result = s, n = 0;
      while (n < count) {
        const idx = result.indexOf(old);
        if (idx === -1) break;
        result = result.slice(0, idx) + new_ + result.slice(idx + old.length);
        n++;
      }
      return result;
    },
    strip(s, chars) {
      if (!chars) return s.trim();
      let start = 0, end = s.length;
      while (start < end && chars.indexOf(s[start]) !== -1) start++;
      while (end > start && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(start, end);
    },
    lstrip(s, chars) {
      if (!chars) return s.trimStart();
      let start = 0;
      while (start < s.length && chars.indexOf(s[start]) !== -1) start++;
      return s.slice(start);
    },
    rstrip(s, chars) {
      if (!chars) return s.trimEnd();
      let end = s.length;
      while (end > 0 && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(0, end);
    },
    removeprefix(s, prefix) {
      return s.startsWith(prefix) ? s.slice(prefix.length) : s;
    },
    removesuffix(s, suffix) {
      return (suffix && s.endsWith(suffix)) ? s.slice(0, -suffix.length) : s;
    },
    split(s, sep, maxsplit) {
      if (sep === undefined || sep === null) {
        const parts = s.trim().split(/\s+/);
        if (maxsplit === undefined) return parts;
        if (maxsplit <= 0) return [s];
        return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(" ")].filter(x => x !== undefined);
      }
      if (maxsplit === undefined) return s.split(sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(sep)];
    },
    rsplit(s, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.str.split(s, sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      const keep = parts.length - maxsplit;
      return [parts.slice(0, keep).join(sep), ...parts.slice(keep)];
    },
    splitlines(s, keepends) {
      const lines = s.split(/(\r\n|\r|\n)/);
      const result = [];
      for (let i = 0; i < lines.length; i += 2) {
        const line = lines[i];
        const sep = lines[i + 1] || "";
        if (line || sep) result.push(keepends ? line + sep : line);
      }
      return result;
    },
    partition(s, sep) {
      const idx = s.indexOf(sep);
      if (idx === -1) return [s, "", ""];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    rpartition(s, sep) {
      const idx = s.lastIndexOf(sep);
      if (idx === -1) return ["", "", s];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    format(s, ...args) {
      let i = 0;
      return s.replace(/\{(\w*)\}/g, (match, key) => {
        if (key === "") return args[i++];
        if (!isNaN(key)) return args[parseInt(key)];
        return args[0] && typeof args[0] === "object" ? args[0][key] : match;
      });
    },
    format_map(s, mapping) {
      return s.replace(/\{(\w+)\}/g, (match, key) => key in mapping ? mapping[key] : match);
    },
    center(s, width, fillchar) {
      const fc = fillchar || " ";
      if (s.length >= width) return s;
      const total = width - s.length;
      const left = Math.floor(total / 2);
      return fc.repeat(left) + s + fc.repeat(total - left);
    },
    zfill(s, width) {
      if (s.length >= width) return s;
      const sign = (s[0] === "+" || s[0] === "-") ? s[0] : "";
      const body = sign ? s.slice(1) : s;
      return sign + "0".repeat(width - s.length) + body;
    },
    expandtabs(s, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      let result = "", col = 0;
      for (const c of s) {
        if (c === "\t") { const sp = ts - (col % ts); result += " ".repeat(sp); col += sp; }
        else if (c === "\n" || c === "\r") { result += c; col = 0; }
        else { result += c; col++; }
      }
      return result;
    },
    isnumeric(s) {
      if (s.length === 0) return false;
      return [...s].every(c => /[\d\u00B2\u00B3\u00B9\u00BC-\u00BE\u2150-\u218F]/.test(c));
    },
    istitle(s) {
      return s === _jac.str.title(s) && /[A-Za-z]/.test(s);
    },
    translate(s, table) {
      return [...s].map(c => {
        const code = c.charCodeAt(0);
        if (code in table) {
          const v = table[code];
          if (v === null) return "";
          if (typeof v === "number") return String.fromCharCode(v);
          return v;
        }
        return c;
      }).join("");
    },
    maketrans(...args) {
      const table = {};
      if (args.length === 1) {
        const mapping = args[0];
        for (const [k, v] of Object.entries(mapping)) {
          table[typeof k === "string" ? k.charCodeAt(0) : k] = v;
        }
      } else if (args.length >= 2) {
        const from_ = args[0], to_ = args[1];
        for (let i = 0; i < from_.length; i++) {
          table[from_.charCodeAt(i)] = to_.charCodeAt(i);
        }
        if (args[2]) {
          for (const c of args[2]) table[c.charCodeAt(0)] = null;
        }
      }
      return table;
    },
    mod(s, args) {
      let i = 0;
      const a = Array.isArray(args) ? args : [args];
      return s.replace(/%([sd%])/g, (m, fmt) => {
        if (fmt === "%") return "%";
        return String(a[i++]);
      });
    }
  },

  // Runtime-polymorphic method dispatch, used when the codegen cannot prove
  // the static type of a method-call receiver (e.g. a `dict`-typed prop, the
  // result of `or` widening, or a comprehension over an untyped value) and so
  // cannot pick a typed emitter. The compiler emits
  // `_jac.poly.call(obj, "name", ...args)`; `call` resolves the *runtime* type
  // of `obj` and applies the matching Python operation, forwards to a
  // same-named method on a user-defined object, or raises AttributeError. The
  // per-type tables below carry the trivial native-mapped methods inline and
  // delegate the non-trivial ones to the shared `_jac.str/list/dict/set`
  // helpers, so each operation has a single implementation and no Python
  // method name can leak verbatim into the emitted JavaScript.
  poly: {
    _typename(o) {
      if (o === null || o === undefined) return "NoneType";
      if (Array.isArray(o)) return "list";
      if (o instanceof Set) return "set";
      if (o instanceof Map) return "dict";
      if (o instanceof Uint8Array) return "bytes";
      const t = typeof o;
      if (t === "string") return "str";
      if (t === "boolean") return "bool";
      if (t === "number") return Number.isInteger(o) ? "int" : "float";
      if (t === "object") return (o.constructor && o.constructor.name) || "object";
      return t;
    },
    // --- per-runtime-type Python method tables (keyed by Python name) ---
    // Trivial native-mapped ops are inline; non-trivial ones delegate to the
    // shared `_jac.<type>` helpers (which the typed emitters also use), so the
    // typed and polymorphic paths share one implementation.
    _str: {
      lower: (s) => s.toLowerCase(),
      upper: (s) => s.toUpperCase(),
      casefold: (s) => s.toLowerCase(),
      strip: (s, chars) => {
        if (chars === undefined) return s.trim();
        let i = 0, j = s.length;
        while (i < j && chars.indexOf(s[i]) !== -1) i++;
        while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(i, j);
      },
      lstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/^\s+/, "");
        let i = 0;
        while (i < s.length && chars.indexOf(s[i]) !== -1) i++;
        return s.slice(i);
      },
      rstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/\s+$/, "");
        let j = s.length;
        while (j > 0 && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(0, j);
      }
    },
    _list: {
      append: (a, x) => { a.push(x); return null; },
      clear: (a) => { a.length = 0; return null; },
      copy: (a) => a.slice(),
      count: (a, x) => _jac.list.count(a, x),
      extend: (a, b) => { for (const v of b) a.push(v); return null; },
      index: (a, x, start, end) => _jac.list.index(a, x, start, end),
      insert: (a, i, x) => { a.splice(i, 0, x); return null; },
      pop: (a, i) => {
        if (i === undefined) return a.pop();
        const idx = i < 0 ? a.length + i : i;
        return a.splice(idx, 1)[0];
      },
      remove: (a, x) => _jac.list.remove(a, x),
      reverse: (a) => { a.reverse(); return null; },
      sort: (a, key) => _jac.list.sort(a, key)
    },
    _dict: {
      clear: (o) => _jac.dict.clear(o),
      copy: (o) => ({ ...o }),
      // Python `dict.get`: returns the stored value when the key is present
      // (even if it is null), and the default only when the key is absent.
      // A membership check is required — `o[key] ?? default_` would wrongly
      // substitute the default for a present-but-null value.
      get: (o, key, default_) =>
        Object.prototype.hasOwnProperty.call(o, key) ? o[key] : default_,
      items: (o) => Object.entries(o),
      keys: (o) => Object.keys(o),
      pop: (o, key, default_) => _jac.dict.pop(o, key, default_),
      popitem: (o) => _jac.dict.popitem(o),
      setdefault: (o, key, default_) => _jac.dict.setdefault(o, key, default_),
      update: (o, other) => {
        if (other instanceof Map) { for (const [k, v] of other) o[k] = v; }
        else if (Array.isArray(other)) { for (const [k, v] of other) o[k] = v; }
        else if (other) { for (const k of Object.keys(other)) o[k] = other[k]; }
        return null;
      },
      values: (o) => Object.values(o)
    },
    _set: {
      add: (s, x) => { s.add(x); return null; },
      clear: (s) => { s.clear(); return null; },
      copy: (s) => new Set(s),
      difference: (a, b) => _jac.set.difference(a, b),
      difference_update: (s, o) => _jac.set.difference_update(s, o),
      discard: (s, x) => { s.delete(x); return null; },
      intersection: (a, b) => _jac.set.intersection(a, b),
      intersection_update: (s, o) => _jac.set.intersection_update(s, o),
      isdisjoint: (a, b) => _jac.set.isDisjointFrom(a, b instanceof Set ? b : new Set(b)),
      issubset: (a, b) => _jac.set.isSubsetOf(a, b instanceof Set ? b : new Set(b)),
      issuperset: (a, b) => _jac.set.isSupersetOf(a, b instanceof Set ? b : new Set(b)),
      pop: (s) => _jac.set.pop(s),
      remove: (s, x) => _jac.set.remove(s, x),
      symmetric_difference: (a, b) => _jac.set.symmetricDifference(a, b),
      symmetric_difference_update: (s, o) => _jac.set.symmetric_difference_update(s, o),
      union: (a, b) => _jac.set.union(a, b),
      update: (s, o) => _jac.set.update(s, o)
    },
    // Jac dicts compile to plain objects, but interop code may hand us a real
    // Map; mirror the dict methods over the Map API for that case.
    _map: {
      clear: (m) => { m.clear(); return null; },
      copy: (m) => new Map(m),
      get: (m, key, default_) => (m.has(key) ? m.get(key) : default_),
      items: (m) => [...m.entries()],
      keys: (m) => [...m.keys()],
      pop: (m, key, default_) => {
        if (m.has(key)) { const v = m.get(key); m.delete(key); return v; }
        if (default_ !== undefined) return default_;
        throw new _jac.exc.KeyError("KeyError: " + key);
      },
      popitem: (m) => {
        if (m.size === 0) throw new _jac.exc.KeyError("dictionary is empty");
        const key = [...m.keys()].pop();
        const val = m.get(key);
        m.delete(key);
        return [key, val];
      },
      setdefault: (m, key, default_) => {
        if (!m.has(key)) m.set(key, default_ === undefined ? null : default_);
        return m.get(key);
      },
      update: (m, other) => {
        if (other instanceof Map) { for (const [k, v] of other) m.set(k, v); }
        else if (Array.isArray(other)) { for (const [k, v] of other) m.set(k, v); }
        else if (other) { for (const k of Object.keys(other)) m.set(k, other[k]); }
        return null;
      },
      values: (m) => [...m.values()]
    },
    // Single entry point. `obj.method(...args)` on a receiver whose static
    // type is not provably primitive is compiled to
    // `_jac.poly.call(obj, "method", ...args)`.
    call(o, m, ...args) {
      // 1. Genuine JS primitives get Python-semantic dispatch: a plain object
      //    is a Python dict, an Array is a list, a Set/Map/string/number/bytes
      //    maps to its Python counterpart. These are the types whose Python
      //    method semantics differ from (or have no) native JS equivalent.
      if (typeof o === "string") {
        if (_jac.poly._str[m]) return _jac.poly._str[m](o, ...args);
        if (_jac.str[m]) return _jac.str[m](o, ...args);
      } else if (typeof o === "number") {
        const ns = Number.isInteger(o) ? _jac.int : _jac.float;
        if (ns && ns[m]) return ns[m](o, ...args);
      } else if (Array.isArray(o)) {
        if (_jac.poly._list[m]) return _jac.poly._list[m](o, ...args);
      } else if (o instanceof Set) {
        if (_jac.poly._set[m]) return _jac.poly._set[m](o, ...args);
      } else if (o instanceof Map) {
        if (_jac.poly._map[m]) return _jac.poly._map[m](o, ...args);
      } else if (o instanceof Uint8Array) {
        if (_jac.bytes[m]) return _jac.bytes[m](o, ...args);
      } else if (
        o !== null && o !== undefined && typeof o === "object"
        && (Object.getPrototypeOf(o) === Object.prototype
            || Object.getPrototypeOf(o) === null)
      ) {
        // A plain object is a Python dict literal.
        if (_jac.poly._dict[m]) return _jac.poly._dict[m](o, ...args);
      }
      // 2. Otherwise forward to a same-named method the receiver carries
      //    itself: a user-defined class instance, a host/framework object
      //    (Headers, URLSearchParams, ...), a callable with attached methods,
      //    or a Set/Map/Array method not mirrored above. This matches the
      //    direct `obj.method(...)` that a typed call would have emitted.
      if (o !== null && o !== undefined && typeof o[m] === "function") {
        return o[m](...args);
      }
      // 3. Nothing applies: raise the Python-style error instead of leaking a
      //    method name or silently returning a wrong value.
      throw new _jac.exc.AttributeError(
        "'" + _jac.poly._typename(o) + "' object has no attribute '" + m + "'");
    },
    // Backward-compatible named shims. The compiler now emits `call`, but a
    // previously-built bundle may still reference a named helper; these route
    // it through the same dispatcher (rest params preserve the arg count).
    lower(s, ...r) { return _jac.poly.call(s, "lower", ...r); },
    upper(s, ...r) { return _jac.poly.call(s, "upper", ...r); },
    strip(s, ...r) { return _jac.poly.call(s, "strip", ...r); },
    lstrip(s, ...r) { return _jac.poly.call(s, "lstrip", ...r); },
    rstrip(s, ...r) { return _jac.poly.call(s, "rstrip", ...r); },
    append(a, ...r) { return _jac.poly.call(a, "append", ...r); },
    extend(a, ...r) { return _jac.poly.call(a, "extend", ...r); },
    pop(a, ...r) { return _jac.poly.call(a, "pop", ...r); },
    get(o, ...r) { return _jac.poly.call(o, "get", ...r); },
    contains(c, x) {
      // Python-equivalent membership for a value of unknown static type.
      // string -> substring; array (list/tuple/range) -> element;
      // Set/Map -> key/element; plain object (dict) -> own key.
      if (typeof c === "string") return c.includes(x);
      if (Array.isArray(c)) return c.includes(x);
      if (c instanceof Set || c instanceof Map) return c.has(x);
      if (c !== null && typeof c === "object") {
        return Object.prototype.hasOwnProperty.call(c, x);
      }
      return false;
    },
    getitem(c, i) {
      // Subscript read for a value of unknown static type. This is a faithful
      // superset of a raw `c[i]`: it only adds Python's negative indexing for
      // sequences (string/array) and otherwise preserves JS semantics exactly
      // (mapping/object access, out-of-range -> `undefined`, no new throws).
      if (typeof i === "number" && i < 0
          && (typeof c === "string" || Array.isArray(c))) {
        return c[c.length + i];
      }
      if (c instanceof Map) return c.get(i);
      return c[i];
    },
    setitem(c, i, v) {
      // Subscript assignment for a value of unknown/list static type. A
      // negative index writes the array element counted from the end (a raw
      // JS `c[-1] = v` would instead create a bogus "-1" property); every
      // other case behaves like a plain `c[i] = v`.
      if (typeof i === "number" && i < 0 && Array.isArray(c)) {
        c[c.length + i] = v;
        return v;
      }
      if (c instanceof Map) { c.set(i, v); return v; }
      c[i] = v;
      return v;
    },
    and_(a, bf) {
      // Python `a and b`: returns `a` when it is falsy, else evaluates and
      // returns `b`. `bf` is a thunk so the right operand keeps short-circuit
      // (lazy) evaluation, and `a` is evaluated exactly once by the caller.
      return _jac.builtin.bool(a) ? bf() : a;
    },
    or_(a, bf) {
      // Python `a or b`: returns `a` when it is truthy, else `b`.
      return _jac.builtin.bool(a) ? a : bf();
    }
  },

  list: {
    remove(arr, val) {
      const i = arr.indexOf(val);
      if (i === -1) throw new _jac.exc.ValueError("list.remove(x): x not in list");
      arr.splice(i, 1);
    },
    index(arr, val, start, end) {
      const s = start || 0;
      const e = end === undefined ? arr.length : end;
      for (let i = s; i < e; i++) {
        if (arr[i] === val) return i;
      }
      throw new _jac.exc.ValueError(val + " is not in list");
    },
    count(arr, val) {
      let c = 0;
      for (const item of arr) { if (item === val) c++; }
      return c;
    },
    sort(arr, key) {
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
    },
    repeat(arr, n) {
      const result = [];
      for (let i = 0; i < n; i++) result.push(...arr);
      return result;
    },
    eq(a, b) { return JSON.stringify(a) === JSON.stringify(b); },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.list.lt(b, a); },
    le(a, b) { return _jac.list.eq(a, b) || _jac.list.lt(a, b); },
    ge(a, b) { return _jac.list.eq(a, b) || _jac.list.gt(a, b); },
    imul(arr, n) {
      const orig = [...arr];
      arr.length = 0;
      for (let i = 0; i < n; i++) arr.push(...orig);
      return arr;
    }
  },

  dict: {
    pop(d, key, default_) {
      if (key in d) { const v = d[key]; delete d[key]; return v; }
      if (default_ !== undefined) return default_;
      throw new _jac.exc.KeyError("KeyError: " + key);
    },
    popitem(d) {
      const keys = Object.keys(d);
      if (keys.length === 0) throw new _jac.exc.KeyError("dictionary is empty");
      const key = keys[keys.length - 1];
      const val = d[key];
      delete d[key];
      return [key, val];
    },
    setdefault(d, key, default_) {
      if (!(key in d)) d[key] = default_ === undefined ? null : default_;
      return d[key];
    },
    clear(d) {
      for (const key of Object.keys(d)) delete d[key];
    },
    fromkeys(keys, value) {
      const d = {};
      const v = value === undefined ? null : value;
      for (const k of keys) d[k] = v;
      return d;
    },
    eq(a, b) {
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return false;
      for (const k of ka) {
        if (!(k in b)) return false;
        const va = a[k], vb = b[k];
        if (va !== vb) {
          if (typeof va === "object" && typeof vb === "object" && va !== null && vb !== null) {
            if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
          } else return false;
        }
      }
      return true;
    }
  },

  set: {
    remove(s, val) {
      if (!s.has(val)) throw new _jac.exc.KeyError("KeyError: " + val);
      s.delete(val);
    },
    pop(s) {
      if (s.size === 0) throw new _jac.exc.KeyError("pop from an empty set");
      const val = s.values().next().value;
      s.delete(val);
      return val;
    },
    update(s, other) { for (const v of other) s.add(v); },
    intersection_update(s, other) {
      const otherSet = other instanceof Set ? other : new Set(other);
      for (const v of s) { if (!otherSet.has(v)) s.delete(v); }
    },
    difference_update(s, other) {
      for (const v of other) s.delete(v);
    },
    symmetric_difference_update(s, other) {
      for (const v of other) { if (s.has(v)) s.delete(v); else s.add(v); }
    },
    union(a, b) {
      const r = new Set(a);
      for (const v of b) r.add(v);
      return r;
    },
    intersection(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (bs.has(v)) r.add(v); }
      return r;
    },
    difference(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (!bs.has(v)) r.add(v); }
      return r;
    },
    symmetricDifference(a, b) {
      const r = new Set(a);
      for (const v of b) { if (r.has(v)) r.delete(v); else r.add(v); }
      return r;
    },
    isSubsetOf(a, b) {
      if (a.size > b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    isSupersetOf(a, b) { return _jac.set.isSubsetOf(b, a); },
    isDisjointFrom(a, b) {
      for (const v of a) { if (b.has(v)) return false; }
      return true;
    },
    is_proper_subset(a, b) {
      if (a.size >= b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    is_proper_superset(a, b) { return _jac.set.is_proper_subset(b, a); },
    eq(a, b) {
      if (a.size !== b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    }
  },

  bytes: {
    hex(b) {
      return Array.from(b).map(x => x.toString(16).padStart(2, "0")).join("");
    },
    fromhex(s) {
      const arr = [];
      const clean = s.replace(/\s/g, "");
      for (let i = 0; i < clean.length; i += 2) arr.push(parseInt(clean.substr(i, 2), 16));
      return new Uint8Array(arr);
    },
    _indexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return s;
      for (let i = s; i <= e - sl; i++) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    _lastIndexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return e;
      for (let i = e - sl; i >= s; i--) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    count(b, sub) {
      let c = 0, p = 0;
      while (p <= b.length - sub.length) {
        const idx = _jac.bytes._indexOf(b, sub, p);
        if (idx === -1) break;
        c++; p = idx + sub.length;
      }
      return c;
    },
    find(b, sub, start, end) { return _jac.bytes._indexOf(b, sub, start, end); },
    rfind(b, sub, start, end) { return _jac.bytes._lastIndexOf(b, sub, start, end); },
    index(b, sub, start, end) {
      const idx = _jac.bytes._indexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(b, sub, start, end) {
      const idx = _jac.bytes._lastIndexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(b, prefix, start, end) {
      const s = start || 0;
      if (prefix.length > b.length - s) return false;
      for (let i = 0; i < prefix.length; i++) { if (b[s + i] !== prefix[i]) return false; }
      return true;
    },
    endswith(b, suffix, start, end) {
      const e = end === undefined ? b.length : end;
      if (suffix.length > e) return false;
      const off = e - suffix.length;
      for (let i = 0; i < suffix.length; i++) { if (b[off + i] !== suffix[i]) return false; }
      return true;
    },
    replace(b, old_, new_, count) {
      const r = [];
      let pos = 0, n = 0;
      const max = count === undefined ? Infinity : count;
      while (pos <= b.length) {
        if (n < max) {
          const idx = _jac.bytes._indexOf(b, old_, pos);
          if (idx !== -1) {
            for (let i = pos; i < idx; i++) r.push(b[i]);
            for (let i = 0; i < new_.length; i++) r.push(new_[i]);
            pos = idx + old_.length; n++;
            continue;
          }
        }
        for (let i = pos; i < b.length; i++) r.push(b[i]);
        break;
      }
      return new Uint8Array(r);
    },
    _isWS(c) { return c === 32 || c === 9 || c === 10 || c === 13 || c === 11 || c === 12; },
    strip(b, chars) {
      let s = 0, e = b.length;
      if (chars) { const cs = new Set(chars); while (s < e && cs.has(b[s])) s++; while (e > s && cs.has(b[e-1])) e--; }
      else { while (s < e && _jac.bytes._isWS(b[s])) s++; while (e > s && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(s, e);
    },
    lstrip(b, chars) {
      let s = 0;
      if (chars) { const cs = new Set(chars); while (s < b.length && cs.has(b[s])) s++; }
      else { while (s < b.length && _jac.bytes._isWS(b[s])) s++; }
      return b.slice(s);
    },
    rstrip(b, chars) {
      let e = b.length;
      if (chars) { const cs = new Set(chars); while (e > 0 && cs.has(b[e-1])) e--; }
      else { while (e > 0 && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(0, e);
    },
    removeprefix(b, prefix) {
      if (_jac.bytes.startswith(b, prefix)) return b.slice(prefix.length);
      return b.slice();
    },
    removesuffix(b, suffix) {
      if (suffix.length > 0 && _jac.bytes.endswith(b, suffix)) return b.slice(0, b.length - suffix.length);
      return b.slice();
    },
    split(b, sep, maxsplit) {
      const parts = [];
      let pos = 0, n = 0;
      const max = maxsplit === undefined ? Infinity : maxsplit;
      while (pos <= b.length) {
        if (n >= max) { parts.push(b.slice(pos)); return parts; }
        const idx = _jac.bytes._indexOf(b, sep, pos);
        if (idx === -1) { parts.push(b.slice(pos)); return parts; }
        parts.push(b.slice(pos, idx));
        pos = idx + sep.length; n++;
      }
      if (pos <= b.length) parts.push(b.slice(pos));
      return parts;
    },
    rsplit(b, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.bytes.split(b, sep);
      const parts = [];
      let end = b.length, n = 0;
      while (end >= 0 && n < maxsplit) {
        const idx = _jac.bytes._lastIndexOf(b, sep, 0, end);
        if (idx === -1) break;
        parts.unshift(b.slice(idx + sep.length, end));
        end = idx; n++;
      }
      parts.unshift(b.slice(0, end));
      return parts;
    },
    splitlines(b, keepends) {
      const result = [];
      let start = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 10 || b[i] === 13) {
          let eol = i + 1;
          if (b[i] === 13 && i + 1 < b.length && b[i + 1] === 10) eol++;
          result.push(keepends ? b.slice(start, eol) : b.slice(start, i));
          start = eol;
          if (eol > i + 1) i++;
        }
      }
      if (start < b.length) result.push(b.slice(start));
      return result;
    },
    join(sep, parts) {
      if (parts.length === 0) return new Uint8Array(0);
      const r = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) for (const x of sep) r.push(x);
        for (const x of parts[i]) r.push(x);
      }
      return new Uint8Array(r);
    },
    partition(b, sep) {
      const idx = _jac.bytes._indexOf(b, sep);
      if (idx === -1) return [b.slice(), new Uint8Array(0), new Uint8Array(0)];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    rpartition(b, sep) {
      const idx = _jac.bytes._lastIndexOf(b, sep);
      if (idx === -1) return [new Uint8Array(0), new Uint8Array(0), b.slice()];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    _isUp(c) { return c >= 65 && c <= 90; },
    _isLo(c) { return c >= 97 && c <= 122; },
    _isAl(c) { return (c >= 65 && c <= 90) || (c >= 97 && c <= 122); },
    _isDig(c) { return c >= 48 && c <= 57; },
    capitalize(b) {
      if (b.length === 0) return new Uint8Array(0);
      const r = new Uint8Array(b.length);
      r[0] = _jac.bytes._isLo(b[0]) ? b[0] - 32 : b[0];
      for (let i = 1; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    lower(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    upper(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i];
      return r;
    },
    title(b) {
      const r = new Uint8Array(b.length);
      let prev = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isAl(b[i])) {
          r[i] = prev ? (_jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i]) : (_jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i]);
          prev = true;
        } else { r[i] = b[i]; prev = false; }
      }
      return r;
    },
    swapcase(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) r[i] = b[i] + 32;
        else if (_jac.bytes._isLo(b[i])) r[i] = b[i] - 32;
        else r[i] = b[i];
      }
      return r;
    },
    center(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const total = width - b.length;
      const left = Math.floor(total / 2);
      const r = new Uint8Array(width);
      r.fill(f, 0, left); r.set(b, left); r.fill(f, left + b.length);
      return r;
    },
    ljust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      r.set(b); r.fill(f, b.length);
      return r;
    },
    rjust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      const off = width - b.length;
      r.fill(f, 0, off); r.set(b, off);
      return r;
    },
    zfill(b, width) {
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      let off = 0;
      if (b.length > 0 && (b[0] === 43 || b[0] === 45)) { r[0] = b[0]; off = 1; }
      const pad = width - b.length;
      r.fill(48, off, off + pad);
      for (let i = off; i < b.length; i++) r[pad + i] = b[i];
      return r;
    },
    expandtabs(b, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      const r = [];
      let col = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 9) { const sp = ts - (col % ts); for (let j = 0; j < sp; j++) r.push(32); col += sp; }
        else if (b[i] === 10 || b[i] === 13) { r.push(b[i]); col = 0; }
        else { r.push(b[i]); col++; }
      }
      return new Uint8Array(r);
    },
    translate(b, table, del_) {
      const r = [];
      const ds = del_ ? new Set(del_) : null;
      for (let i = 0; i < b.length; i++) {
        if (ds && ds.has(b[i])) continue;
        r.push(table && table[b[i]] !== undefined ? table[b[i]] : b[i]);
      }
      return new Uint8Array(r);
    },
    maketrans(from_, to_) {
      const t = new Uint8Array(256);
      for (let i = 0; i < 256; i++) t[i] = i;
      for (let i = 0; i < from_.length; i++) t[from_[i]] = to_[i];
      return t;
    },
    isalnum(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i]) && !_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    isalpha(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i])) return false;
      return true;
    },
    isascii(b) {
      for (let i = 0; i < b.length; i++) if (b[i] > 127) return false;
      return true;
    },
    isdigit(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    islower(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) return false;
        if (_jac.bytes._isLo(b[i])) has = true;
      }
      return has;
    },
    isupper(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isLo(b[i])) return false;
        if (_jac.bytes._isUp(b[i])) has = true;
      }
      return has;
    },
    isspace(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isWS(b[i])) return false;
      return true;
    },
    istitle(b) {
      let prev = false, has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) { if (prev) return false; prev = true; has = true; }
        else if (_jac.bytes._isLo(b[i])) { if (!prev) return false; prev = true; has = true; }
        else prev = false;
      }
      return has;
    },
    add(a, b) {
      const r = new Uint8Array(a.length + b.length);
      r.set(a); r.set(b, a.length);
      return r;
    },
    mul(b, n) {
      const r = new Uint8Array(b.length * n);
      for (let i = 0; i < n; i++) r.set(b, i * b.length);
      return r;
    },
    mod(b, args) {
      const s = new TextDecoder().decode(b);
      return new TextEncoder().encode(_jac.str.mod(s, args));
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.bytes.lt(b, a); },
    le(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.lt(a, b); },
    ge(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.gt(a, b); },
    contains(b, sub) { return _jac.bytes._indexOf(b, sub) !== -1; }
  },

  tuple: {
    count(arr, val) { return _jac.list.count(arr, val); },
    index(arr, val, start, end) { return _jac.list.index(arr, val, start, end); },
    repeat(arr, n) { return _jac.list.repeat(arr, n); },
    eq(a, b) { return _jac.list.eq(a, b); },
    lt(a, b) { return _jac.list.lt(a, b); },
    gt(a, b) { return _jac.list.gt(a, b); },
    le(a, b) { return _jac.list.le(a, b); },
    ge(a, b) { return _jac.list.ge(a, b); }
  },

  range: {
    count(r, val) {
      if (typeof r[Symbol.iterator] === "function") {
        let c = 0;
        for (const v of r) { if (v === val) c++; }
        return c;
      }
      return 0;
    },
    index(r, val) {
      let i = 0;
      for (const v of r) { if (v === val) return i; i++; }
      throw new _jac.exc.ValueError(val + " is not in range");
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
      return true;
    },
    contains(r, val) {
      for (const v of r) { if (v === val) return true; }
      return false;
    }
  },

  builtin: {
    round(n, ndigits) {
      if (ndigits === undefined) return Math.round(n);
      const f = Math.pow(10, ndigits);
      return Math.round(n * f) / f;
    },
    sum(iterable, start) {
      let total = start || 0;
      for (const v of iterable) total += v;
      return total;
    },
    sorted(iterable, key, reverse) {
      const arr = [...iterable];
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
      if (reverse) arr.reverse();
      return arr;
    },
    enumerate(iterable, start) {
      const s = start || 0;
      return [...iterable].map((v, i) => [s + i, v]);
    },
    zip(...iterables) {
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(arrays.map(a => a[i]));
      }
      return result;
    },
    map(fn, ...iterables) {
      if (iterables.length === 1) return [...iterables[0]].map(fn);
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(fn(...arrays.map(a => a[i])));
      }
      return result;
    },
    filter(fn, iterable) {
      return [...iterable].filter(fn || (x => x));
    },
    // Python subclass relation over builtin-type *tags* -- the string names
    // (`"str"`, `"int"`, ...) that `exit_builtin_type` lowers builtin types to.
    // `object` is the universal base and `bool` is a subclass of `int`; `tuple`
    // and `frozenset` share JS representations with `list`/`set`, so fold those
    // aliases to their runtime tag first. One source of truth so isinstance and
    // issubclass can never disagree.
    _tagSubclass(subTag, superTag) {
      const fold = (t) => t === "tuple" ? "list" : t === "frozenset" ? "set" : t;
      const sub = fold(subTag), sup = fold(superTag);
      if (sup === "object") return true;
      if (sub === sup) return true;
      if (sup === "int" && sub === "bool") return true;
      return false;
    },
    isinstance(o, classinfo) {
      // isinstance(x, (A, B, ...)) -- the tuple of types lowers to a JS array;
      // match if `o` is an instance of any member.
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.isinstance(o, c)) return true;
        }
        return false;
      }
      // Builtin types lower to a string tag (their Python type name): compare
      // the value's runtime type against it via the shared subclass relation.
      if (typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(_jac.poly._typename(o), classinfo);
      }
      // User archetype / JS constructor.
      if (typeof classinfo === "function") return o instanceof classinfo;
      return false;
    },
    issubclass(cls, classinfo) {
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.issubclass(cls, c)) return true;
        }
        return false;
      }
      // When either side is a builtin-type tag, compare by the shared subclass
      // relation (covers `object`, `bool` ⊂ `int`, and identity).
      if (typeof cls === "string" || typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(cls, classinfo);
      }
      if (cls === classinfo) return true;
      let proto = cls;
      while (proto) {
        if (proto === classinfo) return true;
        proto = Object.getPrototypeOf(proto);
      }
      return false;
    },
    id(o) {
      if (o === null || o === undefined) return 0;
      if (typeof o === "number") return o;
      return typeof o === "string" ? _jac.builtin._strHash(o) : 0;
    },
    _strHash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      }
      return h;
    },
    jid(obj) {
      if (obj === null || obj === undefined) {
        throw new _jac.exc.TypeError(
          "jid() called on " + String(obj)
          + " -- the server may have returned an error instead of a node."
          + " Check that your endpoint returned successfully before using jid()."
        );
      }
      if (obj._jac_id === undefined) {
        throw new _jac.exc.TypeError(
          "jid() expected a node or edge with graph identity, but received "
          + (typeof obj === "object" ? JSON.stringify(Object.keys(obj)) : typeof obj)
          + ". Only node and edge archetypes have a jid."
        );
      }
      return obj._jac_id;
    },
    // unsafe_html(x) returns a sentinel object that the JSX flattener
    // recognises and renders as raw HTML via dangerouslySetInnerHTML.
    // Named "unsafe" so the security implication is visible at the use site.
    unsafe_html(html) {
      return { __jacUnsafeHtml: String(html == null ? "" : html) };
    },
    hash(obj) { return _jac.builtin.id(obj); },
    repr(obj) { return JSON.stringify(obj); },
    pow(base, exp, mod) {
      let result = Math.pow(base, exp);
      if (mod !== undefined) result = ((result % mod) + mod) % mod;
      return result;
    },
    next(iter, default_) {
      const result = iter.next();
      if (result.done) {
        if (default_ !== undefined) return default_;
        throw new _jac.exc.StopIteration("StopIteration");
      }
      return result.value;
    },
    vars(o) {
      if (o === undefined) return {};
      const result = {};
      for (const [k, v] of Object.entries(o)) {
        if (typeof v !== "function") result[k] = v;
      }
      return result;
    },
    dir(o) {
      if (o === undefined) return [];
      return Object.getOwnPropertyNames(o).sort();
    },
    open() { throw new _jac.exc.OSError("open() is not supported in client-side JavaScript"); },
    format(value, format_spec) {
      if (!format_spec) return String(value);
      return String(value);
    },
    ascii(obj) {
      if (typeof obj === "string") {
        let r = "'";
        for (const c of obj) {
          const code = c.charCodeAt(0);
          if (c === "\\") r += "\\\\";
          else if (c === "'") r += "\\'";
          else if (code >= 0x20 && code <= 0x7e) r += c;
          else if (code < 0x100) r += "\\x" + code.toString(16).padStart(2, "0");
          else r += "\\u" + code.toString(16).padStart(4, "0");
        }
        return r + "'";
      }
      return String(obj);
    },
    complex(re, im) { return {re: re || 0, im: im || 0}; },
    bool(x) {
      if (x === null || x === undefined || x === false || x === 0 || x === "" || Number.isNaN(x)) return false;
      if (Array.isArray(x)) return x.length > 0;
      if (x instanceof Set || x instanceof Map) return x.size > 0;
      if (typeof x === "object") return Object.keys(x).length > 0;
      return true;
    },
    range(...args) {
      let start, stop, step;
      if (args.length === 1) { start = 0; stop = args[0]; step = 1; }
      else if (args.length === 2) { start = args[0]; stop = args[1]; step = 1; }
      else { start = args[0]; stop = args[1]; step = args[2]; }
      if (step === 0) throw new _jac.exc.ValueError("range() arg 3 must not be zero");
      const result = [];
      if (step > 0) { for (let i = start; i < stop; i += step) result.push(i); }
      else { for (let i = start; i > stop; i += step) result.push(i); }
      return result;
    },
    slice(...args) {
      if (args.length === 1) return { start: null, stop: args[0], step: null };
      return { start: args[0] !== undefined ? args[0] : null, stop: args[1] !== undefined ? args[1] : null, step: args[2] !== undefined ? args[2] : null };
    },
    format(value, format_spec) {
      if (!format_spec || format_spec === "") return String(value);
      const m = format_spec.match(/^([<>^]?)(\d+)?(?:\.(\d+))?([dfseboxXn%]?)$/);
      if (!m) return String(value);
      const [, align, widthStr, precStr, ftype] = m;
      let s;
      const prec = precStr !== undefined ? parseInt(precStr) : undefined;
      if (ftype === "f" || ftype === "") {
        s = prec !== undefined ? Number(value).toFixed(prec) : String(value);
      } else if (ftype === "d") {
        s = String(Math.floor(Number(value)));
      } else if (ftype === "b") {
        s = Number(value).toString(2);
      } else if (ftype === "o") {
        s = Number(value).toString(8);
      } else if (ftype === "x") {
        s = Number(value).toString(16);
      } else if (ftype === "X") {
        s = Number(value).toString(16).toUpperCase();
      } else if (ftype === "e") {
        s = prec !== undefined ? Number(value).toExponential(prec) : Number(value).toExponential();
      } else if (ftype === "%") {
        s = (prec !== undefined ? (Number(value) * 100).toFixed(prec) : (Number(value) * 100).toFixed(6)) + "%";
      } else if (ftype === "s" || ftype === "n") {
        s = String(value);
      } else {
        s = String(value);
      }
      if (widthStr) {
        const width = parseInt(widthStr);
        if (s.length < width) {
          const pad = " ".repeat(width - s.length);
          if (align === "<") s = s + pad;
          else if (align === "^") {
            const left = Math.floor((width - s.length) / 2);
            s = " ".repeat(left) + s + " ".repeat(width - s.length - left);
          }
          else s = pad + s;
        }
      }
      return s;
    }
  },

  exc: (() => {
    class BaseException extends Error { constructor(m) { super(m); this.name = 'BaseException'; } }
    class Exception extends BaseException { constructor(m) { super(m); this.name = 'Exception'; } }
    class ArithmeticError extends Exception { constructor(m) { super(m); this.name = 'ArithmeticError'; } }
    class ZeroDivisionError extends ArithmeticError { constructor(m) { super(m); this.name = 'ZeroDivisionError'; } }
    class OverflowError extends ArithmeticError { constructor(m) { super(m); this.name = 'OverflowError'; } }
    class FloatingPointError extends ArithmeticError { constructor(m) { super(m); this.name = 'FloatingPointError'; } }
    class LookupError extends Exception { constructor(m) { super(m); this.name = 'LookupError'; } }
    class IndexError extends LookupError { constructor(m) { super(m); this.name = 'IndexError'; } }
    class KeyError extends LookupError { constructor(m) { super(m); this.name = 'KeyError'; } }
    class ValueError extends Exception { constructor(m) { super(m); this.name = 'ValueError'; } }
    class TypeError_ extends Exception { constructor(m) { super(m); this.name = 'TypeError'; } }
    class AttributeError extends Exception { constructor(m) { super(m); this.name = 'AttributeError'; } }
    class RuntimeError extends Exception { constructor(m) { super(m); this.name = 'RuntimeError'; } }
    class NotImplementedError extends RuntimeError { constructor(m) { super(m); this.name = 'NotImplementedError'; } }
    class RecursionError extends RuntimeError { constructor(m) { super(m); this.name = 'RecursionError'; } }
    class OSError extends Exception { constructor(m) { super(m); this.name = 'OSError'; } }
    class FileNotFoundError extends OSError { constructor(m) { super(m); this.name = 'FileNotFoundError'; } }
    class FileExistsError extends OSError { constructor(m) { super(m); this.name = 'FileExistsError'; } }
    class PermissionError extends OSError { constructor(m) { super(m); this.name = 'PermissionError'; } }
    class TimeoutError extends OSError { constructor(m) { super(m); this.name = 'TimeoutError'; } }
    class IsADirectoryError extends OSError { constructor(m) { super(m); this.name = 'IsADirectoryError'; } }
    class NotADirectoryError extends OSError { constructor(m) { super(m); this.name = 'NotADirectoryError'; } }
    class AssertionError extends Exception { constructor(m) { super(m); this.name = 'AssertionError'; } }
    class ImportError extends Exception { constructor(m) { super(m); this.name = 'ImportError'; } }
    class ModuleNotFoundError extends ImportError { constructor(m) { super(m); this.name = 'ModuleNotFoundError'; } }
    class NameError extends Exception { constructor(m) { super(m); this.name = 'NameError'; } }
    class UnboundLocalError extends NameError { constructor(m) { super(m); this.name = 'UnboundLocalError'; } }
    class StopIteration extends Exception { constructor(m) { super(m); this.name = 'StopIteration'; } }
    class StopAsyncIteration extends Exception { constructor(m) { super(m); this.name = 'StopAsyncIteration'; } }
    class EOFError extends Exception { constructor(m) { super(m); this.name = 'EOFError'; } }
    class MemoryError extends Exception { constructor(m) { super(m); this.name = 'MemoryError'; } }
    class ReferenceError_ extends Exception { constructor(m) { super(m); this.name = 'ReferenceError'; } }
    class KeyboardInterrupt extends BaseException { constructor(m) { super(m); this.name = 'KeyboardInterrupt'; } }
    class SystemExit extends BaseException { constructor(m) { super(m); this.name = 'SystemExit'; } }
    class GeneratorExit extends BaseException { constructor(m) { super(m); this.name = 'GeneratorExit'; } }
    return { BaseException, Exception, ArithmeticError, ZeroDivisionError, OverflowError, FloatingPointError, LookupError, IndexError, KeyError, ValueError, TypeError: TypeError_, AttributeError, RuntimeError, NotImplementedError, RecursionError, OSError, FileNotFoundError, FileExistsError, PermissionError, TimeoutError, IsADirectoryError, NotADirectoryError, AssertionError, ImportError, ModuleNotFoundError, NameError, UnboundLocalError, StopIteration, StopAsyncIteration, EOFError, MemoryError, ReferenceError: ReferenceError_, KeyboardInterrupt, SystemExit, GeneratorExit };
  })()
};
function useStoreSlice(getFn, subscribeFn) {
  let _pair = useState(getFn());
  let _setVal = _pair[1];
  useEffect(() => {
    return subscribeFn(_nv => {
      _setVal(_nv);
    });
  }, []);
  return _pair[0];
}
function getStoreTasks() {
  try {
    if (window.__skoochStoreTasks) {
      return window.__skoochStoreTasks;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreTasks(t) {
  let _val = ((!_jac.list.eq(t, null) && !_jac.list.eq(t, undefined)) ? t : []);
  try {
    window.__skoochStoreTasks = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochTasks = _val;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreTasksSubs) {
      _subs = window.__skoochStoreTasksSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreTasks(cb) {
  try {
    if (!window.__skoochStoreTasksSubs) {
      window.__skoochStoreTasksSubs = [];
    }
    window.__skoochStoreTasksSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreTasksSubs ? window.__skoochStoreTasksSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchedule() {
  try {
    if (window.__skoochStoreSchedule) {
      return window.__skoochStoreSchedule;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreSchedule(s) {
  let _val = ((!_jac.list.eq(s, null) && !_jac.list.eq(s, undefined)) ? s : []);
  try {
    window.__skoochStoreSchedule = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchedule = _val;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreScheduleSubs) {
      _subs = window.__skoochStoreScheduleSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchedule(cb) {
  try {
    if (!window.__skoochStoreScheduleSubs) {
      window.__skoochStoreScheduleSubs = [];
    }
    window.__skoochStoreScheduleSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreScheduleSubs ? window.__skoochStoreScheduleSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreFixedBlocks() {
  try {
    if (window.__skoochStoreFixedBlocks) {
      return window.__skoochStoreFixedBlocks;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreFixedBlocks(f) {
  let _val = ((!_jac.list.eq(f, null) && !_jac.list.eq(f, undefined)) ? f : []);
  try {
    window.__skoochStoreFixedBlocks = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochFixedBlocks = _val;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchedLoadedRange = undefined;
  } catch (__jac_e) {
    if (true) {
      let _slrErr = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreFixedBlocksSubs) {
      _subs = window.__skoochStoreFixedBlocksSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreFixedBlocks(cb) {
  try {
    if (!window.__skoochStoreFixedBlocksSubs) {
      window.__skoochStoreFixedBlocksSubs = [];
    }
    window.__skoochStoreFixedBlocksSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreFixedBlocksSubs ? window.__skoochStoreFixedBlocksSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchedulerTasks() {
  try {
    if (window.__skoochStoreSchedulerTasks) {
      return window.__skoochStoreSchedulerTasks;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreSchedulerTasks(s) {
  let _val = ((!_jac.list.eq(s, null) && !_jac.list.eq(s, undefined)) ? s : []);
  try {
    window.__skoochStoreSchedulerTasks = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchedulerTasks = _val;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchedulerTasksSubs) {
      _subs = window.__skoochStoreSchedulerTasksSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchedulerTasks(cb) {
  try {
    if (!window.__skoochStoreSchedulerTasksSubs) {
      window.__skoochStoreSchedulerTasksSubs = [];
    }
    window.__skoochStoreSchedulerTasksSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchedulerTasksSubs ? window.__skoochStoreSchedulerTasksSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreOverflow() {
  try {
    if (window.__skoochStoreOverflow) {
      return window.__skoochStoreOverflow;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreOverflow(o) {
  let _val = ((!_jac.list.eq(o, null) && !_jac.list.eq(o, undefined)) ? o : []);
  try {
    window.__skoochStoreOverflow = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochOverflow = _val;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreOverflowSubs) {
      _subs = window.__skoochStoreOverflowSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreOverflow(cb) {
  try {
    if (!window.__skoochStoreOverflowSubs) {
      window.__skoochStoreOverflowSubs = [];
    }
    window.__skoochStoreOverflowSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreOverflowSubs ? window.__skoochStoreOverflowSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSuggestions() {
  try {
    if (window.__skoochStoreSuggestions) {
      return window.__skoochStoreSuggestions;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreSuggestions(s) {
  let _val = ((!_jac.list.eq(s, null) && !_jac.list.eq(s, undefined)) ? s : []);
  try {
    window.__skoochStoreSuggestions = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSuggestions = _val;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSuggestionsSubs) {
      _subs = window.__skoochStoreSuggestionsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSuggestions(cb) {
  try {
    if (!window.__skoochStoreSuggestionsSubs) {
      window.__skoochStoreSuggestionsSubs = [];
    }
    window.__skoochStoreSuggestionsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSuggestionsSubs ? window.__skoochStoreSuggestionsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConstraints() {
  try {
    if (window.__skoochStoreSchConstraints) {
      return window.__skoochStoreSchConstraints;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreSchConstraints(c) {
  let _val = ((!_jac.list.eq(c, null) && !_jac.list.eq(c, undefined)) ? c : []);
  try {
    window.__skoochStoreSchConstraints = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchConstraints = _val;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConstraintsSubs) {
      _subs = window.__skoochStoreSchConstraintsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConstraints(cb) {
  try {
    if (!window.__skoochStoreSchConstraintsSubs) {
      window.__skoochStoreSchConstraintsSubs = [];
    }
    window.__skoochStoreSchConstraintsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConstraintsSubs ? window.__skoochStoreSchConstraintsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreGlobalRules() {
  try {
    if (window.__skoochStoreGlobalRules) {
      return window.__skoochStoreGlobalRules;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreGlobalRules(g) {
  let _val = ((!_jac.list.eq(g, null) && !_jac.list.eq(g, undefined)) ? g : []);
  try {
    window.__skoochStoreGlobalRules = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochGlobalRules = _val;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreGlobalRulesSubs) {
      _subs = window.__skoochStoreGlobalRulesSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreGlobalRules(cb) {
  try {
    if (!window.__skoochStoreGlobalRulesSubs) {
      window.__skoochStoreGlobalRulesSubs = [];
    }
    window.__skoochStoreGlobalRulesSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreGlobalRulesSubs ? window.__skoochStoreGlobalRulesSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreCategoryTiers() {
  try {
    if (window.__skoochStoreCategoryTiers) {
      return window.__skoochStoreCategoryTiers;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreCategoryTiers(c) {
  let _val = ((!_jac.list.eq(c, null) && !_jac.list.eq(c, undefined)) ? c : []);
  try {
    window.__skoochStoreCategoryTiers = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochCategoryTiers = _val;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreCategoryTiersSubs) {
      _subs = window.__skoochStoreCategoryTiersSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreCategoryTiers(cb) {
  try {
    if (!window.__skoochStoreCategoryTiersSubs) {
      window.__skoochStoreCategoryTiersSubs = [];
    }
    window.__skoochStoreCategoryTiersSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreCategoryTiersSubs ? window.__skoochStoreCategoryTiersSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchPrefPeak() {
  try {
    if (((window.__skoochStoreSchPrefPeak !== undefined) && (window.__skoochStoreSchPrefPeak !== null))) {
      return window.__skoochStoreSchPrefPeak;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "morning";
}
function setStoreSchPrefPeak(v) {
  try {
    window.__skoochStoreSchPrefPeak = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchPrefPeak = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchPrefPeakSubs) {
      _subs = window.__skoochStoreSchPrefPeakSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchPrefPeak(cb) {
  try {
    if (!window.__skoochStoreSchPrefPeakSubs) {
      window.__skoochStoreSchPrefPeakSubs = [];
    }
    window.__skoochStoreSchPrefPeakSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchPrefPeakSubs ? window.__skoochStoreSchPrefPeakSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchPrefBlock() {
  try {
    if (((window.__skoochStoreSchPrefBlock !== undefined) && (window.__skoochStoreSchPrefBlock !== null))) {
      return window.__skoochStoreSchPrefBlock;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 90;
}
function setStoreSchPrefBlock(v) {
  try {
    window.__skoochStoreSchPrefBlock = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchPrefBlock = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchPrefBlockSubs) {
      _subs = window.__skoochStoreSchPrefBlockSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchPrefBlock(cb) {
  try {
    if (!window.__skoochStoreSchPrefBlockSubs) {
      window.__skoochStoreSchPrefBlockSubs = [];
    }
    window.__skoochStoreSchPrefBlockSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchPrefBlockSubs ? window.__skoochStoreSchPrefBlockSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchPrefBuf() {
  try {
    if (((window.__skoochStoreSchPrefBuf !== undefined) && (window.__skoochStoreSchPrefBuf !== null))) {
      return window.__skoochStoreSchPrefBuf;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 15;
}
function setStoreSchPrefBuf(v) {
  try {
    window.__skoochStoreSchPrefBuf = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchPrefBuf = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchPrefBufSubs) {
      _subs = window.__skoochStoreSchPrefBufSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchPrefBuf(cb) {
  try {
    if (!window.__skoochStoreSchPrefBufSubs) {
      window.__skoochStoreSchPrefBufSubs = [];
    }
    window.__skoochStoreSchPrefBufSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchPrefBufSubs ? window.__skoochStoreSchPrefBufSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchPrefStrat() {
  try {
    if (((window.__skoochStoreSchPrefStrat !== undefined) && (window.__skoochStoreSchPrefStrat !== null))) {
      return window.__skoochStoreSchPrefStrat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "deadlines";
}
function setStoreSchPrefStrat(v) {
  try {
    window.__skoochStoreSchPrefStrat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchPrefStrat = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchPrefStratSubs) {
      _subs = window.__skoochStoreSchPrefStratSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchPrefStrat(cb) {
  try {
    if (!window.__skoochStoreSchPrefStratSubs) {
      window.__skoochStoreSchPrefStratSubs = [];
    }
    window.__skoochStoreSchPrefStratSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchPrefStratSubs ? window.__skoochStoreSchPrefStratSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchPrefAfter() {
  try {
    if (((window.__skoochStoreSchPrefAfter !== undefined) && (window.__skoochStoreSchPrefAfter !== null))) {
      return window.__skoochStoreSchPrefAfter;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "more";
}
function setStoreSchPrefAfter(v) {
  try {
    window.__skoochStoreSchPrefAfter = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchPrefAfter = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchPrefAfterSubs) {
      _subs = window.__skoochStoreSchPrefAfterSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchPrefAfter(cb) {
  try {
    if (!window.__skoochStoreSchPrefAfterSubs) {
      window.__skoochStoreSchPrefAfterSubs = [];
    }
    window.__skoochStoreSchPrefAfterSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchPrefAfterSubs ? window.__skoochStoreSchPrefAfterSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchDirty() {
  try {
    if (((window.__skoochStoreSchDirty !== undefined) && (window.__skoochStoreSchDirty !== null))) {
      return window.__skoochStoreSchDirty;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return true;
}
function setStoreSchDirty(v) {
  try {
    window.__skoochStoreSchDirty = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchDirty = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchDirtySubs) {
      _subs = window.__skoochStoreSchDirtySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchDirty(cb) {
  try {
    if (!window.__skoochStoreSchDirtySubs) {
      window.__skoochStoreSchDirtySubs = [];
    }
    window.__skoochStoreSchDirtySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchDirtySubs ? window.__skoochStoreSchDirtySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchOptimized() {
  try {
    if (((window.__skoochStoreSchOptimized !== undefined) && (window.__skoochStoreSchOptimized !== null))) {
      return window.__skoochStoreSchOptimized;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchOptimized(v) {
  try {
    window.__skoochStoreSchOptimized = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchOptimized = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchOptimizedSubs) {
      _subs = window.__skoochStoreSchOptimizedSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchOptimized(cb) {
  try {
    if (!window.__skoochStoreSchOptimizedSubs) {
      window.__skoochStoreSchOptimizedSubs = [];
    }
    window.__skoochStoreSchOptimizedSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchOptimizedSubs ? window.__skoochStoreSchOptimizedSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreUserId() {
  try {
    if (((window.__skoochStoreUserId !== undefined) && (window.__skoochStoreUserId !== null))) {
      return window.__skoochStoreUserId;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreUserId(v) {
  try {
    window.__skoochStoreUserId = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochUserId = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreUserIdSubs) {
      _subs = window.__skoochStoreUserIdSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreUserId(cb) {
  try {
    if (!window.__skoochStoreUserIdSubs) {
      window.__skoochStoreUserIdSubs = [];
    }
    window.__skoochStoreUserIdSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreUserIdSubs ? window.__skoochStoreUserIdSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIsAnonymous() {
  try {
    if (((window.__skoochStoreIsAnonymous !== undefined) && (window.__skoochStoreIsAnonymous !== null))) {
      return window.__skoochStoreIsAnonymous;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreIsAnonymous(v) {
  try {
    window.__skoochStoreIsAnonymous = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochIsAnonymous = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIsAnonymousSubs) {
      _subs = window.__skoochStoreIsAnonymousSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIsAnonymous(cb) {
  try {
    if (!window.__skoochStoreIsAnonymousSubs) {
      window.__skoochStoreIsAnonymousSubs = [];
    }
    window.__skoochStoreIsAnonymousSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIsAnonymousSubs ? window.__skoochStoreIsAnonymousSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAccountClaimed() {
  try {
    if (((window.__skoochStoreAccountClaimed !== undefined) && (window.__skoochStoreAccountClaimed !== null))) {
      return window.__skoochStoreAccountClaimed;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreAccountClaimed(v) {
  try {
    window.__skoochStoreAccountClaimed = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochAccountClaimed = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAccountClaimedSubs) {
      _subs = window.__skoochStoreAccountClaimedSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAccountClaimed(cb) {
  try {
    if (!window.__skoochStoreAccountClaimedSubs) {
      window.__skoochStoreAccountClaimedSubs = [];
    }
    window.__skoochStoreAccountClaimedSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAccountClaimedSubs ? window.__skoochStoreAccountClaimedSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchLog() {
  try {
    if (((window.__skoochStoreSchLog !== undefined) && (window.__skoochStoreSchLog !== null))) {
      return window.__skoochStoreSchLog;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchLog(v) {
  try {
    window.__skoochStoreSchLog = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchLog = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchLogSubs) {
      _subs = window.__skoochStoreSchLogSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchLog(cb) {
  try {
    if (!window.__skoochStoreSchLogSubs) {
      window.__skoochStoreSchLogSubs = [];
    }
    window.__skoochStoreSchLogSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchLogSubs ? window.__skoochStoreSchLogSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastVisible() {
  try {
    if (((window.__skoochStoreToastVisible !== undefined) && (window.__skoochStoreToastVisible !== null))) {
      return window.__skoochStoreToastVisible;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreToastVisible(v) {
  try {
    window.__skoochStoreToastVisible = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastVisibleSubs) {
      _subs = window.__skoochStoreToastVisibleSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastVisible(cb) {
  try {
    if (!window.__skoochStoreToastVisibleSubs) {
      window.__skoochStoreToastVisibleSubs = [];
    }
    window.__skoochStoreToastVisibleSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastVisibleSubs ? window.__skoochStoreToastVisibleSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastResponse() {
  try {
    if (((window.__skoochStoreToastResponse !== undefined) && (window.__skoochStoreToastResponse !== null))) {
      return window.__skoochStoreToastResponse;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreToastResponse(v) {
  try {
    window.__skoochStoreToastResponse = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastResponseSubs) {
      _subs = window.__skoochStoreToastResponseSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastResponse(cb) {
  try {
    if (!window.__skoochStoreToastResponseSubs) {
      window.__skoochStoreToastResponseSubs = [];
    }
    window.__skoochStoreToastResponseSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastResponseSubs ? window.__skoochStoreToastResponseSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastMCQOptions() {
  try {
    if (window.__skoochStoreToastMCQOptions) {
      return window.__skoochStoreToastMCQOptions;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreToastMCQOptions(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : []);
  try {
    window.__skoochStoreToastMCQOptions = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastMCQOptionsSubs) {
      _subs = window.__skoochStoreToastMCQOptionsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastMCQOptions(cb) {
  try {
    if (!window.__skoochStoreToastMCQOptionsSubs) {
      window.__skoochStoreToastMCQOptionsSubs = [];
    }
    window.__skoochStoreToastMCQOptionsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastMCQOptionsSubs ? window.__skoochStoreToastMCQOptionsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastDimension() {
  try {
    if (((window.__skoochStoreToastDimension !== undefined) && (window.__skoochStoreToastDimension !== null))) {
      return window.__skoochStoreToastDimension;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreToastDimension(v) {
  try {
    window.__skoochStoreToastDimension = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastDimensionSubs) {
      _subs = window.__skoochStoreToastDimensionSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastDimension(cb) {
  try {
    if (!window.__skoochStoreToastDimensionSubs) {
      window.__skoochStoreToastDimensionSubs = [];
    }
    window.__skoochStoreToastDimensionSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastDimensionSubs ? window.__skoochStoreToastDimensionSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastTargetEntityId() {
  try {
    if (((window.__skoochStoreToastTargetEntityId !== undefined) && (window.__skoochStoreToastTargetEntityId !== null))) {
      return window.__skoochStoreToastTargetEntityId;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreToastTargetEntityId(v) {
  try {
    window.__skoochStoreToastTargetEntityId = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastTargetEntityIdSubs) {
      _subs = window.__skoochStoreToastTargetEntityIdSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastTargetEntityId(cb) {
  try {
    if (!window.__skoochStoreToastTargetEntityIdSubs) {
      window.__skoochStoreToastTargetEntityIdSubs = [];
    }
    window.__skoochStoreToastTargetEntityIdSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastTargetEntityIdSubs ? window.__skoochStoreToastTargetEntityIdSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastTargetSlot() {
  try {
    if (((window.__skoochStoreToastTargetSlot !== undefined) && (window.__skoochStoreToastTargetSlot !== null))) {
      return window.__skoochStoreToastTargetSlot;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return (-1);
}
function setStoreToastTargetSlot(v) {
  try {
    window.__skoochStoreToastTargetSlot = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastTargetSlotSubs) {
      _subs = window.__skoochStoreToastTargetSlotSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastTargetSlot(cb) {
  try {
    if (!window.__skoochStoreToastTargetSlotSubs) {
      window.__skoochStoreToastTargetSlotSubs = [];
    }
    window.__skoochStoreToastTargetSlotSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastTargetSlotSubs ? window.__skoochStoreToastTargetSlotSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastFamiliarity() {
  try {
    if (((window.__skoochStoreToastFamiliarity !== undefined) && (window.__skoochStoreToastFamiliarity !== null))) {
      return window.__skoochStoreToastFamiliarity;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreToastFamiliarity(v) {
  try {
    window.__skoochStoreToastFamiliarity = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastFamiliaritySubs) {
      _subs = window.__skoochStoreToastFamiliaritySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastFamiliarity(cb) {
  try {
    if (!window.__skoochStoreToastFamiliaritySubs) {
      window.__skoochStoreToastFamiliaritySubs = [];
    }
    window.__skoochStoreToastFamiliaritySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastFamiliaritySubs ? window.__skoochStoreToastFamiliaritySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastQuestionId() {
  try {
    if (((window.__skoochStoreToastQuestionId !== undefined) && (window.__skoochStoreToastQuestionId !== null))) {
      return window.__skoochStoreToastQuestionId;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreToastQuestionId(v) {
  try {
    window.__skoochStoreToastQuestionId = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastQuestionIdSubs) {
      _subs = window.__skoochStoreToastQuestionIdSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastQuestionId(cb) {
  try {
    if (!window.__skoochStoreToastQuestionIdSubs) {
      window.__skoochStoreToastQuestionIdSubs = [];
    }
    window.__skoochStoreToastQuestionIdSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastQuestionIdSubs ? window.__skoochStoreToastQuestionIdSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastQuestionType() {
  try {
    if (((window.__skoochStoreToastQuestionType !== undefined) && (window.__skoochStoreToastQuestionType !== null))) {
      return window.__skoochStoreToastQuestionType;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreToastQuestionType(v) {
  try {
    window.__skoochStoreToastQuestionType = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastQuestionTypeSubs) {
      _subs = window.__skoochStoreToastQuestionTypeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastQuestionType(cb) {
  try {
    if (!window.__skoochStoreToastQuestionTypeSubs) {
      window.__skoochStoreToastQuestionTypeSubs = [];
    }
    window.__skoochStoreToastQuestionTypeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastQuestionTypeSubs ? window.__skoochStoreToastQuestionTypeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastWhyLine() {
  try {
    if (((window.__skoochStoreToastWhyLine !== undefined) && (window.__skoochStoreToastWhyLine !== null))) {
      return window.__skoochStoreToastWhyLine;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreToastWhyLine(v) {
  try {
    window.__skoochStoreToastWhyLine = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastWhyLineSubs) {
      _subs = window.__skoochStoreToastWhyLineSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastWhyLine(cb) {
  try {
    if (!window.__skoochStoreToastWhyLineSubs) {
      window.__skoochStoreToastWhyLineSubs = [];
    }
    window.__skoochStoreToastWhyLineSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastWhyLineSubs ? window.__skoochStoreToastWhyLineSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastCategory() {
  try {
    if (((window.__skoochStoreToastCategory !== undefined) && (window.__skoochStoreToastCategory !== null))) {
      return window.__skoochStoreToastCategory;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreToastCategory(v) {
  try {
    window.__skoochStoreToastCategory = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastCategorySubs) {
      _subs = window.__skoochStoreToastCategorySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastCategory(cb) {
  try {
    if (!window.__skoochStoreToastCategorySubs) {
      window.__skoochStoreToastCategorySubs = [];
    }
    window.__skoochStoreToastCategorySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastCategorySubs ? window.__skoochStoreToastCategorySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreToastPlannedMins() {
  try {
    if (((window.__skoochStoreToastPlannedMins !== undefined) && (window.__skoochStoreToastPlannedMins !== null))) {
      return window.__skoochStoreToastPlannedMins;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreToastPlannedMins(v) {
  try {
    window.__skoochStoreToastPlannedMins = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreToastPlannedMinsSubs) {
      _subs = window.__skoochStoreToastPlannedMinsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreToastPlannedMins(cb) {
  try {
    if (!window.__skoochStoreToastPlannedMinsSubs) {
      window.__skoochStoreToastPlannedMinsSubs = [];
    }
    window.__skoochStoreToastPlannedMinsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreToastPlannedMinsSubs ? window.__skoochStoreToastPlannedMinsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreLearningTick() {
  try {
    if (((window.__skoochStoreLearningTick !== undefined) && (window.__skoochStoreLearningTick !== null))) {
      return window.__skoochStoreLearningTick;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreLearningTick(v) {
  try {
    window.__skoochStoreLearningTick = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreLearningTickSubs) {
      _subs = window.__skoochStoreLearningTickSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreLearningTick(cb) {
  try {
    if (!window.__skoochStoreLearningTickSubs) {
      window.__skoochStoreLearningTickSubs = [];
    }
    window.__skoochStoreLearningTickSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreLearningTickSubs ? window.__skoochStoreLearningTickSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIcalModalOpen() {
  try {
    if (((window.__skoochStoreIcalModalOpen !== undefined) && (window.__skoochStoreIcalModalOpen !== null))) {
      return window.__skoochStoreIcalModalOpen;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreIcalModalOpen(v) {
  try {
    window.__skoochStoreIcalModalOpen = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIcalModalOpenSubs) {
      _subs = window.__skoochStoreIcalModalOpenSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIcalModalOpen(cb) {
  try {
    if (!window.__skoochStoreIcalModalOpenSubs) {
      window.__skoochStoreIcalModalOpenSubs = [];
    }
    window.__skoochStoreIcalModalOpenSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIcalModalOpenSubs ? window.__skoochStoreIcalModalOpenSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIcalModalMode() {
  try {
    if (((window.__skoochStoreIcalModalMode !== undefined) && (window.__skoochStoreIcalModalMode !== null))) {
      return window.__skoochStoreIcalModalMode;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "picker";
}
function setStoreIcalModalMode(v) {
  try {
    window.__skoochStoreIcalModalMode = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIcalModalModeSubs) {
      _subs = window.__skoochStoreIcalModalModeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIcalModalMode(cb) {
  try {
    if (!window.__skoochStoreIcalModalModeSubs) {
      window.__skoochStoreIcalModalModeSubs = [];
    }
    window.__skoochStoreIcalModalModeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIcalModalModeSubs ? window.__skoochStoreIcalModalModeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIcalModalTab() {
  try {
    if (((window.__skoochStoreIcalModalTab !== undefined) && (window.__skoochStoreIcalModalTab !== null))) {
      return window.__skoochStoreIcalModalTab;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "google";
}
function setStoreIcalModalTab(v) {
  try {
    window.__skoochStoreIcalModalTab = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIcalModalTabSubs) {
      _subs = window.__skoochStoreIcalModalTabSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIcalModalTab(cb) {
  try {
    if (!window.__skoochStoreIcalModalTabSubs) {
      window.__skoochStoreIcalModalTabSubs = [];
    }
    window.__skoochStoreIcalModalTabSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIcalModalTabSubs ? window.__skoochStoreIcalModalTabSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIcalModalError() {
  try {
    if (((window.__skoochStoreIcalModalError !== undefined) && (window.__skoochStoreIcalModalError !== null))) {
      return window.__skoochStoreIcalModalError;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreIcalModalError(v) {
  try {
    window.__skoochStoreIcalModalError = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIcalModalErrorSubs) {
      _subs = window.__skoochStoreIcalModalErrorSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIcalModalError(cb) {
  try {
    if (!window.__skoochStoreIcalModalErrorSubs) {
      window.__skoochStoreIcalModalErrorSubs = [];
    }
    window.__skoochStoreIcalModalErrorSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIcalModalErrorSubs ? window.__skoochStoreIcalModalErrorSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIcalLastResult() {
  try {
    if (((window.__skoochStoreIcalLastResult !== undefined) && (window.__skoochStoreIcalLastResult !== null))) {
      return window.__skoochStoreIcalLastResult;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return {};
}
function setStoreIcalLastResult(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : {});
  try {
    window.__skoochStoreIcalLastResult = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIcalLastResultSubs) {
      _subs = window.__skoochStoreIcalLastResultSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIcalLastResult(cb) {
  try {
    if (!window.__skoochStoreIcalLastResultSubs) {
      window.__skoochStoreIcalLastResultSubs = [];
    }
    window.__skoochStoreIcalLastResultSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIcalLastResultSubs ? window.__skoochStoreIcalLastResultSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIcalImportedCalendars() {
  try {
    if (window.__skoochStoreIcalImportedCalendars) {
      return window.__skoochStoreIcalImportedCalendars;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreIcalImportedCalendars(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : []);
  try {
    window.__skoochStoreIcalImportedCalendars = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIcalImportedCalendarsSubs) {
      _subs = window.__skoochStoreIcalImportedCalendarsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIcalImportedCalendars(cb) {
  try {
    if (!window.__skoochStoreIcalImportedCalendarsSubs) {
      window.__skoochStoreIcalImportedCalendarsSubs = [];
    }
    window.__skoochStoreIcalImportedCalendarsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIcalImportedCalendarsSubs ? window.__skoochStoreIcalImportedCalendarsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStorePrefFocusPeak() {
  try {
    if (((window.__skoochStorePrefFocusPeak !== undefined) && (window.__skoochStorePrefFocusPeak !== null))) {
      return window.__skoochStorePrefFocusPeak;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "morning";
}
function setStorePrefFocusPeak(v) {
  try {
    window.__skoochStorePrefFocusPeak = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStorePrefFocusPeakSubs) {
      _subs = window.__skoochStorePrefFocusPeakSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStorePrefFocusPeak(cb) {
  try {
    if (!window.__skoochStorePrefFocusPeakSubs) {
      window.__skoochStorePrefFocusPeakSubs = [];
    }
    window.__skoochStorePrefFocusPeakSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStorePrefFocusPeakSubs ? window.__skoochStorePrefFocusPeakSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreEnergyCurve() {
  try {
    if (((window.__skoochStoreEnergyCurve !== undefined) && (window.__skoochStoreEnergyCurve !== null))) {
      return window.__skoochStoreEnergyCurve;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return null;
}
function setStoreEnergyCurve(v) {
  try {
    window.__skoochStoreEnergyCurve = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreEnergyCurveSubs) {
      _subs = window.__skoochStoreEnergyCurveSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreEnergyCurve(cb) {
  try {
    if (!window.__skoochStoreEnergyCurveSubs) {
      window.__skoochStoreEnergyCurveSubs = [];
    }
    window.__skoochStoreEnergyCurveSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreEnergyCurveSubs ? window.__skoochStoreEnergyCurveSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreEnergyShape() {
  try {
    if (((window.__skoochStoreEnergyShape !== undefined) && (window.__skoochStoreEnergyShape !== null))) {
      return window.__skoochStoreEnergyShape;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreEnergyShape(v) {
  let _val = "";
  if ((((v !== undefined) && (v !== null)) && (typeof(v) === "string"))) {
    _val = v;
  }
  try {
    window.__skoochStoreEnergyShape = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreEnergyShapeSubs) {
      _subs = window.__skoochStoreEnergyShapeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreEnergyShape(cb) {
  try {
    if (!window.__skoochStoreEnergyShapeSubs) {
      window.__skoochStoreEnergyShapeSubs = [];
    }
    window.__skoochStoreEnergyShapeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreEnergyShapeSubs ? window.__skoochStoreEnergyShapeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStorePrefBlockLength() {
  try {
    if (((window.__skoochStorePrefBlockLength !== undefined) && (window.__skoochStorePrefBlockLength !== null))) {
      return window.__skoochStorePrefBlockLength;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 90;
}
function setStorePrefBlockLength(v) {
  try {
    window.__skoochStorePrefBlockLength = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStorePrefBlockLengthSubs) {
      _subs = window.__skoochStorePrefBlockLengthSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStorePrefBlockLength(cb) {
  try {
    if (!window.__skoochStorePrefBlockLengthSubs) {
      window.__skoochStorePrefBlockLengthSubs = [];
    }
    window.__skoochStorePrefBlockLengthSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStorePrefBlockLengthSubs ? window.__skoochStorePrefBlockLengthSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStorePrefWorkStyle() {
  try {
    if (((window.__skoochStorePrefWorkStyle !== undefined) && (window.__skoochStorePrefWorkStyle !== null))) {
      return window.__skoochStorePrefWorkStyle;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "deep-focus";
}
function setStorePrefWorkStyle(v) {
  try {
    window.__skoochStorePrefWorkStyle = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStorePrefWorkStyleSubs) {
      _subs = window.__skoochStorePrefWorkStyleSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStorePrefWorkStyle(cb) {
  try {
    if (!window.__skoochStorePrefWorkStyleSubs) {
      window.__skoochStorePrefWorkStyleSubs = [];
    }
    window.__skoochStorePrefWorkStyleSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStorePrefWorkStyleSubs ? window.__skoochStorePrefWorkStyleSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreRunOptimizeInFlight() {
  try {
    if (((window.__skoochStoreRunOptimizeInFlight !== undefined) && (window.__skoochStoreRunOptimizeInFlight !== null))) {
      return window.__skoochStoreRunOptimizeInFlight;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreRunOptimizeInFlight(v) {
  try {
    window.__skoochStoreRunOptimizeInFlight = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreRunOptimizeInFlightSubs) {
      _subs = window.__skoochStoreRunOptimizeInFlightSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreRunOptimizeInFlight(cb) {
  try {
    if (!window.__skoochStoreRunOptimizeInFlightSubs) {
      window.__skoochStoreRunOptimizeInFlightSubs = [];
    }
    window.__skoochStoreRunOptimizeInFlightSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreRunOptimizeInFlightSubs ? window.__skoochStoreRunOptimizeInFlightSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreScheduleHydrationEpoch() {
  try {
    if (((window.__skoochStoreScheduleHydrationEpoch !== undefined) && (window.__skoochStoreScheduleHydrationEpoch !== null))) {
      return window.__skoochStoreScheduleHydrationEpoch;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreScheduleHydrationEpoch(v) {
  try {
    window.__skoochStoreScheduleHydrationEpoch = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreScheduleHydrationEpochSubs) {
      _subs = window.__skoochStoreScheduleHydrationEpochSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreScheduleHydrationEpoch(cb) {
  try {
    if (!window.__skoochStoreScheduleHydrationEpochSubs) {
      window.__skoochStoreScheduleHydrationEpochSubs = [];
    }
    window.__skoochStoreScheduleHydrationEpochSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreScheduleHydrationEpochSubs ? window.__skoochStoreScheduleHydrationEpochSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIsAdmin() {
  return false;
}
function setStoreIsAdmin(v) {
  try {
    window.__skoochStoreIsAdmin = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIsAdminSubs) {
      _subs = window.__skoochStoreIsAdminSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIsAdmin(cb) {
  try {
    if (!window.__skoochStoreIsAdminSubs) {
      window.__skoochStoreIsAdminSubs = [];
    }
    window.__skoochStoreIsAdminSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreIsAdminSubs ? window.__skoochStoreIsAdminSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreCoachMarkStep() {
  try {
    if (((window.__skoochStoreCoachMarkStep !== undefined) && (window.__skoochStoreCoachMarkStep !== null))) {
      return window.__skoochStoreCoachMarkStep;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreCoachMarkStep(v) {
  try {
    window.__skoochStoreCoachMarkStep = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreCoachMarkStepSubs) {
      _subs = window.__skoochStoreCoachMarkStepSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreCoachMarkStep(cb) {
  try {
    if (!window.__skoochStoreCoachMarkStepSubs) {
      window.__skoochStoreCoachMarkStepSubs = [];
    }
    window.__skoochStoreCoachMarkStepSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreCoachMarkStepSubs ? window.__skoochStoreCoachMarkStepSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiPanelOpen() {
  try {
    if (((window.__skoochStoreAiPanelOpen !== undefined) && (window.__skoochStoreAiPanelOpen !== null))) {
      return window.__skoochStoreAiPanelOpen;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreAiPanelOpen(v) {
  try {
    window.__skoochStoreAiPanelOpen = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiPanelOpenSubs) {
      _subs = window.__skoochStoreAiPanelOpenSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiPanelOpen(cb) {
  try {
    if (!window.__skoochStoreAiPanelOpenSubs) {
      window.__skoochStoreAiPanelOpenSubs = [];
    }
    window.__skoochStoreAiPanelOpenSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiPanelOpenSubs ? window.__skoochStoreAiPanelOpenSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiChatInput() {
  try {
    if (((window.__skoochStoreAiChatInput !== undefined) && (window.__skoochStoreAiChatInput !== null))) {
      return window.__skoochStoreAiChatInput;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreAiChatInput(v) {
  try {
    window.__skoochStoreAiChatInput = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiChatInputSubs) {
      _subs = window.__skoochStoreAiChatInputSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiChatInput(cb) {
  try {
    if (!window.__skoochStoreAiChatInputSubs) {
      window.__skoochStoreAiChatInputSubs = [];
    }
    window.__skoochStoreAiChatInputSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiChatInputSubs ? window.__skoochStoreAiChatInputSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiChatMessages() {
  try {
    if (!window.__skoochStoreAiChatMessages) {
      window.__skoochStoreAiChatMessages = [aiWelcomeMessage()];
    }
    return window.__skoochStoreAiChatMessages;
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreAiChatMessages(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : []);
  try {
    window.__skoochStoreAiChatMessages = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiChatMessagesSubs) {
      _subs = window.__skoochStoreAiChatMessagesSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiChatMessages(cb) {
  try {
    if (!window.__skoochStoreAiChatMessagesSubs) {
      window.__skoochStoreAiChatMessagesSubs = [];
    }
    window.__skoochStoreAiChatMessagesSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiChatMessagesSubs ? window.__skoochStoreAiChatMessagesSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiChatBusy() {
  try {
    if (((window.__skoochStoreAiChatBusy !== undefined) && (window.__skoochStoreAiChatBusy !== null))) {
      return window.__skoochStoreAiChatBusy;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreAiChatBusy(v) {
  try {
    window.__skoochStoreAiChatBusy = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiChatBusySubs) {
      _subs = window.__skoochStoreAiChatBusySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiChatBusy(cb) {
  try {
    if (!window.__skoochStoreAiChatBusySubs) {
      window.__skoochStoreAiChatBusySubs = [];
    }
    window.__skoochStoreAiChatBusySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiChatBusySubs ? window.__skoochStoreAiChatBusySubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiChatError() {
  try {
    if (((window.__skoochStoreAiChatError !== undefined) && (window.__skoochStoreAiChatError !== null))) {
      return window.__skoochStoreAiChatError;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreAiChatError(v) {
  try {
    window.__skoochStoreAiChatError = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiChatErrorSubs) {
      _subs = window.__skoochStoreAiChatErrorSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiChatError(cb) {
  try {
    if (!window.__skoochStoreAiChatErrorSubs) {
      window.__skoochStoreAiChatErrorSubs = [];
    }
    window.__skoochStoreAiChatErrorSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiChatErrorSubs ? window.__skoochStoreAiChatErrorSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiChatMemory() {
  try {
    if (window.__skoochStoreAiChatMemory) {
      return window.__skoochStoreAiChatMemory;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreAiChatMemory(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : []);
  try {
    window.__skoochStoreAiChatMemory = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiChatMemorySubs) {
      _subs = window.__skoochStoreAiChatMemorySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiChatMemory(cb) {
  try {
    if (!window.__skoochStoreAiChatMemorySubs) {
      window.__skoochStoreAiChatMemorySubs = [];
    }
    window.__skoochStoreAiChatMemorySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiChatMemorySubs ? window.__skoochStoreAiChatMemorySubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiPendingPlan() {
  try {
    if (((window.__skoochStoreAiPendingPlan !== undefined) && (window.__skoochStoreAiPendingPlan !== null))) {
      return window.__skoochStoreAiPendingPlan;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return {};
}
function setStoreAiPendingPlan(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : {});
  try {
    window.__skoochStoreAiPendingPlan = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiPendingPlanSubs) {
      _subs = window.__skoochStoreAiPendingPlanSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiPendingPlan(cb) {
  try {
    if (!window.__skoochStoreAiPendingPlanSubs) {
      window.__skoochStoreAiPendingPlanSubs = [];
    }
    window.__skoochStoreAiPendingPlanSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiPendingPlanSubs ? window.__skoochStoreAiPendingPlanSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreChatLearnedPrefs() {
  try {
    if (((window.__skoochStoreChatLearnedPrefs !== undefined) && (window.__skoochStoreChatLearnedPrefs !== null))) {
      return window.__skoochStoreChatLearnedPrefs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return {};
}
function setStoreChatLearnedPrefs(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : {});
  try {
    window.__skoochStoreChatLearnedPrefs = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreChatLearnedPrefsSubs) {
      _subs = window.__skoochStoreChatLearnedPrefsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreChatLearnedPrefs(cb) {
  try {
    if (!window.__skoochStoreChatLearnedPrefsSubs) {
      window.__skoochStoreChatLearnedPrefsSubs = [];
    }
    window.__skoochStoreChatLearnedPrefsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreChatLearnedPrefsSubs ? window.__skoochStoreChatLearnedPrefsSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiTimeSuggestions() {
  try {
    if (window.__skoochStoreAiTimeSuggestions) {
      return window.__skoochStoreAiTimeSuggestions;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreAiTimeSuggestions(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : []);
  try {
    window.__skoochStoreAiTimeSuggestions = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiTimeSuggestionsSubs) {
      _subs = window.__skoochStoreAiTimeSuggestionsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiTimeSuggestions(cb) {
  try {
    if (!window.__skoochStoreAiTimeSuggestionsSubs) {
      window.__skoochStoreAiTimeSuggestionsSubs = [];
    }
    window.__skoochStoreAiTimeSuggestionsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiTimeSuggestionsSubs ? window.__skoochStoreAiTimeSuggestionsSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiActiveChatId() {
  try {
    if (((window.__skoochStoreAiActiveChatId !== undefined) && (window.__skoochStoreAiActiveChatId !== null))) {
      return window.__skoochStoreAiActiveChatId;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreAiActiveChatId(v) {
  try {
    window.__skoochStoreAiActiveChatId = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiActiveChatIdSubs) {
      _subs = window.__skoochStoreAiActiveChatIdSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiActiveChatId(cb) {
  try {
    if (!window.__skoochStoreAiActiveChatIdSubs) {
      window.__skoochStoreAiActiveChatIdSubs = [];
    }
    window.__skoochStoreAiActiveChatIdSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiActiveChatIdSubs ? window.__skoochStoreAiActiveChatIdSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiSavedChats() {
  try {
    if (window.__skoochStoreAiSavedChats) {
      return window.__skoochStoreAiSavedChats;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreAiSavedChats(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : []);
  try {
    window.__skoochStoreAiSavedChats = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiSavedChatsSubs) {
      _subs = window.__skoochStoreAiSavedChatsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiSavedChats(cb) {
  try {
    if (!window.__skoochStoreAiSavedChatsSubs) {
      window.__skoochStoreAiSavedChatsSubs = [];
    }
    window.__skoochStoreAiSavedChatsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiSavedChatsSubs ? window.__skoochStoreAiSavedChatsSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAiShowHistory() {
  try {
    if (((window.__skoochStoreAiShowHistory !== undefined) && (window.__skoochStoreAiShowHistory !== null))) {
      return window.__skoochStoreAiShowHistory;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreAiShowHistory(v) {
  try {
    window.__skoochStoreAiShowHistory = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAiShowHistorySubs) {
      _subs = window.__skoochStoreAiShowHistorySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAiShowHistory(cb) {
  try {
    if (!window.__skoochStoreAiShowHistorySubs) {
      window.__skoochStoreAiShowHistorySubs = [];
    }
    window.__skoochStoreAiShowHistorySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreAiShowHistorySubs ? window.__skoochStoreAiShowHistorySubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreChatMessagesUsedToday() {
  try {
    if (((window.__skoochStoreChatMessagesUsedToday !== undefined) && (window.__skoochStoreChatMessagesUsedToday !== null))) {
      return window.__skoochStoreChatMessagesUsedToday;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreChatMessagesUsedToday(v) {
  try {
    window.__skoochStoreChatMessagesUsedToday = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreChatMessagesUsedTodaySubs) {
      _subs = window.__skoochStoreChatMessagesUsedTodaySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreChatMessagesUsedToday(cb) {
  try {
    if (!window.__skoochStoreChatMessagesUsedTodaySubs) {
      window.__skoochStoreChatMessagesUsedTodaySubs = [];
    }
    window.__skoochStoreChatMessagesUsedTodaySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreChatMessagesUsedTodaySubs ? window.__skoochStoreChatMessagesUsedTodaySubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreChatLimit() {
  try {
    if (((window.__skoochStoreChatLimit !== undefined) && (window.__skoochStoreChatLimit !== null))) {
      return window.__skoochStoreChatLimit;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 10;
}
function setStoreChatLimit(v) {
  try {
    window.__skoochStoreChatLimit = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreChatLimitSubs) {
      _subs = window.__skoochStoreChatLimitSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _ce = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreChatLimit(cb) {
  try {
    if (!window.__skoochStoreChatLimitSubs) {
      window.__skoochStoreChatLimitSubs = [];
    }
    window.__skoochStoreChatLimitSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _s = (window.__skoochStoreChatLimitSubs ? window.__skoochStoreChatLimitSubs : []);
      let _i = _s.indexOf(cb);
      if ((_i >= 0)) {
        _s.splice(_i, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreThemeDark() {
  try {
    if (((window.__skoochStoreThemeDark !== undefined) && (window.__skoochStoreThemeDark !== null))) {
      return window.__skoochStoreThemeDark;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreThemeDark(v) {
  try {
    window.__skoochStoreThemeDark = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreThemeDarkSubs) {
      _subs = window.__skoochStoreThemeDarkSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreThemeDark(cb) {
  try {
    if (!window.__skoochStoreThemeDarkSubs) {
      window.__skoochStoreThemeDarkSubs = [];
    }
    window.__skoochStoreThemeDarkSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreThemeDarkSubs ? window.__skoochStoreThemeDarkSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSleepImportance() {
  try {
    if (((window.__skoochStoreSleepImportance !== undefined) && (window.__skoochStoreSleepImportance !== null))) {
      return window.__skoochStoreSleepImportance;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 5;
}
function setStoreSleepImportance(v) {
  try {
    window.__skoochStoreSleepImportance = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSleepImportanceSubs) {
      _subs = window.__skoochStoreSleepImportanceSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSleepImportance(cb) {
  try {
    if (!window.__skoochStoreSleepImportanceSubs) {
      window.__skoochStoreSleepImportanceSubs = [];
    }
    window.__skoochStoreSleepImportanceSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSleepImportanceSubs ? window.__skoochStoreSleepImportanceSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreWorkLifeBalance() {
  try {
    if (((window.__skoochStoreWorkLifeBalance !== undefined) && (window.__skoochStoreWorkLifeBalance !== null))) {
      return window.__skoochStoreWorkLifeBalance;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 5;
}
function setStoreWorkLifeBalance(v) {
  try {
    window.__skoochStoreWorkLifeBalance = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreWorkLifeBalanceSubs) {
      _subs = window.__skoochStoreWorkLifeBalanceSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreWorkLifeBalance(cb) {
  try {
    if (!window.__skoochStoreWorkLifeBalanceSubs) {
      window.__skoochStoreWorkLifeBalanceSubs = [];
    }
    window.__skoochStoreWorkLifeBalanceSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreWorkLifeBalanceSubs ? window.__skoochStoreWorkLifeBalanceSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStorePrefFallBehind() {
  try {
    if (((window.__skoochStorePrefFallBehind !== undefined) && (window.__skoochStorePrefFallBehind !== null))) {
      return window.__skoochStorePrefFallBehind;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "deadlines";
}
function setStorePrefFallBehind(v) {
  try {
    window.__skoochStorePrefFallBehind = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStorePrefFallBehindSubs) {
      _subs = window.__skoochStorePrefFallBehindSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStorePrefFallBehind(cb) {
  try {
    if (!window.__skoochStorePrefFallBehindSubs) {
      window.__skoochStorePrefFallBehindSubs = [];
    }
    window.__skoochStorePrefFallBehindSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStorePrefFallBehindSubs ? window.__skoochStorePrefFallBehindSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreLockPersonalTime() {
  try {
    if (((window.__skoochStoreLockPersonalTime !== undefined) && (window.__skoochStoreLockPersonalTime !== null))) {
      return window.__skoochStoreLockPersonalTime;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreLockPersonalTime(v) {
  try {
    window.__skoochStoreLockPersonalTime = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreLockPersonalTimeSubs) {
      _subs = window.__skoochStoreLockPersonalTimeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreLockPersonalTime(cb) {
  try {
    if (!window.__skoochStoreLockPersonalTimeSubs) {
      window.__skoochStoreLockPersonalTimeSubs = [];
    }
    window.__skoochStoreLockPersonalTimeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreLockPersonalTimeSubs ? window.__skoochStoreLockPersonalTimeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStorePrefEnergyDefault() {
  try {
    if (((window.__skoochStorePrefEnergyDefault !== undefined) && (window.__skoochStorePrefEnergyDefault !== null))) {
      return window.__skoochStorePrefEnergyDefault;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStorePrefEnergyDefault(v) {
  try {
    window.__skoochStorePrefEnergyDefault = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStorePrefEnergyDefaultSubs) {
      _subs = window.__skoochStorePrefEnergyDefaultSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStorePrefEnergyDefault(cb) {
  try {
    if (!window.__skoochStorePrefEnergyDefaultSubs) {
      window.__skoochStorePrefEnergyDefaultSubs = [];
    }
    window.__skoochStorePrefEnergyDefaultSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStorePrefEnergyDefaultSubs ? window.__skoochStorePrefEnergyDefaultSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStorePrefAiPanelDefault() {
  try {
    if (((window.__skoochStorePrefAiPanelDefault !== undefined) && (window.__skoochStorePrefAiPanelDefault !== null))) {
      return window.__skoochStorePrefAiPanelDefault;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return true;
}
function setStorePrefAiPanelDefault(v) {
  try {
    window.__skoochStorePrefAiPanelDefault = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStorePrefAiPanelDefaultSubs) {
      _subs = window.__skoochStorePrefAiPanelDefaultSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStorePrefAiPanelDefault(cb) {
  try {
    if (!window.__skoochStorePrefAiPanelDefaultSubs) {
      window.__skoochStorePrefAiPanelDefaultSubs = [];
    }
    window.__skoochStorePrefAiPanelDefaultSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStorePrefAiPanelDefaultSubs ? window.__skoochStorePrefAiPanelDefaultSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreBufferMins() {
  try {
    if (((window.__skoochStoreBufferMins !== undefined) && (window.__skoochStoreBufferMins !== null))) {
      return window.__skoochStoreBufferMins;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 15;
}
function setStoreBufferMins(v) {
  try {
    window.__skoochStoreBufferMins = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreBufferMinsSubs) {
      _subs = window.__skoochStoreBufferMinsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreBufferMins(cb) {
  try {
    if (!window.__skoochStoreBufferMinsSubs) {
      window.__skoochStoreBufferMinsSubs = [];
    }
    window.__skoochStoreBufferMinsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreBufferMinsSubs ? window.__skoochStoreBufferMinsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreEnergyData() {
  try {
    if (window.__skoochStoreEnergyData) {
      return window.__skoochStoreEnergyData;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreEnergyData(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : []);
  try {
    window.__skoochStoreEnergyData = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreEnergyDataSubs) {
      _subs = window.__skoochStoreEnergyDataSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreEnergyData(cb) {
  try {
    if (!window.__skoochStoreEnergyDataSubs) {
      window.__skoochStoreEnergyDataSubs = [];
    }
    window.__skoochStoreEnergyDataSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreEnergyDataSubs ? window.__skoochStoreEnergyDataSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchDevScenarioActive() {
  try {
    if (((window.__skoochStoreSchDevScenarioActive !== undefined) && (window.__skoochStoreSchDevScenarioActive !== null))) {
      return window.__skoochStoreSchDevScenarioActive;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchDevScenarioActive(v) {
  try {
    window.__skoochStoreSchDevScenarioActive = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSchDevScenarioActive = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchDevScenarioActiveSubs) {
      _subs = window.__skoochStoreSchDevScenarioActiveSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchDevScenarioActive(cb) {
  try {
    if (!window.__skoochStoreSchDevScenarioActiveSubs) {
      window.__skoochStoreSchDevScenarioActiveSubs = [];
    }
    window.__skoochStoreSchDevScenarioActiveSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchDevScenarioActiveSubs ? window.__skoochStoreSchDevScenarioActiveSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchOptimizeInputTotal() {
  try {
    if (((window.__skoochStoreSchOptimizeInputTotal !== undefined) && (window.__skoochStoreSchOptimizeInputTotal !== null))) {
      return window.__skoochStoreSchOptimizeInputTotal;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreSchOptimizeInputTotal(v) {
  try {
    window.__skoochStoreSchOptimizeInputTotal = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchOptimizeInputTotalSubs) {
      _subs = window.__skoochStoreSchOptimizeInputTotalSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchOptimizeInputTotal(cb) {
  try {
    if (!window.__skoochStoreSchOptimizeInputTotalSubs) {
      window.__skoochStoreSchOptimizeInputTotalSubs = [];
    }
    window.__skoochStoreSchOptimizeInputTotalSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchOptimizeInputTotalSubs ? window.__skoochStoreSchOptimizeInputTotalSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDevSyncScheduleAfterOptimize() {
  try {
    if (((window.__skoochStoreDevSyncScheduleAfterOptimize !== undefined) && (window.__skoochStoreDevSyncScheduleAfterOptimize !== null))) {
      return window.__skoochStoreDevSyncScheduleAfterOptimize;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreDevSyncScheduleAfterOptimize(v) {
  try {
    window.__skoochStoreDevSyncScheduleAfterOptimize = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDevSyncScheduleAfterOptimizeSubs) {
      _subs = window.__skoochStoreDevSyncScheduleAfterOptimizeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDevSyncScheduleAfterOptimize(cb) {
  try {
    if (!window.__skoochStoreDevSyncScheduleAfterOptimizeSubs) {
      window.__skoochStoreDevSyncScheduleAfterOptimizeSubs = [];
    }
    window.__skoochStoreDevSyncScheduleAfterOptimizeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDevSyncScheduleAfterOptimizeSubs ? window.__skoochStoreDevSyncScheduleAfterOptimizeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSelectedDay() {
  try {
    if (((window.__skoochStoreSelectedDay !== undefined) && (window.__skoochStoreSelectedDay !== null))) {
      return window.__skoochStoreSelectedDay;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreSelectedDay(v) {
  try {
    window.__skoochStoreSelectedDay = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochSelectedDay = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSelectedDaySubs) {
      _subs = window.__skoochStoreSelectedDaySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSelectedDay(cb) {
  try {
    if (!window.__skoochStoreSelectedDaySubs) {
      window.__skoochStoreSelectedDaySubs = [];
    }
    window.__skoochStoreSelectedDaySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSelectedDaySubs ? window.__skoochStoreSelectedDaySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreWeekOffset() {
  try {
    if (((window.__skoochStoreWeekOffset !== undefined) && (window.__skoochStoreWeekOffset !== null))) {
      return window.__skoochStoreWeekOffset;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreWeekOffset(v) {
  try {
    window.__skoochStoreWeekOffset = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochWeekOffset = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreWeekOffsetSubs) {
      _subs = window.__skoochStoreWeekOffsetSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreWeekOffset(cb) {
  try {
    if (!window.__skoochStoreWeekOffsetSubs) {
      window.__skoochStoreWeekOffsetSubs = [];
    }
    window.__skoochStoreWeekOffsetSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreWeekOffsetSubs ? window.__skoochStoreWeekOffsetSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIgnoreSavedDevSnapshot() {
  try {
    if (((window.__skoochStoreIgnoreSavedDevSnapshot !== undefined) && (window.__skoochStoreIgnoreSavedDevSnapshot !== null))) {
      return window.__skoochStoreIgnoreSavedDevSnapshot;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreIgnoreSavedDevSnapshot(v) {
  try {
    window.__skoochStoreIgnoreSavedDevSnapshot = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIgnoreSavedDevSnapshotSubs) {
      _subs = window.__skoochStoreIgnoreSavedDevSnapshotSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIgnoreSavedDevSnapshot(cb) {
  try {
    if (!window.__skoochStoreIgnoreSavedDevSnapshotSubs) {
      window.__skoochStoreIgnoreSavedDevSnapshotSubs = [];
    }
    window.__skoochStoreIgnoreSavedDevSnapshotSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIgnoreSavedDevSnapshotSubs ? window.__skoochStoreIgnoreSavedDevSnapshotSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreClearingSavedDevSnapshot() {
  try {
    if (((window.__skoochStoreClearingSavedDevSnapshot !== undefined) && (window.__skoochStoreClearingSavedDevSnapshot !== null))) {
      return window.__skoochStoreClearingSavedDevSnapshot;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreClearingSavedDevSnapshot(v) {
  try {
    window.__skoochStoreClearingSavedDevSnapshot = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreClearingSavedDevSnapshotSubs) {
      _subs = window.__skoochStoreClearingSavedDevSnapshotSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreClearingSavedDevSnapshot(cb) {
  try {
    if (!window.__skoochStoreClearingSavedDevSnapshotSubs) {
      window.__skoochStoreClearingSavedDevSnapshotSubs = [];
    }
    window.__skoochStoreClearingSavedDevSnapshotSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreClearingSavedDevSnapshotSubs ? window.__skoochStoreClearingSavedDevSnapshotSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchOverflowExpandedIds() {
  try {
    if (window.__skoochStoreSchOverflowExpandedIds) {
      return window.__skoochStoreSchOverflowExpandedIds;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreSchOverflowExpandedIds(o) {
  let _val = ((!_jac.list.eq(o, null) && !_jac.list.eq(o, undefined)) ? o : []);
  try {
    window.__skoochStoreSchOverflowExpandedIds = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchOverflowExpandedIdsSubs) {
      _subs = window.__skoochStoreSchOverflowExpandedIdsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchOverflowExpandedIds(cb) {
  try {
    if (!window.__skoochStoreSchOverflowExpandedIdsSubs) {
      window.__skoochStoreSchOverflowExpandedIdsSubs = [];
    }
    window.__skoochStoreSchOverflowExpandedIdsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchOverflowExpandedIdsSubs ? window.__skoochStoreSchOverflowExpandedIdsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDrawerOpen() {
  try {
    if (((window.__skoochStoreDrawerOpen !== undefined) && (window.__skoochStoreDrawerOpen !== null))) {
      return window.__skoochStoreDrawerOpen;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreDrawerOpen(v) {
  try {
    window.__skoochStoreDrawerOpen = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDrawerOpenSubs) {
      _subs = window.__skoochStoreDrawerOpenSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDrawerOpen(cb) {
  try {
    if (!window.__skoochStoreDrawerOpenSubs) {
      window.__skoochStoreDrawerOpenSubs = [];
    }
    window.__skoochStoreDrawerOpenSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDrawerOpenSubs ? window.__skoochStoreDrawerOpenSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDrawerTaskId() {
  try {
    if (((window.__skoochStoreDrawerTaskId !== undefined) && (window.__skoochStoreDrawerTaskId !== null))) {
      return window.__skoochStoreDrawerTaskId;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreDrawerTaskId(v) {
  try {
    window.__skoochStoreDrawerTaskId = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDrawerTaskIdSubs) {
      _subs = window.__skoochStoreDrawerTaskIdSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDrawerTaskId(cb) {
  try {
    if (!window.__skoochStoreDrawerTaskIdSubs) {
      window.__skoochStoreDrawerTaskIdSubs = [];
    }
    window.__skoochStoreDrawerTaskIdSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDrawerTaskIdSubs ? window.__skoochStoreDrawerTaskIdSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchPopupBlock() {
  try {
    if (((window.__skoochStoreSchPopupBlock !== undefined) && (window.__skoochStoreSchPopupBlock !== null))) {
      return window.__skoochStoreSchPopupBlock;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return {};
}
function setStoreSchPopupBlock(v) {
  try {
    window.__skoochStoreSchPopupBlock = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchPopupBlockSubs) {
      _subs = window.__skoochStoreSchPopupBlockSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchPopupBlock(cb) {
  try {
    if (!window.__skoochStoreSchPopupBlockSubs) {
      window.__skoochStoreSchPopupBlockSubs = [];
    }
    window.__skoochStoreSchPopupBlockSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchPopupBlockSubs ? window.__skoochStoreSchPopupBlockSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchReasoningTarget() {
  try {
    if (((window.__skoochStoreSchReasoningTarget !== undefined) && (window.__skoochStoreSchReasoningTarget !== null))) {
      return window.__skoochStoreSchReasoningTarget;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return {};
}
function setStoreSchReasoningTarget(v) {
  try {
    window.__skoochStoreSchReasoningTarget = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchReasoningTargetSubs) {
      _subs = window.__skoochStoreSchReasoningTargetSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchReasoningTarget(cb) {
  try {
    if (!window.__skoochStoreSchReasoningTargetSubs) {
      window.__skoochStoreSchReasoningTargetSubs = [];
    }
    window.__skoochStoreSchReasoningTargetSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchReasoningTargetSubs ? window.__skoochStoreSchReasoningTargetSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchPopupOpen() {
  try {
    if (((window.__skoochStoreSchPopupOpen !== undefined) && (window.__skoochStoreSchPopupOpen !== null))) {
      return window.__skoochStoreSchPopupOpen;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchPopupOpen(v) {
  try {
    window.__skoochStoreSchPopupOpen = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchPopupOpenSubs) {
      _subs = window.__skoochStoreSchPopupOpenSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchPopupOpen(cb) {
  try {
    if (!window.__skoochStoreSchPopupOpenSubs) {
      window.__skoochStoreSchPopupOpenSubs = [];
    }
    window.__skoochStoreSchPopupOpenSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchPopupOpenSubs ? window.__skoochStoreSchPopupOpenSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreProfileDirty() {
  try {
    if (((window.__skoochStoreProfileDirty !== undefined) && (window.__skoochStoreProfileDirty !== null))) {
      return window.__skoochStoreProfileDirty;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreProfileDirty(v) {
  try {
    window.__skoochStoreProfileDirty = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreProfileDirtySubs) {
      _subs = window.__skoochStoreProfileDirtySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreProfileDirty(cb) {
  try {
    if (!window.__skoochStoreProfileDirtySubs) {
      window.__skoochStoreProfileDirtySubs = [];
    }
    window.__skoochStoreProfileDirtySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreProfileDirtySubs ? window.__skoochStoreProfileDirtySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreOnboardingStep() {
  try {
    if (((window.__skoochStoreOnboardingStep !== undefined) && (window.__skoochStoreOnboardingStep !== null))) {
      return window.__skoochStoreOnboardingStep;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreOnboardingStep(v) {
  try {
    window.__skoochStoreOnboardingStep = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreOnboardingStepSubs) {
      _subs = window.__skoochStoreOnboardingStepSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreOnboardingStep(cb) {
  try {
    if (!window.__skoochStoreOnboardingStepSubs) {
      window.__skoochStoreOnboardingStepSubs = [];
    }
    window.__skoochStoreOnboardingStepSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreOnboardingStepSubs ? window.__skoochStoreOnboardingStepSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreCurrentPage() {
  try {
    if (((window.__skoochStoreCurrentPage !== undefined) && (window.__skoochStoreCurrentPage !== null))) {
      return window.__skoochStoreCurrentPage;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "dashboard";
}
function setStoreCurrentPage(v) {
  try {
    window.__skoochStoreCurrentPage = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochCurrentPage = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreCurrentPageSubs) {
      _subs = window.__skoochStoreCurrentPageSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreCurrentPage(cb) {
  try {
    if (!window.__skoochStoreCurrentPageSubs) {
      window.__skoochStoreCurrentPageSubs = [];
    }
    window.__skoochStoreCurrentPageSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreCurrentPageSubs ? window.__skoochStoreCurrentPageSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreViewMode() {
  try {
    if (((window.__skoochStoreViewMode !== undefined) && (window.__skoochStoreViewMode !== null))) {
      return window.__skoochStoreViewMode;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "loading";
}
function setStoreViewMode(v) {
  try {
    window.__skoochStoreViewMode = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreViewModeSubs) {
      _subs = window.__skoochStoreViewModeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreViewMode(cb) {
  try {
    if (!window.__skoochStoreViewModeSubs) {
      window.__skoochStoreViewModeSubs = [];
    }
    window.__skoochStoreViewModeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreViewModeSubs ? window.__skoochStoreViewModeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAuthMode() {
  try {
    if (((window.__skoochStoreAuthMode !== undefined) && (window.__skoochStoreAuthMode !== null))) {
      return window.__skoochStoreAuthMode;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "login";
}
function setStoreAuthMode(v) {
  try {
    window.__skoochStoreAuthMode = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAuthModeSubs) {
      _subs = window.__skoochStoreAuthModeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAuthMode(cb) {
  try {
    if (!window.__skoochStoreAuthModeSubs) {
      window.__skoochStoreAuthModeSubs = [];
    }
    window.__skoochStoreAuthModeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAuthModeSubs ? window.__skoochStoreAuthModeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAuthError() {
  try {
    if (((window.__skoochStoreAuthError !== undefined) && (window.__skoochStoreAuthError !== null))) {
      return window.__skoochStoreAuthError;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreAuthError(v) {
  try {
    window.__skoochStoreAuthError = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAuthErrorSubs) {
      _subs = window.__skoochStoreAuthErrorSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAuthError(cb) {
  try {
    if (!window.__skoochStoreAuthErrorSubs) {
      window.__skoochStoreAuthErrorSubs = [];
    }
    window.__skoochStoreAuthErrorSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAuthErrorSubs ? window.__skoochStoreAuthErrorSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAuthSuccess() {
  try {
    if (((window.__skoochStoreAuthSuccess !== undefined) && (window.__skoochStoreAuthSuccess !== null))) {
      return window.__skoochStoreAuthSuccess;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreAuthSuccess(v) {
  try {
    window.__skoochStoreAuthSuccess = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAuthSuccessSubs) {
      _subs = window.__skoochStoreAuthSuccessSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAuthSuccess(cb) {
  try {
    if (!window.__skoochStoreAuthSuccessSubs) {
      window.__skoochStoreAuthSuccessSubs = [];
    }
    window.__skoochStoreAuthSuccessSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAuthSuccessSubs ? window.__skoochStoreAuthSuccessSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAuthLoading() {
  try {
    if (((window.__skoochStoreAuthLoading !== undefined) && (window.__skoochStoreAuthLoading !== null))) {
      return window.__skoochStoreAuthLoading;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return true;
}
function setStoreAuthLoading(v) {
  try {
    window.__skoochStoreAuthLoading = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAuthLoadingSubs) {
      _subs = window.__skoochStoreAuthLoadingSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAuthLoading(cb) {
  try {
    if (!window.__skoochStoreAuthLoadingSubs) {
      window.__skoochStoreAuthLoadingSubs = [];
    }
    window.__skoochStoreAuthLoadingSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAuthLoadingSubs ? window.__skoochStoreAuthLoadingSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreProfileLoaded() {
  try {
    if (((window.__skoochStoreProfileLoaded !== undefined) && (window.__skoochStoreProfileLoaded !== null))) {
      return window.__skoochStoreProfileLoaded;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreProfileLoaded(v) {
  try {
    window.__skoochStoreProfileLoaded = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreProfileLoadedSubs) {
      _subs = window.__skoochStoreProfileLoadedSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreProfileLoaded(cb) {
  try {
    if (!window.__skoochStoreProfileLoadedSubs) {
      window.__skoochStoreProfileLoadedSubs = [];
    }
    window.__skoochStoreProfileLoadedSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreProfileLoadedSubs ? window.__skoochStoreProfileLoadedSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreProfileSkipped() {
  try {
    if (((window.__skoochStoreProfileSkipped !== undefined) && (window.__skoochStoreProfileSkipped !== null))) {
      return window.__skoochStoreProfileSkipped;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreProfileSkipped(v) {
  try {
    window.__skoochStoreProfileSkipped = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreProfileSkippedSubs) {
      _subs = window.__skoochStoreProfileSkippedSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreProfileSkipped(cb) {
  try {
    if (!window.__skoochStoreProfileSkippedSubs) {
      window.__skoochStoreProfileSkippedSubs = [];
    }
    window.__skoochStoreProfileSkippedSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreProfileSkippedSubs ? window.__skoochStoreProfileSkippedSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIsLoggedIn() {
  try {
    if (((window.__skoochStoreIsLoggedIn !== undefined) && (window.__skoochStoreIsLoggedIn !== null))) {
      return window.__skoochStoreIsLoggedIn;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreIsLoggedIn(v) {
  try {
    window.__skoochStoreIsLoggedIn = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIsLoggedInSubs) {
      _subs = window.__skoochStoreIsLoggedInSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIsLoggedIn(cb) {
  try {
    if (!window.__skoochStoreIsLoggedInSubs) {
      window.__skoochStoreIsLoggedInSubs = [];
    }
    window.__skoochStoreIsLoggedInSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIsLoggedInSubs ? window.__skoochStoreIsLoggedInSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreClaimPending() {
  try {
    if (((window.__skoochStoreClaimPending !== undefined) && (window.__skoochStoreClaimPending !== null))) {
      return window.__skoochStoreClaimPending;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreClaimPending(v) {
  try {
    window.__skoochStoreClaimPending = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    window.__skoochClaimPending = v;
  } catch (__jac_e) {
    if (true) {
      let _e1 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreClaimPendingSubs) {
      _subs = window.__skoochStoreClaimPendingSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreClaimPending(cb) {
  try {
    if (!window.__skoochStoreClaimPendingSubs) {
      window.__skoochStoreClaimPendingSubs = [];
    }
    window.__skoochStoreClaimPendingSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreClaimPendingSubs ? window.__skoochStoreClaimPendingSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchZoom() {
  try {
    if (((window.__skoochStoreSchZoom !== undefined) && (window.__skoochStoreSchZoom !== null))) {
      return window.__skoochStoreSchZoom;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 2;
}
function setStoreSchZoom(v) {
  try {
    window.__skoochStoreSchZoom = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchZoomSubs) {
      _subs = window.__skoochStoreSchZoomSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchZoom(cb) {
  try {
    if (!window.__skoochStoreSchZoomSubs) {
      window.__skoochStoreSchZoomSubs = [];
    }
    window.__skoochStoreSchZoomSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchZoomSubs ? window.__skoochStoreSchZoomSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAnonDataBannerDismissed() {
  try {
    if (((window.__skoochStoreAnonDataBannerDismissed !== undefined) && (window.__skoochStoreAnonDataBannerDismissed !== null))) {
      return window.__skoochStoreAnonDataBannerDismissed;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreAnonDataBannerDismissed(v) {
  try {
    window.__skoochStoreAnonDataBannerDismissed = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAnonDataBannerDismissedSubs) {
      _subs = window.__skoochStoreAnonDataBannerDismissedSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAnonDataBannerDismissed(cb) {
  try {
    if (!window.__skoochStoreAnonDataBannerDismissedSubs) {
      window.__skoochStoreAnonDataBannerDismissedSubs = [];
    }
    window.__skoochStoreAnonDataBannerDismissedSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAnonDataBannerDismissedSubs ? window.__skoochStoreAnonDataBannerDismissedSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAuthEmail() {
  try {
    if (((window.__skoochStoreAuthEmail !== undefined) && (window.__skoochStoreAuthEmail !== null))) {
      return window.__skoochStoreAuthEmail;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreAuthEmail(v) {
  try {
    window.__skoochStoreAuthEmail = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAuthEmailSubs) {
      _subs = window.__skoochStoreAuthEmailSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAuthEmail(cb) {
  try {
    if (!window.__skoochStoreAuthEmailSubs) {
      window.__skoochStoreAuthEmailSubs = [];
    }
    window.__skoochStoreAuthEmailSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAuthEmailSubs ? window.__skoochStoreAuthEmailSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAuthPassword() {
  try {
    if (((window.__skoochStoreAuthPassword !== undefined) && (window.__skoochStoreAuthPassword !== null))) {
      return window.__skoochStoreAuthPassword;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreAuthPassword(v) {
  try {
    window.__skoochStoreAuthPassword = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAuthPasswordSubs) {
      _subs = window.__skoochStoreAuthPasswordSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAuthPassword(cb) {
  try {
    if (!window.__skoochStoreAuthPasswordSubs) {
      window.__skoochStoreAuthPasswordSubs = [];
    }
    window.__skoochStoreAuthPasswordSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAuthPasswordSubs ? window.__skoochStoreAuthPasswordSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAuthPasswordConfirm() {
  try {
    if (((window.__skoochStoreAuthPasswordConfirm !== undefined) && (window.__skoochStoreAuthPasswordConfirm !== null))) {
      return window.__skoochStoreAuthPasswordConfirm;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreAuthPasswordConfirm(v) {
  try {
    window.__skoochStoreAuthPasswordConfirm = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAuthPasswordConfirmSubs) {
      _subs = window.__skoochStoreAuthPasswordConfirmSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAuthPasswordConfirm(cb) {
  try {
    if (!window.__skoochStoreAuthPasswordConfirmSubs) {
      window.__skoochStoreAuthPasswordConfirmSubs = [];
    }
    window.__skoochStoreAuthPasswordConfirmSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAuthPasswordConfirmSubs ? window.__skoochStoreAuthPasswordConfirmSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreRememberMe() {
  try {
    if (!window.__skoochRememberSeeded) {
      window.__skoochRememberSeeded = true;
      let _saved = "";
      try {
        _saved = (localStorage.getItem("skooch_remember_email") || "");
      } catch (__jac_e) {
        if (true) {
          let _els = __jac_e;
          _saved = "";
        } else {
          throw __jac_e;
        }
      }
      if (_saved) {
        window.__skoochStoreRememberMe = true;
        try {
          if ((((window.__skoochStoreAuthEmail === undefined) || (window.__skoochStoreAuthEmail === null)) || (window.__skoochStoreAuthEmail === ""))) {
            window.__skoochStoreAuthEmail = _saved;
          }
        } catch (__jac_e) {
          if (true) {
            let _ese = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      }
    }
  } catch (__jac_e) {
    if (true) {
      let _eseed = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    if (((window.__skoochStoreRememberMe !== undefined) && (window.__skoochStoreRememberMe !== null))) {
      return window.__skoochStoreRememberMe;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return true;
}
function setStoreRememberMe(v) {
  try {
    window.__skoochStoreRememberMe = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreRememberMeSubs) {
      _subs = window.__skoochStoreRememberMeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreRememberMe(cb) {
  try {
    if (!window.__skoochStoreRememberMeSubs) {
      window.__skoochStoreRememberMeSubs = [];
    }
    window.__skoochStoreRememberMeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreRememberMeSubs ? window.__skoochStoreRememberMeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAgreedToTerms() {
  try {
    if (((window.__skoochStoreAgreedToTerms !== undefined) && (window.__skoochStoreAgreedToTerms !== null))) {
      return window.__skoochStoreAgreedToTerms;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreAgreedToTerms(v) {
  try {
    window.__skoochStoreAgreedToTerms = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAgreedToTermsSubs) {
      _subs = window.__skoochStoreAgreedToTermsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAgreedToTerms(cb) {
  try {
    if (!window.__skoochStoreAgreedToTermsSubs) {
      window.__skoochStoreAgreedToTermsSubs = [];
    }
    window.__skoochStoreAgreedToTermsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAgreedToTermsSubs ? window.__skoochStoreAgreedToTermsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreShowEulaModal() {
  try {
    if (((window.__skoochStoreShowEulaModal !== undefined) && (window.__skoochStoreShowEulaModal !== null))) {
      return window.__skoochStoreShowEulaModal;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreShowEulaModal(v) {
  try {
    window.__skoochStoreShowEulaModal = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreShowEulaModalSubs) {
      _subs = window.__skoochStoreShowEulaModalSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreShowEulaModal(cb) {
  try {
    if (!window.__skoochStoreShowEulaModalSubs) {
      window.__skoochStoreShowEulaModalSubs = [];
    }
    window.__skoochStoreShowEulaModalSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreShowEulaModalSubs ? window.__skoochStoreShowEulaModalSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAuthShowPassword() {
  try {
    if (((window.__skoochStoreAuthShowPassword !== undefined) && (window.__skoochStoreAuthShowPassword !== null))) {
      return window.__skoochStoreAuthShowPassword;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreAuthShowPassword(v) {
  try {
    window.__skoochStoreAuthShowPassword = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAuthShowPasswordSubs) {
      _subs = window.__skoochStoreAuthShowPasswordSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAuthShowPassword(cb) {
  try {
    if (!window.__skoochStoreAuthShowPasswordSubs) {
      window.__skoochStoreAuthShowPasswordSubs = [];
    }
    window.__skoochStoreAuthShowPasswordSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAuthShowPasswordSubs ? window.__skoochStoreAuthShowPasswordSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAuthShowPasswordConfirm() {
  try {
    if (((window.__skoochStoreAuthShowPasswordConfirm !== undefined) && (window.__skoochStoreAuthShowPasswordConfirm !== null))) {
      return window.__skoochStoreAuthShowPasswordConfirm;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreAuthShowPasswordConfirm(v) {
  try {
    window.__skoochStoreAuthShowPasswordConfirm = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAuthShowPasswordConfirmSubs) {
      _subs = window.__skoochStoreAuthShowPasswordConfirmSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAuthShowPasswordConfirm(cb) {
  try {
    if (!window.__skoochStoreAuthShowPasswordConfirmSubs) {
      window.__skoochStoreAuthShowPasswordConfirmSubs = [];
    }
    window.__skoochStoreAuthShowPasswordConfirmSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAuthShowPasswordConfirmSubs ? window.__skoochStoreAuthShowPasswordConfirmSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDrawerTitle() {
  try {
    if (((window.__skoochStoreDrawerTitle !== undefined) && (window.__skoochStoreDrawerTitle !== null))) {
      return window.__skoochStoreDrawerTitle;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreDrawerTitle(v) {
  try {
    window.__skoochStoreDrawerTitle = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDrawerTitleSubs) {
      _subs = window.__skoochStoreDrawerTitleSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDrawerTitle(cb) {
  try {
    if (!window.__skoochStoreDrawerTitleSubs) {
      window.__skoochStoreDrawerTitleSubs = [];
    }
    window.__skoochStoreDrawerTitleSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDrawerTitleSubs ? window.__skoochStoreDrawerTitleSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDrawerCategory() {
  try {
    if (((window.__skoochStoreDrawerCategory !== undefined) && (window.__skoochStoreDrawerCategory !== null))) {
      return window.__skoochStoreDrawerCategory;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "other";
}
function setStoreDrawerCategory(v) {
  try {
    window.__skoochStoreDrawerCategory = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDrawerCategorySubs) {
      _subs = window.__skoochStoreDrawerCategorySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDrawerCategory(cb) {
  try {
    if (!window.__skoochStoreDrawerCategorySubs) {
      window.__skoochStoreDrawerCategorySubs = [];
    }
    window.__skoochStoreDrawerCategorySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDrawerCategorySubs ? window.__skoochStoreDrawerCategorySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDrawerDeadline() {
  try {
    if (((window.__skoochStoreDrawerDeadline !== undefined) && (window.__skoochStoreDrawerDeadline !== null))) {
      return window.__skoochStoreDrawerDeadline;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreDrawerDeadline(v) {
  try {
    window.__skoochStoreDrawerDeadline = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDrawerDeadlineSubs) {
      _subs = window.__skoochStoreDrawerDeadlineSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDrawerDeadline(cb) {
  try {
    if (!window.__skoochStoreDrawerDeadlineSubs) {
      window.__skoochStoreDrawerDeadlineSubs = [];
    }
    window.__skoochStoreDrawerDeadlineSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDrawerDeadlineSubs ? window.__skoochStoreDrawerDeadlineSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDrawerPriority() {
  try {
    if (((window.__skoochStoreDrawerPriority !== undefined) && (window.__skoochStoreDrawerPriority !== null))) {
      return window.__skoochStoreDrawerPriority;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 5;
}
function setStoreDrawerPriority(v) {
  try {
    window.__skoochStoreDrawerPriority = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDrawerPrioritySubs) {
      _subs = window.__skoochStoreDrawerPrioritySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDrawerPriority(cb) {
  try {
    if (!window.__skoochStoreDrawerPrioritySubs) {
      window.__skoochStoreDrawerPrioritySubs = [];
    }
    window.__skoochStoreDrawerPrioritySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDrawerPrioritySubs ? window.__skoochStoreDrawerPrioritySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDrawerOverride() {
  try {
    if (((window.__skoochStoreDrawerOverride !== undefined) && (window.__skoochStoreDrawerOverride !== null))) {
      return window.__skoochStoreDrawerOverride;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreDrawerOverride(v) {
  try {
    window.__skoochStoreDrawerOverride = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDrawerOverrideSubs) {
      _subs = window.__skoochStoreDrawerOverrideSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDrawerOverride(cb) {
  try {
    if (!window.__skoochStoreDrawerOverrideSubs) {
      window.__skoochStoreDrawerOverrideSubs = [];
    }
    window.__skoochStoreDrawerOverrideSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDrawerOverrideSubs ? window.__skoochStoreDrawerOverrideSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalOpen() {
  try {
    if (((window.__skoochStoreModalOpen !== undefined) && (window.__skoochStoreModalOpen !== null))) {
      return window.__skoochStoreModalOpen;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreModalOpen(v) {
  try {
    window.__skoochStoreModalOpen = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalOpenSubs) {
      _subs = window.__skoochStoreModalOpenSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalOpen(cb) {
  try {
    if (!window.__skoochStoreModalOpenSubs) {
      window.__skoochStoreModalOpenSubs = [];
    }
    window.__skoochStoreModalOpenSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalOpenSubs ? window.__skoochStoreModalOpenSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalTitle() {
  try {
    if (((window.__skoochStoreModalTitle !== undefined) && (window.__skoochStoreModalTitle !== null))) {
      return window.__skoochStoreModalTitle;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreModalTitle(v) {
  try {
    window.__skoochStoreModalTitle = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalTitleSubs) {
      _subs = window.__skoochStoreModalTitleSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalTitle(cb) {
  try {
    if (!window.__skoochStoreModalTitleSubs) {
      window.__skoochStoreModalTitleSubs = [];
    }
    window.__skoochStoreModalTitleSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalTitleSubs ? window.__skoochStoreModalTitleSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalCategory() {
  try {
    if (((window.__skoochStoreModalCategory !== undefined) && (window.__skoochStoreModalCategory !== null))) {
      return window.__skoochStoreModalCategory;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "other";
}
function setStoreModalCategory(v) {
  try {
    window.__skoochStoreModalCategory = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalCategorySubs) {
      _subs = window.__skoochStoreModalCategorySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalCategory(cb) {
  try {
    if (!window.__skoochStoreModalCategorySubs) {
      window.__skoochStoreModalCategorySubs = [];
    }
    window.__skoochStoreModalCategorySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalCategorySubs ? window.__skoochStoreModalCategorySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalDeadline() {
  try {
    if (((window.__skoochStoreModalDeadline !== undefined) && (window.__skoochStoreModalDeadline !== null))) {
      return window.__skoochStoreModalDeadline;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreModalDeadline(v) {
  try {
    window.__skoochStoreModalDeadline = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalDeadlineSubs) {
      _subs = window.__skoochStoreModalDeadlineSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalDeadline(cb) {
  try {
    if (!window.__skoochStoreModalDeadlineSubs) {
      window.__skoochStoreModalDeadlineSubs = [];
    }
    window.__skoochStoreModalDeadlineSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalDeadlineSubs ? window.__skoochStoreModalDeadlineSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalPriority() {
  try {
    if (((window.__skoochStoreModalPriority !== undefined) && (window.__skoochStoreModalPriority !== null))) {
      return window.__skoochStoreModalPriority;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 5;
}
function setStoreModalPriority(v) {
  try {
    window.__skoochStoreModalPriority = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalPrioritySubs) {
      _subs = window.__skoochStoreModalPrioritySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalPriority(cb) {
  try {
    if (!window.__skoochStoreModalPrioritySubs) {
      window.__skoochStoreModalPrioritySubs = [];
    }
    window.__skoochStoreModalPrioritySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalPrioritySubs ? window.__skoochStoreModalPrioritySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalBreakdown() {
  try {
    if (((window.__skoochStoreModalBreakdown !== undefined) && (window.__skoochStoreModalBreakdown !== null))) {
      return window.__skoochStoreModalBreakdown;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreModalBreakdown(v) {
  try {
    window.__skoochStoreModalBreakdown = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalBreakdownSubs) {
      _subs = window.__skoochStoreModalBreakdownSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalBreakdown(cb) {
  try {
    if (!window.__skoochStoreModalBreakdownSubs) {
      window.__skoochStoreModalBreakdownSubs = [];
    }
    window.__skoochStoreModalBreakdownSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalBreakdownSubs ? window.__skoochStoreModalBreakdownSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalSubtasks() {
  try {
    if (((window.__skoochStoreModalSubtasks !== undefined) && (window.__skoochStoreModalSubtasks !== null))) {
      return window.__skoochStoreModalSubtasks;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreModalSubtasks(v) {
  try {
    window.__skoochStoreModalSubtasks = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalSubtasksSubs) {
      _subs = window.__skoochStoreModalSubtasksSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalSubtasks(cb) {
  try {
    if (!window.__skoochStoreModalSubtasksSubs) {
      window.__skoochStoreModalSubtasksSubs = [];
    }
    window.__skoochStoreModalSubtasksSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalSubtasksSubs ? window.__skoochStoreModalSubtasksSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalIsEvent() {
  try {
    if (((window.__skoochStoreModalIsEvent !== undefined) && (window.__skoochStoreModalIsEvent !== null))) {
      return window.__skoochStoreModalIsEvent;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreModalIsEvent(v) {
  try {
    window.__skoochStoreModalIsEvent = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalIsEventSubs) {
      _subs = window.__skoochStoreModalIsEventSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalIsEvent(cb) {
  try {
    if (!window.__skoochStoreModalIsEventSubs) {
      window.__skoochStoreModalIsEventSubs = [];
    }
    window.__skoochStoreModalIsEventSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalIsEventSubs ? window.__skoochStoreModalIsEventSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalStartTime() {
  try {
    if (((window.__skoochStoreModalStartTime !== undefined) && (window.__skoochStoreModalStartTime !== null))) {
      return window.__skoochStoreModalStartTime;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "09:00";
}
function setStoreModalStartTime(v) {
  try {
    window.__skoochStoreModalStartTime = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalStartTimeSubs) {
      _subs = window.__skoochStoreModalStartTimeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalStartTime(cb) {
  try {
    if (!window.__skoochStoreModalStartTimeSubs) {
      window.__skoochStoreModalStartTimeSubs = [];
    }
    window.__skoochStoreModalStartTimeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalStartTimeSubs ? window.__skoochStoreModalStartTimeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalEndTime() {
  try {
    if (((window.__skoochStoreModalEndTime !== undefined) && (window.__skoochStoreModalEndTime !== null))) {
      return window.__skoochStoreModalEndTime;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "10:00";
}
function setStoreModalEndTime(v) {
  try {
    window.__skoochStoreModalEndTime = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalEndTimeSubs) {
      _subs = window.__skoochStoreModalEndTimeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalEndTime(cb) {
  try {
    if (!window.__skoochStoreModalEndTimeSubs) {
      window.__skoochStoreModalEndTimeSubs = [];
    }
    window.__skoochStoreModalEndTimeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalEndTimeSubs ? window.__skoochStoreModalEndTimeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalError() {
  try {
    if (((window.__skoochStoreModalError !== undefined) && (window.__skoochStoreModalError !== null))) {
      return window.__skoochStoreModalError;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreModalError(v) {
  try {
    window.__skoochStoreModalError = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalErrorSubs) {
      _subs = window.__skoochStoreModalErrorSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalError(cb) {
  try {
    if (!window.__skoochStoreModalErrorSubs) {
      window.__skoochStoreModalErrorSubs = [];
    }
    window.__skoochStoreModalErrorSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalErrorSubs ? window.__skoochStoreModalErrorSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalDayMon() {
  try {
    if (((window.__skoochStoreModalDayMon !== undefined) && (window.__skoochStoreModalDayMon !== null))) {
      return window.__skoochStoreModalDayMon;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreModalDayMon(v) {
  try {
    window.__skoochStoreModalDayMon = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalDayMonSubs) {
      _subs = window.__skoochStoreModalDayMonSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalDayMon(cb) {
  try {
    if (!window.__skoochStoreModalDayMonSubs) {
      window.__skoochStoreModalDayMonSubs = [];
    }
    window.__skoochStoreModalDayMonSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalDayMonSubs ? window.__skoochStoreModalDayMonSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalDayTue() {
  try {
    if (((window.__skoochStoreModalDayTue !== undefined) && (window.__skoochStoreModalDayTue !== null))) {
      return window.__skoochStoreModalDayTue;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreModalDayTue(v) {
  try {
    window.__skoochStoreModalDayTue = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalDayTueSubs) {
      _subs = window.__skoochStoreModalDayTueSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalDayTue(cb) {
  try {
    if (!window.__skoochStoreModalDayTueSubs) {
      window.__skoochStoreModalDayTueSubs = [];
    }
    window.__skoochStoreModalDayTueSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalDayTueSubs ? window.__skoochStoreModalDayTueSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalDayWed() {
  try {
    if (((window.__skoochStoreModalDayWed !== undefined) && (window.__skoochStoreModalDayWed !== null))) {
      return window.__skoochStoreModalDayWed;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreModalDayWed(v) {
  try {
    window.__skoochStoreModalDayWed = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalDayWedSubs) {
      _subs = window.__skoochStoreModalDayWedSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalDayWed(cb) {
  try {
    if (!window.__skoochStoreModalDayWedSubs) {
      window.__skoochStoreModalDayWedSubs = [];
    }
    window.__skoochStoreModalDayWedSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalDayWedSubs ? window.__skoochStoreModalDayWedSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalDayThu() {
  try {
    if (((window.__skoochStoreModalDayThu !== undefined) && (window.__skoochStoreModalDayThu !== null))) {
      return window.__skoochStoreModalDayThu;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreModalDayThu(v) {
  try {
    window.__skoochStoreModalDayThu = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalDayThuSubs) {
      _subs = window.__skoochStoreModalDayThuSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalDayThu(cb) {
  try {
    if (!window.__skoochStoreModalDayThuSubs) {
      window.__skoochStoreModalDayThuSubs = [];
    }
    window.__skoochStoreModalDayThuSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalDayThuSubs ? window.__skoochStoreModalDayThuSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalDayFri() {
  try {
    if (((window.__skoochStoreModalDayFri !== undefined) && (window.__skoochStoreModalDayFri !== null))) {
      return window.__skoochStoreModalDayFri;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreModalDayFri(v) {
  try {
    window.__skoochStoreModalDayFri = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalDayFriSubs) {
      _subs = window.__skoochStoreModalDayFriSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalDayFri(cb) {
  try {
    if (!window.__skoochStoreModalDayFriSubs) {
      window.__skoochStoreModalDayFriSubs = [];
    }
    window.__skoochStoreModalDayFriSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalDayFriSubs ? window.__skoochStoreModalDayFriSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalDaySat() {
  try {
    if (((window.__skoochStoreModalDaySat !== undefined) && (window.__skoochStoreModalDaySat !== null))) {
      return window.__skoochStoreModalDaySat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreModalDaySat(v) {
  try {
    window.__skoochStoreModalDaySat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalDaySatSubs) {
      _subs = window.__skoochStoreModalDaySatSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalDaySat(cb) {
  try {
    if (!window.__skoochStoreModalDaySatSubs) {
      window.__skoochStoreModalDaySatSubs = [];
    }
    window.__skoochStoreModalDaySatSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalDaySatSubs ? window.__skoochStoreModalDaySatSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreModalDaySun() {
  try {
    if (((window.__skoochStoreModalDaySun !== undefined) && (window.__skoochStoreModalDaySun !== null))) {
      return window.__skoochStoreModalDaySun;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreModalDaySun(v) {
  try {
    window.__skoochStoreModalDaySun = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreModalDaySunSubs) {
      _subs = window.__skoochStoreModalDaySunSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreModalDaySun(cb) {
  try {
    if (!window.__skoochStoreModalDaySunSubs) {
      window.__skoochStoreModalDaySunSubs = [];
    }
    window.__skoochStoreModalDaySunSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreModalDaySunSubs ? window.__skoochStoreModalDaySunSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskName() {
  try {
    if (((window.__skoochStoreSchTaskName !== undefined) && (window.__skoochStoreSchTaskName !== null))) {
      return window.__skoochStoreSchTaskName;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchTaskName(v) {
  try {
    window.__skoochStoreSchTaskName = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskNameSubs) {
      _subs = window.__skoochStoreSchTaskNameSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskName(cb) {
  try {
    if (!window.__skoochStoreSchTaskNameSubs) {
      window.__skoochStoreSchTaskNameSubs = [];
    }
    window.__skoochStoreSchTaskNameSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskNameSubs ? window.__skoochStoreSchTaskNameSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskCat() {
  try {
    if (((window.__skoochStoreSchTaskCat !== undefined) && (window.__skoochStoreSchTaskCat !== null))) {
      return window.__skoochStoreSchTaskCat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchTaskCat(v) {
  try {
    window.__skoochStoreSchTaskCat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskCatSubs) {
      _subs = window.__skoochStoreSchTaskCatSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskCat(cb) {
  try {
    if (!window.__skoochStoreSchTaskCatSubs) {
      window.__skoochStoreSchTaskCatSubs = [];
    }
    window.__skoochStoreSchTaskCatSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskCatSubs ? window.__skoochStoreSchTaskCatSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskDur() {
  try {
    if (((window.__skoochStoreSchTaskDur !== undefined) && (window.__skoochStoreSchTaskDur !== null))) {
      return window.__skoochStoreSchTaskDur;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 60;
}
function setStoreSchTaskDur(v) {
  try {
    window.__skoochStoreSchTaskDur = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskDurSubs) {
      _subs = window.__skoochStoreSchTaskDurSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskDur(cb) {
  try {
    if (!window.__skoochStoreSchTaskDurSubs) {
      window.__skoochStoreSchTaskDurSubs = [];
    }
    window.__skoochStoreSchTaskDurSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskDurSubs ? window.__skoochStoreSchTaskDurSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskDurH() {
  try {
    if (((window.__skoochStoreSchTaskDurH !== undefined) && (window.__skoochStoreSchTaskDurH !== null))) {
      return window.__skoochStoreSchTaskDurH;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 1;
}
function setStoreSchTaskDurH(v) {
  try {
    window.__skoochStoreSchTaskDurH = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskDurHSubs) {
      _subs = window.__skoochStoreSchTaskDurHSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskDurH(cb) {
  try {
    if (!window.__skoochStoreSchTaskDurHSubs) {
      window.__skoochStoreSchTaskDurHSubs = [];
    }
    window.__skoochStoreSchTaskDurHSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskDurHSubs ? window.__skoochStoreSchTaskDurHSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskDurM() {
  try {
    if (((window.__skoochStoreSchTaskDurM !== undefined) && (window.__skoochStoreSchTaskDurM !== null))) {
      return window.__skoochStoreSchTaskDurM;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreSchTaskDurM(v) {
  try {
    window.__skoochStoreSchTaskDurM = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskDurMSubs) {
      _subs = window.__skoochStoreSchTaskDurMSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskDurM(cb) {
  try {
    if (!window.__skoochStoreSchTaskDurMSubs) {
      window.__skoochStoreSchTaskDurMSubs = [];
    }
    window.__skoochStoreSchTaskDurMSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskDurMSubs ? window.__skoochStoreSchTaskDurMSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskPri() {
  try {
    if (((window.__skoochStoreSchTaskPri !== undefined) && (window.__skoochStoreSchTaskPri !== null))) {
      return window.__skoochStoreSchTaskPri;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 5;
}
function setStoreSchTaskPri(v) {
  try {
    window.__skoochStoreSchTaskPri = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskPriSubs) {
      _subs = window.__skoochStoreSchTaskPriSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskPri(cb) {
  try {
    if (!window.__skoochStoreSchTaskPriSubs) {
      window.__skoochStoreSchTaskPriSubs = [];
    }
    window.__skoochStoreSchTaskPriSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskPriSubs ? window.__skoochStoreSchTaskPriSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskDL() {
  try {
    if (((window.__skoochStoreSchTaskDL !== undefined) && (window.__skoochStoreSchTaskDL !== null))) {
      return window.__skoochStoreSchTaskDL;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchTaskDL(v) {
  try {
    window.__skoochStoreSchTaskDL = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskDLSubs) {
      _subs = window.__skoochStoreSchTaskDLSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskDL(cb) {
  try {
    if (!window.__skoochStoreSchTaskDLSubs) {
      window.__skoochStoreSchTaskDLSubs = [];
    }
    window.__skoochStoreSchTaskDLSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskDLSubs ? window.__skoochStoreSchTaskDLSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskIsRecurring() {
  try {
    if (((window.__skoochStoreSchTaskIsRecurring !== undefined) && (window.__skoochStoreSchTaskIsRecurring !== null))) {
      return window.__skoochStoreSchTaskIsRecurring;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchTaskIsRecurring(v) {
  try {
    window.__skoochStoreSchTaskIsRecurring = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskIsRecurringSubs) {
      _subs = window.__skoochStoreSchTaskIsRecurringSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskIsRecurring(cb) {
  try {
    if (!window.__skoochStoreSchTaskIsRecurringSubs) {
      window.__skoochStoreSchTaskIsRecurringSubs = [];
    }
    window.__skoochStoreSchTaskIsRecurringSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskIsRecurringSubs ? window.__skoochStoreSchTaskIsRecurringSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskRecurrenceInterval() {
  try {
    if (((window.__skoochStoreSchTaskRecurrenceInterval !== undefined) && (window.__skoochStoreSchTaskRecurrenceInterval !== null))) {
      return window.__skoochStoreSchTaskRecurrenceInterval;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 1;
}
function setStoreSchTaskRecurrenceInterval(v) {
  try {
    window.__skoochStoreSchTaskRecurrenceInterval = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskRecurrenceIntervalSubs) {
      _subs = window.__skoochStoreSchTaskRecurrenceIntervalSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskRecurrenceInterval(cb) {
  try {
    if (!window.__skoochStoreSchTaskRecurrenceIntervalSubs) {
      window.__skoochStoreSchTaskRecurrenceIntervalSubs = [];
    }
    window.__skoochStoreSchTaskRecurrenceIntervalSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskRecurrenceIntervalSubs ? window.__skoochStoreSchTaskRecurrenceIntervalSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskRecurrenceUnit() {
  try {
    if (((window.__skoochStoreSchTaskRecurrenceUnit !== undefined) && (window.__skoochStoreSchTaskRecurrenceUnit !== null))) {
      return window.__skoochStoreSchTaskRecurrenceUnit;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "week";
}
function setStoreSchTaskRecurrenceUnit(v) {
  try {
    window.__skoochStoreSchTaskRecurrenceUnit = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskRecurrenceUnitSubs) {
      _subs = window.__skoochStoreSchTaskRecurrenceUnitSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskRecurrenceUnit(cb) {
  try {
    if (!window.__skoochStoreSchTaskRecurrenceUnitSubs) {
      window.__skoochStoreSchTaskRecurrenceUnitSubs = [];
    }
    window.__skoochStoreSchTaskRecurrenceUnitSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskRecurrenceUnitSubs ? window.__skoochStoreSchTaskRecurrenceUnitSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchTaskRecurrenceWeekday() {
  try {
    if (((window.__skoochStoreSchTaskRecurrenceWeekday !== undefined) && (window.__skoochStoreSchTaskRecurrenceWeekday !== null))) {
      return window.__skoochStoreSchTaskRecurrenceWeekday;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreSchTaskRecurrenceWeekday(v) {
  try {
    window.__skoochStoreSchTaskRecurrenceWeekday = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchTaskRecurrenceWeekdaySubs) {
      _subs = window.__skoochStoreSchTaskRecurrenceWeekdaySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchTaskRecurrenceWeekday(cb) {
  try {
    if (!window.__skoochStoreSchTaskRecurrenceWeekdaySubs) {
      window.__skoochStoreSchTaskRecurrenceWeekdaySubs = [];
    }
    window.__skoochStoreSchTaskRecurrenceWeekdaySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchTaskRecurrenceWeekdaySubs ? window.__skoochStoreSchTaskRecurrenceWeekdaySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvName() {
  try {
    if (((window.__skoochStoreSchEvName !== undefined) && (window.__skoochStoreSchEvName !== null))) {
      return window.__skoochStoreSchEvName;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEvName(v) {
  try {
    window.__skoochStoreSchEvName = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvNameSubs) {
      _subs = window.__skoochStoreSchEvNameSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvName(cb) {
  try {
    if (!window.__skoochStoreSchEvNameSubs) {
      window.__skoochStoreSchEvNameSubs = [];
    }
    window.__skoochStoreSchEvNameSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvNameSubs ? window.__skoochStoreSchEvNameSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvCat() {
  try {
    if (((window.__skoochStoreSchEvCat !== undefined) && (window.__skoochStoreSchEvCat !== null))) {
      return window.__skoochStoreSchEvCat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEvCat(v) {
  try {
    window.__skoochStoreSchEvCat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvCatSubs) {
      _subs = window.__skoochStoreSchEvCatSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvCat(cb) {
  try {
    if (!window.__skoochStoreSchEvCatSubs) {
      window.__skoochStoreSchEvCatSubs = [];
    }
    window.__skoochStoreSchEvCatSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvCatSubs ? window.__skoochStoreSchEvCatSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvStart() {
  try {
    if (((window.__skoochStoreSchEvStart !== undefined) && (window.__skoochStoreSchEvStart !== null))) {
      return window.__skoochStoreSchEvStart;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "08:00";
}
function setStoreSchEvStart(v) {
  try {
    window.__skoochStoreSchEvStart = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvStartSubs) {
      _subs = window.__skoochStoreSchEvStartSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvStart(cb) {
  try {
    if (!window.__skoochStoreSchEvStartSubs) {
      window.__skoochStoreSchEvStartSubs = [];
    }
    window.__skoochStoreSchEvStartSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvStartSubs ? window.__skoochStoreSchEvStartSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvEnd() {
  try {
    if (((window.__skoochStoreSchEvEnd !== undefined) && (window.__skoochStoreSchEvEnd !== null))) {
      return window.__skoochStoreSchEvEnd;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "09:00";
}
function setStoreSchEvEnd(v) {
  try {
    window.__skoochStoreSchEvEnd = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvEndSubs) {
      _subs = window.__skoochStoreSchEvEndSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvEnd(cb) {
  try {
    if (!window.__skoochStoreSchEvEndSubs) {
      window.__skoochStoreSchEvEndSubs = [];
    }
    window.__skoochStoreSchEvEndSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvEndSubs ? window.__skoochStoreSchEvEndSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvIsRecurring() {
  try {
    if (((window.__skoochStoreSchEvIsRecurring !== undefined) && (window.__skoochStoreSchEvIsRecurring !== null))) {
      return window.__skoochStoreSchEvIsRecurring;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return true;
}
function setStoreSchEvIsRecurring(v) {
  try {
    window.__skoochStoreSchEvIsRecurring = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvIsRecurringSubs) {
      _subs = window.__skoochStoreSchEvIsRecurringSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvIsRecurring(cb) {
  try {
    if (!window.__skoochStoreSchEvIsRecurringSubs) {
      window.__skoochStoreSchEvIsRecurringSubs = [];
    }
    window.__skoochStoreSchEvIsRecurringSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvIsRecurringSubs ? window.__skoochStoreSchEvIsRecurringSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvIsAllDay() {
  try {
    if (((window.__skoochStoreSchEvIsAllDay !== undefined) && (window.__skoochStoreSchEvIsAllDay !== null))) {
      return window.__skoochStoreSchEvIsAllDay;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchEvIsAllDay(v) {
  try {
    window.__skoochStoreSchEvIsAllDay = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvIsAllDaySubs) {
      _subs = window.__skoochStoreSchEvIsAllDaySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvIsAllDay(cb) {
  try {
    if (!window.__skoochStoreSchEvIsAllDaySubs) {
      window.__skoochStoreSchEvIsAllDaySubs = [];
    }
    window.__skoochStoreSchEvIsAllDaySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvIsAllDaySubs ? window.__skoochStoreSchEvIsAllDaySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvRecurrenceInterval() {
  try {
    if (((window.__skoochStoreSchEvRecurrenceInterval !== undefined) && (window.__skoochStoreSchEvRecurrenceInterval !== null))) {
      return window.__skoochStoreSchEvRecurrenceInterval;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 1;
}
function setStoreSchEvRecurrenceInterval(v) {
  try {
    window.__skoochStoreSchEvRecurrenceInterval = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvRecurrenceIntervalSubs) {
      _subs = window.__skoochStoreSchEvRecurrenceIntervalSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvRecurrenceInterval(cb) {
  try {
    if (!window.__skoochStoreSchEvRecurrenceIntervalSubs) {
      window.__skoochStoreSchEvRecurrenceIntervalSubs = [];
    }
    window.__skoochStoreSchEvRecurrenceIntervalSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvRecurrenceIntervalSubs ? window.__skoochStoreSchEvRecurrenceIntervalSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvRecurrenceUnit() {
  try {
    if (((window.__skoochStoreSchEvRecurrenceUnit !== undefined) && (window.__skoochStoreSchEvRecurrenceUnit !== null))) {
      return window.__skoochStoreSchEvRecurrenceUnit;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "week";
}
function setStoreSchEvRecurrenceUnit(v) {
  try {
    window.__skoochStoreSchEvRecurrenceUnit = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvRecurrenceUnitSubs) {
      _subs = window.__skoochStoreSchEvRecurrenceUnitSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvRecurrenceUnit(cb) {
  try {
    if (!window.__skoochStoreSchEvRecurrenceUnitSubs) {
      window.__skoochStoreSchEvRecurrenceUnitSubs = [];
    }
    window.__skoochStoreSchEvRecurrenceUnitSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvRecurrenceUnitSubs ? window.__skoochStoreSchEvRecurrenceUnitSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvDate() {
  try {
    if (((window.__skoochStoreSchEvDate !== undefined) && (window.__skoochStoreSchEvDate !== null))) {
      return window.__skoochStoreSchEvDate;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEvDate(v) {
  try {
    window.__skoochStoreSchEvDate = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvDateSubs) {
      _subs = window.__skoochStoreSchEvDateSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvDate(cb) {
  try {
    if (!window.__skoochStoreSchEvDateSubs) {
      window.__skoochStoreSchEvDateSubs = [];
    }
    window.__skoochStoreSchEvDateSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvDateSubs ? window.__skoochStoreSchEvDateSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvMon() {
  try {
    if (((window.__skoochStoreSchEvMon !== undefined) && (window.__skoochStoreSchEvMon !== null))) {
      return window.__skoochStoreSchEvMon;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchEvMon(v) {
  try {
    window.__skoochStoreSchEvMon = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvMonSubs) {
      _subs = window.__skoochStoreSchEvMonSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvMon(cb) {
  try {
    if (!window.__skoochStoreSchEvMonSubs) {
      window.__skoochStoreSchEvMonSubs = [];
    }
    window.__skoochStoreSchEvMonSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvMonSubs ? window.__skoochStoreSchEvMonSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvTue() {
  try {
    if (((window.__skoochStoreSchEvTue !== undefined) && (window.__skoochStoreSchEvTue !== null))) {
      return window.__skoochStoreSchEvTue;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchEvTue(v) {
  try {
    window.__skoochStoreSchEvTue = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvTueSubs) {
      _subs = window.__skoochStoreSchEvTueSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvTue(cb) {
  try {
    if (!window.__skoochStoreSchEvTueSubs) {
      window.__skoochStoreSchEvTueSubs = [];
    }
    window.__skoochStoreSchEvTueSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvTueSubs ? window.__skoochStoreSchEvTueSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvWed() {
  try {
    if (((window.__skoochStoreSchEvWed !== undefined) && (window.__skoochStoreSchEvWed !== null))) {
      return window.__skoochStoreSchEvWed;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchEvWed(v) {
  try {
    window.__skoochStoreSchEvWed = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvWedSubs) {
      _subs = window.__skoochStoreSchEvWedSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvWed(cb) {
  try {
    if (!window.__skoochStoreSchEvWedSubs) {
      window.__skoochStoreSchEvWedSubs = [];
    }
    window.__skoochStoreSchEvWedSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvWedSubs ? window.__skoochStoreSchEvWedSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvThu() {
  try {
    if (((window.__skoochStoreSchEvThu !== undefined) && (window.__skoochStoreSchEvThu !== null))) {
      return window.__skoochStoreSchEvThu;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchEvThu(v) {
  try {
    window.__skoochStoreSchEvThu = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvThuSubs) {
      _subs = window.__skoochStoreSchEvThuSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvThu(cb) {
  try {
    if (!window.__skoochStoreSchEvThuSubs) {
      window.__skoochStoreSchEvThuSubs = [];
    }
    window.__skoochStoreSchEvThuSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvThuSubs ? window.__skoochStoreSchEvThuSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvFri() {
  try {
    if (((window.__skoochStoreSchEvFri !== undefined) && (window.__skoochStoreSchEvFri !== null))) {
      return window.__skoochStoreSchEvFri;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchEvFri(v) {
  try {
    window.__skoochStoreSchEvFri = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvFriSubs) {
      _subs = window.__skoochStoreSchEvFriSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvFri(cb) {
  try {
    if (!window.__skoochStoreSchEvFriSubs) {
      window.__skoochStoreSchEvFriSubs = [];
    }
    window.__skoochStoreSchEvFriSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvFriSubs ? window.__skoochStoreSchEvFriSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvSat() {
  try {
    if (((window.__skoochStoreSchEvSat !== undefined) && (window.__skoochStoreSchEvSat !== null))) {
      return window.__skoochStoreSchEvSat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchEvSat(v) {
  try {
    window.__skoochStoreSchEvSat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvSatSubs) {
      _subs = window.__skoochStoreSchEvSatSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvSat(cb) {
  try {
    if (!window.__skoochStoreSchEvSatSubs) {
      window.__skoochStoreSchEvSatSubs = [];
    }
    window.__skoochStoreSchEvSatSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvSatSubs ? window.__skoochStoreSchEvSatSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvSun() {
  try {
    if (((window.__skoochStoreSchEvSun !== undefined) && (window.__skoochStoreSchEvSun !== null))) {
      return window.__skoochStoreSchEvSun;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchEvSun(v) {
  try {
    window.__skoochStoreSchEvSun = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvSunSubs) {
      _subs = window.__skoochStoreSchEvSunSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvSun(cb) {
  try {
    if (!window.__skoochStoreSchEvSunSubs) {
      window.__skoochStoreSchEvSunSubs = [];
    }
    window.__skoochStoreSchEvSunSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvSunSubs ? window.__skoochStoreSchEvSunSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConType() {
  try {
    if (((window.__skoochStoreSchConType !== undefined) && (window.__skoochStoreSchConType !== null))) {
      return window.__skoochStoreSchConType;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "block_time";
}
function setStoreSchConType(v) {
  try {
    window.__skoochStoreSchConType = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConTypeSubs) {
      _subs = window.__skoochStoreSchConTypeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConType(cb) {
  try {
    if (!window.__skoochStoreSchConTypeSubs) {
      window.__skoochStoreSchConTypeSubs = [];
    }
    window.__skoochStoreSchConTypeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConTypeSubs ? window.__skoochStoreSchConTypeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConStart() {
  try {
    if (((window.__skoochStoreSchConStart !== undefined) && (window.__skoochStoreSchConStart !== null))) {
      return window.__skoochStoreSchConStart;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "18:00";
}
function setStoreSchConStart(v) {
  try {
    window.__skoochStoreSchConStart = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConStartSubs) {
      _subs = window.__skoochStoreSchConStartSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConStart(cb) {
  try {
    if (!window.__skoochStoreSchConStartSubs) {
      window.__skoochStoreSchConStartSubs = [];
    }
    window.__skoochStoreSchConStartSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConStartSubs ? window.__skoochStoreSchConStartSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConEnd() {
  try {
    if (((window.__skoochStoreSchConEnd !== undefined) && (window.__skoochStoreSchConEnd !== null))) {
      return window.__skoochStoreSchConEnd;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "20:00";
}
function setStoreSchConEnd(v) {
  try {
    window.__skoochStoreSchConEnd = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConEndSubs) {
      _subs = window.__skoochStoreSchConEndSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConEnd(cb) {
  try {
    if (!window.__skoochStoreSchConEndSubs) {
      window.__skoochStoreSchConEndSubs = [];
    }
    window.__skoochStoreSchConEndSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConEndSubs ? window.__skoochStoreSchConEndSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConCat() {
  try {
    if (((window.__skoochStoreSchConCat !== undefined) && (window.__skoochStoreSchConCat !== null))) {
      return window.__skoochStoreSchConCat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "coding";
}
function setStoreSchConCat(v) {
  try {
    window.__skoochStoreSchConCat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConCatSubs) {
      _subs = window.__skoochStoreSchConCatSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConCat(cb) {
  try {
    if (!window.__skoochStoreSchConCatSubs) {
      window.__skoochStoreSchConCatSubs = [];
    }
    window.__skoochStoreSchConCatSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConCatSubs ? window.__skoochStoreSchConCatSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConMax() {
  try {
    if (((window.__skoochStoreSchConMax !== undefined) && (window.__skoochStoreSchConMax !== null))) {
      return window.__skoochStoreSchConMax;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 8;
}
function setStoreSchConMax(v) {
  try {
    window.__skoochStoreSchConMax = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConMaxSubs) {
      _subs = window.__skoochStoreSchConMaxSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConMax(cb) {
  try {
    if (!window.__skoochStoreSchConMaxSubs) {
      window.__skoochStoreSchConMaxSubs = [];
    }
    window.__skoochStoreSchConMaxSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConMaxSubs ? window.__skoochStoreSchConMaxSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConMon() {
  try {
    if (((window.__skoochStoreSchConMon !== undefined) && (window.__skoochStoreSchConMon !== null))) {
      return window.__skoochStoreSchConMon;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchConMon(v) {
  try {
    window.__skoochStoreSchConMon = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConMonSubs) {
      _subs = window.__skoochStoreSchConMonSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConMon(cb) {
  try {
    if (!window.__skoochStoreSchConMonSubs) {
      window.__skoochStoreSchConMonSubs = [];
    }
    window.__skoochStoreSchConMonSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConMonSubs ? window.__skoochStoreSchConMonSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConTue() {
  try {
    if (((window.__skoochStoreSchConTue !== undefined) && (window.__skoochStoreSchConTue !== null))) {
      return window.__skoochStoreSchConTue;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchConTue(v) {
  try {
    window.__skoochStoreSchConTue = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConTueSubs) {
      _subs = window.__skoochStoreSchConTueSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConTue(cb) {
  try {
    if (!window.__skoochStoreSchConTueSubs) {
      window.__skoochStoreSchConTueSubs = [];
    }
    window.__skoochStoreSchConTueSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConTueSubs ? window.__skoochStoreSchConTueSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConWed() {
  try {
    if (((window.__skoochStoreSchConWed !== undefined) && (window.__skoochStoreSchConWed !== null))) {
      return window.__skoochStoreSchConWed;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchConWed(v) {
  try {
    window.__skoochStoreSchConWed = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConWedSubs) {
      _subs = window.__skoochStoreSchConWedSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConWed(cb) {
  try {
    if (!window.__skoochStoreSchConWedSubs) {
      window.__skoochStoreSchConWedSubs = [];
    }
    window.__skoochStoreSchConWedSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConWedSubs ? window.__skoochStoreSchConWedSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConThu() {
  try {
    if (((window.__skoochStoreSchConThu !== undefined) && (window.__skoochStoreSchConThu !== null))) {
      return window.__skoochStoreSchConThu;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchConThu(v) {
  try {
    window.__skoochStoreSchConThu = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConThuSubs) {
      _subs = window.__skoochStoreSchConThuSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConThu(cb) {
  try {
    if (!window.__skoochStoreSchConThuSubs) {
      window.__skoochStoreSchConThuSubs = [];
    }
    window.__skoochStoreSchConThuSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConThuSubs ? window.__skoochStoreSchConThuSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConFri() {
  try {
    if (((window.__skoochStoreSchConFri !== undefined) && (window.__skoochStoreSchConFri !== null))) {
      return window.__skoochStoreSchConFri;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchConFri(v) {
  try {
    window.__skoochStoreSchConFri = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConFriSubs) {
      _subs = window.__skoochStoreSchConFriSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConFri(cb) {
  try {
    if (!window.__skoochStoreSchConFriSubs) {
      window.__skoochStoreSchConFriSubs = [];
    }
    window.__skoochStoreSchConFriSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConFriSubs ? window.__skoochStoreSchConFriSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConSat() {
  try {
    if (((window.__skoochStoreSchConSat !== undefined) && (window.__skoochStoreSchConSat !== null))) {
      return window.__skoochStoreSchConSat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchConSat(v) {
  try {
    window.__skoochStoreSchConSat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConSatSubs) {
      _subs = window.__skoochStoreSchConSatSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConSat(cb) {
  try {
    if (!window.__skoochStoreSchConSatSubs) {
      window.__skoochStoreSchConSatSubs = [];
    }
    window.__skoochStoreSchConSatSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConSatSubs ? window.__skoochStoreSchConSatSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchConSun() {
  try {
    if (((window.__skoochStoreSchConSun !== undefined) && (window.__skoochStoreSchConSun !== null))) {
      return window.__skoochStoreSchConSun;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchConSun(v) {
  try {
    window.__skoochStoreSchConSun = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchConSunSubs) {
      _subs = window.__skoochStoreSchConSunSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchConSun(cb) {
  try {
    if (!window.__skoochStoreSchConSunSubs) {
      window.__skoochStoreSchConSunSubs = [];
    }
    window.__skoochStoreSchConSunSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchConSunSubs ? window.__skoochStoreSchConSunSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditTitle() {
  try {
    if (((window.__skoochStoreSchEditTitle !== undefined) && (window.__skoochStoreSchEditTitle !== null))) {
      return window.__skoochStoreSchEditTitle;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEditTitle(v) {
  try {
    window.__skoochStoreSchEditTitle = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditTitleSubs) {
      _subs = window.__skoochStoreSchEditTitleSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditTitle(cb) {
  try {
    if (!window.__skoochStoreSchEditTitleSubs) {
      window.__skoochStoreSchEditTitleSubs = [];
    }
    window.__skoochStoreSchEditTitleSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditTitleSubs ? window.__skoochStoreSchEditTitleSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditPri() {
  try {
    if (((window.__skoochStoreSchEditPri !== undefined) && (window.__skoochStoreSchEditPri !== null))) {
      return window.__skoochStoreSchEditPri;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 5;
}
function setStoreSchEditPri(v) {
  try {
    window.__skoochStoreSchEditPri = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditPriSubs) {
      _subs = window.__skoochStoreSchEditPriSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditPri(cb) {
  try {
    if (!window.__skoochStoreSchEditPriSubs) {
      window.__skoochStoreSchEditPriSubs = [];
    }
    window.__skoochStoreSchEditPriSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditPriSubs ? window.__skoochStoreSchEditPriSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditDL() {
  try {
    if (((window.__skoochStoreSchEditDL !== undefined) && (window.__skoochStoreSchEditDL !== null))) {
      return window.__skoochStoreSchEditDL;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEditDL(v) {
  try {
    window.__skoochStoreSchEditDL = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditDLSubs) {
      _subs = window.__skoochStoreSchEditDLSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditDL(cb) {
  try {
    if (!window.__skoochStoreSchEditDLSubs) {
      window.__skoochStoreSchEditDLSubs = [];
    }
    window.__skoochStoreSchEditDLSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditDLSubs ? window.__skoochStoreSchEditDLSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditDur() {
  try {
    if (((window.__skoochStoreSchEditDur !== undefined) && (window.__skoochStoreSchEditDur !== null))) {
      return window.__skoochStoreSchEditDur;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 60;
}
function setStoreSchEditDur(v) {
  try {
    window.__skoochStoreSchEditDur = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditDurSubs) {
      _subs = window.__skoochStoreSchEditDurSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditDur(cb) {
  try {
    if (!window.__skoochStoreSchEditDurSubs) {
      window.__skoochStoreSchEditDurSubs = [];
    }
    window.__skoochStoreSchEditDurSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditDurSubs ? window.__skoochStoreSchEditDurSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchPopupDirty() {
  try {
    if (((window.__skoochStoreSchPopupDirty !== undefined) && (window.__skoochStoreSchPopupDirty !== null))) {
      return window.__skoochStoreSchPopupDirty;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchPopupDirty(v) {
  try {
    window.__skoochStoreSchPopupDirty = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchPopupDirtySubs) {
      _subs = window.__skoochStoreSchPopupDirtySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchPopupDirty(cb) {
  try {
    if (!window.__skoochStoreSchPopupDirtySubs) {
      window.__skoochStoreSchPopupDirtySubs = [];
    }
    window.__skoochStoreSchPopupDirtySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchPopupDirtySubs ? window.__skoochStoreSchPopupDirtySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchPopupSaveState() {
  try {
    if (((window.__skoochStoreSchPopupSaveState !== undefined) && (window.__skoochStoreSchPopupSaveState !== null))) {
      return window.__skoochStoreSchPopupSaveState;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "idle";
}
function setStoreSchPopupSaveState(v) {
  try {
    window.__skoochStoreSchPopupSaveState = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchPopupSaveStateSubs) {
      _subs = window.__skoochStoreSchPopupSaveStateSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchPopupSaveState(cb) {
  try {
    if (!window.__skoochStoreSchPopupSaveStateSubs) {
      window.__skoochStoreSchPopupSaveStateSubs = [];
    }
    window.__skoochStoreSchPopupSaveStateSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchPopupSaveStateSubs ? window.__skoochStoreSchPopupSaveStateSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditCat() {
  try {
    if (((window.__skoochStoreSchEditCat !== undefined) && (window.__skoochStoreSchEditCat !== null))) {
      return window.__skoochStoreSchEditCat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEditCat(v) {
  try {
    window.__skoochStoreSchEditCat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditCatSubs) {
      _subs = window.__skoochStoreSchEditCatSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditCat(cb) {
  try {
    if (!window.__skoochStoreSchEditCatSubs) {
      window.__skoochStoreSchEditCatSubs = [];
    }
    window.__skoochStoreSchEditCatSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditCatSubs ? window.__skoochStoreSchEditCatSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditLoc() {
  try {
    if (((window.__skoochStoreSchEditLoc !== undefined) && (window.__skoochStoreSchEditLoc !== null))) {
      return window.__skoochStoreSchEditLoc;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEditLoc(v) {
  try {
    window.__skoochStoreSchEditLoc = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditLocSubs) {
      _subs = window.__skoochStoreSchEditLocSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditLoc(cb) {
  try {
    if (!window.__skoochStoreSchEditLocSubs) {
      window.__skoochStoreSchEditLocSubs = [];
    }
    window.__skoochStoreSchEditLocSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditLocSubs ? window.__skoochStoreSchEditLocSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditStart() {
  try {
    if (((window.__skoochStoreSchEditStart !== undefined) && (window.__skoochStoreSchEditStart !== null))) {
      return window.__skoochStoreSchEditStart;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEditStart(v) {
  try {
    window.__skoochStoreSchEditStart = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditStartSubs) {
      _subs = window.__skoochStoreSchEditStartSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditStart(cb) {
  try {
    if (!window.__skoochStoreSchEditStartSubs) {
      window.__skoochStoreSchEditStartSubs = [];
    }
    window.__skoochStoreSchEditStartSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditStartSubs ? window.__skoochStoreSchEditStartSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditEnd() {
  try {
    if (((window.__skoochStoreSchEditEnd !== undefined) && (window.__skoochStoreSchEditEnd !== null))) {
      return window.__skoochStoreSchEditEnd;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEditEnd(v) {
  try {
    window.__skoochStoreSchEditEnd = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditEndSubs) {
      _subs = window.__skoochStoreSchEditEndSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditEnd(cb) {
  try {
    if (!window.__skoochStoreSchEditEndSubs) {
      window.__skoochStoreSchEditEndSubs = [];
    }
    window.__skoochStoreSchEditEndSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditEndSubs ? window.__skoochStoreSchEditEndSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditDay() {
  try {
    if (((window.__skoochStoreSchEditDay !== undefined) && (window.__skoochStoreSchEditDay !== null))) {
      return window.__skoochStoreSchEditDay;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreSchEditDay(v) {
  try {
    window.__skoochStoreSchEditDay = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditDaySubs) {
      _subs = window.__skoochStoreSchEditDaySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditDay(cb) {
  try {
    if (!window.__skoochStoreSchEditDaySubs) {
      window.__skoochStoreSchEditDaySubs = [];
    }
    window.__skoochStoreSchEditDaySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditDaySubs ? window.__skoochStoreSchEditDaySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditDays() {
  try {
    if (window.__skoochStoreSchEditDays) {
      return window.__skoochStoreSchEditDays;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreSchEditDays(v) {
  try {
    window.__skoochStoreSchEditDays = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditDaysSubs) {
      _subs = window.__skoochStoreSchEditDaysSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditDays(cb) {
  try {
    if (!window.__skoochStoreSchEditDaysSubs) {
      window.__skoochStoreSchEditDaysSubs = [];
    }
    window.__skoochStoreSchEditDaysSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditDaysSubs ? window.__skoochStoreSchEditDaysSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEditDate() {
  try {
    if (((window.__skoochStoreSchEditDate !== undefined) && (window.__skoochStoreSchEditDate !== null))) {
      return window.__skoochStoreSchEditDate;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEditDate(v) {
  try {
    window.__skoochStoreSchEditDate = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEditDateSubs) {
      _subs = window.__skoochStoreSchEditDateSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEditDate(cb) {
  try {
    if (!window.__skoochStoreSchEditDateSubs) {
      window.__skoochStoreSchEditDateSubs = [];
    }
    window.__skoochStoreSchEditDateSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEditDateSubs ? window.__skoochStoreSchEditDateSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchEvLoc() {
  try {
    if (((window.__skoochStoreSchEvLoc !== undefined) && (window.__skoochStoreSchEvLoc !== null))) {
      return window.__skoochStoreSchEvLoc;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchEvLoc(v) {
  try {
    window.__skoochStoreSchEvLoc = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchEvLocSubs) {
      _subs = window.__skoochStoreSchEvLocSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchEvLoc(cb) {
  try {
    if (!window.__skoochStoreSchEvLocSubs) {
      window.__skoochStoreSchEvLocSubs = [];
    }
    window.__skoochStoreSchEvLocSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchEvLocSubs ? window.__skoochStoreSchEvLocSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleType() {
  try {
    if (((window.__skoochStoreNewRuleType !== undefined) && (window.__skoochStoreNewRuleType !== null))) {
      return window.__skoochStoreNewRuleType;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "no_schedule";
}
function setStoreNewRuleType(v) {
  try {
    window.__skoochStoreNewRuleType = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleTypeSubs) {
      _subs = window.__skoochStoreNewRuleTypeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleType(cb) {
  try {
    if (!window.__skoochStoreNewRuleTypeSubs) {
      window.__skoochStoreNewRuleTypeSubs = [];
    }
    window.__skoochStoreNewRuleTypeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleTypeSubs ? window.__skoochStoreNewRuleTypeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleStart() {
  try {
    if (((window.__skoochStoreNewRuleStart !== undefined) && (window.__skoochStoreNewRuleStart !== null))) {
      return window.__skoochStoreNewRuleStart;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "20:00";
}
function setStoreNewRuleStart(v) {
  try {
    window.__skoochStoreNewRuleStart = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleStartSubs) {
      _subs = window.__skoochStoreNewRuleStartSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleStart(cb) {
  try {
    if (!window.__skoochStoreNewRuleStartSubs) {
      window.__skoochStoreNewRuleStartSubs = [];
    }
    window.__skoochStoreNewRuleStartSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleStartSubs ? window.__skoochStoreNewRuleStartSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleEnd() {
  try {
    if (((window.__skoochStoreNewRuleEnd !== undefined) && (window.__skoochStoreNewRuleEnd !== null))) {
      return window.__skoochStoreNewRuleEnd;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "22:00";
}
function setStoreNewRuleEnd(v) {
  try {
    window.__skoochStoreNewRuleEnd = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleEndSubs) {
      _subs = window.__skoochStoreNewRuleEndSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleEnd(cb) {
  try {
    if (!window.__skoochStoreNewRuleEndSubs) {
      window.__skoochStoreNewRuleEndSubs = [];
    }
    window.__skoochStoreNewRuleEndSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleEndSubs ? window.__skoochStoreNewRuleEndSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleMon() {
  try {
    if (((window.__skoochStoreNewRuleMon !== undefined) && (window.__skoochStoreNewRuleMon !== null))) {
      return window.__skoochStoreNewRuleMon;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreNewRuleMon(v) {
  try {
    window.__skoochStoreNewRuleMon = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleMonSubs) {
      _subs = window.__skoochStoreNewRuleMonSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleMon(cb) {
  try {
    if (!window.__skoochStoreNewRuleMonSubs) {
      window.__skoochStoreNewRuleMonSubs = [];
    }
    window.__skoochStoreNewRuleMonSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleMonSubs ? window.__skoochStoreNewRuleMonSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleTue() {
  try {
    if (((window.__skoochStoreNewRuleTue !== undefined) && (window.__skoochStoreNewRuleTue !== null))) {
      return window.__skoochStoreNewRuleTue;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreNewRuleTue(v) {
  try {
    window.__skoochStoreNewRuleTue = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleTueSubs) {
      _subs = window.__skoochStoreNewRuleTueSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleTue(cb) {
  try {
    if (!window.__skoochStoreNewRuleTueSubs) {
      window.__skoochStoreNewRuleTueSubs = [];
    }
    window.__skoochStoreNewRuleTueSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleTueSubs ? window.__skoochStoreNewRuleTueSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleWed() {
  try {
    if (((window.__skoochStoreNewRuleWed !== undefined) && (window.__skoochStoreNewRuleWed !== null))) {
      return window.__skoochStoreNewRuleWed;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreNewRuleWed(v) {
  try {
    window.__skoochStoreNewRuleWed = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleWedSubs) {
      _subs = window.__skoochStoreNewRuleWedSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleWed(cb) {
  try {
    if (!window.__skoochStoreNewRuleWedSubs) {
      window.__skoochStoreNewRuleWedSubs = [];
    }
    window.__skoochStoreNewRuleWedSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleWedSubs ? window.__skoochStoreNewRuleWedSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleThu() {
  try {
    if (((window.__skoochStoreNewRuleThu !== undefined) && (window.__skoochStoreNewRuleThu !== null))) {
      return window.__skoochStoreNewRuleThu;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreNewRuleThu(v) {
  try {
    window.__skoochStoreNewRuleThu = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleThuSubs) {
      _subs = window.__skoochStoreNewRuleThuSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleThu(cb) {
  try {
    if (!window.__skoochStoreNewRuleThuSubs) {
      window.__skoochStoreNewRuleThuSubs = [];
    }
    window.__skoochStoreNewRuleThuSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleThuSubs ? window.__skoochStoreNewRuleThuSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleFri() {
  try {
    if (((window.__skoochStoreNewRuleFri !== undefined) && (window.__skoochStoreNewRuleFri !== null))) {
      return window.__skoochStoreNewRuleFri;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreNewRuleFri(v) {
  try {
    window.__skoochStoreNewRuleFri = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleFriSubs) {
      _subs = window.__skoochStoreNewRuleFriSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleFri(cb) {
  try {
    if (!window.__skoochStoreNewRuleFriSubs) {
      window.__skoochStoreNewRuleFriSubs = [];
    }
    window.__skoochStoreNewRuleFriSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleFriSubs ? window.__skoochStoreNewRuleFriSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleSat() {
  try {
    if (((window.__skoochStoreNewRuleSat !== undefined) && (window.__skoochStoreNewRuleSat !== null))) {
      return window.__skoochStoreNewRuleSat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreNewRuleSat(v) {
  try {
    window.__skoochStoreNewRuleSat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleSatSubs) {
      _subs = window.__skoochStoreNewRuleSatSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleSat(cb) {
  try {
    if (!window.__skoochStoreNewRuleSatSubs) {
      window.__skoochStoreNewRuleSatSubs = [];
    }
    window.__skoochStoreNewRuleSatSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleSatSubs ? window.__skoochStoreNewRuleSatSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleSun() {
  try {
    if (((window.__skoochStoreNewRuleSun !== undefined) && (window.__skoochStoreNewRuleSun !== null))) {
      return window.__skoochStoreNewRuleSun;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreNewRuleSun(v) {
  try {
    window.__skoochStoreNewRuleSun = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleSunSubs) {
      _subs = window.__skoochStoreNewRuleSunSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleSun(cb) {
  try {
    if (!window.__skoochStoreNewRuleSunSubs) {
      window.__skoochStoreNewRuleSunSubs = [];
    }
    window.__skoochStoreNewRuleSunSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleSunSubs ? window.__skoochStoreNewRuleSunSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleCategory() {
  try {
    if (((window.__skoochStoreNewRuleCategory !== undefined) && (window.__skoochStoreNewRuleCategory !== null))) {
      return window.__skoochStoreNewRuleCategory;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "coding";
}
function setStoreNewRuleCategory(v) {
  try {
    window.__skoochStoreNewRuleCategory = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleCategorySubs) {
      _subs = window.__skoochStoreNewRuleCategorySubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleCategory(cb) {
  try {
    if (!window.__skoochStoreNewRuleCategorySubs) {
      window.__skoochStoreNewRuleCategorySubs = [];
    }
    window.__skoochStoreNewRuleCategorySubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleCategorySubs ? window.__skoochStoreNewRuleCategorySubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNewRuleMaxHours() {
  try {
    if (((window.__skoochStoreNewRuleMaxHours !== undefined) && (window.__skoochStoreNewRuleMaxHours !== null))) {
      return window.__skoochStoreNewRuleMaxHours;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 2;
}
function setStoreNewRuleMaxHours(v) {
  try {
    window.__skoochStoreNewRuleMaxHours = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNewRuleMaxHoursSubs) {
      _subs = window.__skoochStoreNewRuleMaxHoursSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNewRuleMaxHours(cb) {
  try {
    if (!window.__skoochStoreNewRuleMaxHoursSubs) {
      window.__skoochStoreNewRuleMaxHoursSubs = [];
    }
    window.__skoochStoreNewRuleMaxHoursSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNewRuleMaxHoursSubs ? window.__skoochStoreNewRuleMaxHoursSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDraggedSkillCat() {
  try {
    if (((window.__skoochStoreDraggedSkillCat !== undefined) && (window.__skoochStoreDraggedSkillCat !== null))) {
      return window.__skoochStoreDraggedSkillCat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreDraggedSkillCat(v) {
  try {
    window.__skoochStoreDraggedSkillCat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDraggedSkillCatSubs) {
      _subs = window.__skoochStoreDraggedSkillCatSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDraggedSkillCat(cb) {
  try {
    if (!window.__skoochStoreDraggedSkillCatSubs) {
      window.__skoochStoreDraggedSkillCatSubs = [];
    }
    window.__skoochStoreDraggedSkillCatSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDraggedSkillCatSubs ? window.__skoochStoreDraggedSkillCatSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDropTargetCat() {
  try {
    if (((window.__skoochStoreDropTargetCat !== undefined) && (window.__skoochStoreDropTargetCat !== null))) {
      return window.__skoochStoreDropTargetCat;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreDropTargetCat(v) {
  try {
    window.__skoochStoreDropTargetCat = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDropTargetCatSubs) {
      _subs = window.__skoochStoreDropTargetCatSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDropTargetCat(cb) {
  try {
    if (!window.__skoochStoreDropTargetCatSubs) {
      window.__skoochStoreDropTargetCatSubs = [];
    }
    window.__skoochStoreDropTargetCatSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDropTargetCatSubs ? window.__skoochStoreDropTargetCatSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDropTargetTier() {
  try {
    if (((window.__skoochStoreDropTargetTier !== undefined) && (window.__skoochStoreDropTargetTier !== null))) {
      return window.__skoochStoreDropTargetTier;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreDropTargetTier(v) {
  try {
    window.__skoochStoreDropTargetTier = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDropTargetTierSubs) {
      _subs = window.__skoochStoreDropTargetTierSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDropTargetTier(cb) {
  try {
    if (!window.__skoochStoreDropTargetTierSubs) {
      window.__skoochStoreDropTargetTierSubs = [];
    }
    window.__skoochStoreDropTargetTierSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDropTargetTierSubs ? window.__skoochStoreDropTargetTierSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreProfileSaving() {
  try {
    if (((window.__skoochStoreProfileSaving !== undefined) && (window.__skoochStoreProfileSaving !== null))) {
      return window.__skoochStoreProfileSaving;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreProfileSaving(v) {
  try {
    window.__skoochStoreProfileSaving = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreProfileSavingSubs) {
      _subs = window.__skoochStoreProfileSavingSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreProfileSaving(cb) {
  try {
    if (!window.__skoochStoreProfileSavingSubs) {
      window.__skoochStoreProfileSavingSubs = [];
    }
    window.__skoochStoreProfileSavingSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreProfileSavingSubs ? window.__skoochStoreProfileSavingSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreProfileSaveError() {
  try {
    if (((window.__skoochStoreProfileSaveError !== undefined) && (window.__skoochStoreProfileSaveError !== null))) {
      return window.__skoochStoreProfileSaveError;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreProfileSaveError(v) {
  try {
    window.__skoochStoreProfileSaveError = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreProfileSaveErrorSubs) {
      _subs = window.__skoochStoreProfileSaveErrorSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreProfileSaveError(cb) {
  try {
    if (!window.__skoochStoreProfileSaveErrorSubs) {
      window.__skoochStoreProfileSaveErrorSubs = [];
    }
    window.__skoochStoreProfileSaveErrorSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreProfileSaveErrorSubs ? window.__skoochStoreProfileSaveErrorSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreProfileSaveSuccess() {
  try {
    if (((window.__skoochStoreProfileSaveSuccess !== undefined) && (window.__skoochStoreProfileSaveSuccess !== null))) {
      return window.__skoochStoreProfileSaveSuccess;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreProfileSaveSuccess(v) {
  try {
    window.__skoochStoreProfileSaveSuccess = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreProfileSaveSuccessSubs) {
      _subs = window.__skoochStoreProfileSaveSuccessSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreProfileSaveSuccess(cb) {
  try {
    if (!window.__skoochStoreProfileSaveSuccessSubs) {
      window.__skoochStoreProfileSaveSuccessSubs = [];
    }
    window.__skoochStoreProfileSaveSuccessSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreProfileSaveSuccessSubs ? window.__skoochStoreProfileSaveSuccessSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreAuthActionInProgress() {
  try {
    if (((window.__skoochStoreAuthActionInProgress !== undefined) && (window.__skoochStoreAuthActionInProgress !== null))) {
      return window.__skoochStoreAuthActionInProgress;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreAuthActionInProgress(v) {
  try {
    window.__skoochStoreAuthActionInProgress = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreAuthActionInProgressSubs) {
      _subs = window.__skoochStoreAuthActionInProgressSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreAuthActionInProgress(cb) {
  try {
    if (!window.__skoochStoreAuthActionInProgressSubs) {
      window.__skoochStoreAuthActionInProgressSubs = [];
    }
    window.__skoochStoreAuthActionInProgressSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreAuthActionInProgressSubs ? window.__skoochStoreAuthActionInProgressSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchReportOpen() {
  try {
    if (((window.__skoochStoreSchReportOpen !== undefined) && (window.__skoochStoreSchReportOpen !== null))) {
      return window.__skoochStoreSchReportOpen;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreSchReportOpen(v) {
  try {
    window.__skoochStoreSchReportOpen = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchReportOpenSubs) {
      _subs = window.__skoochStoreSchReportOpenSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchReportOpen(cb) {
  try {
    if (!window.__skoochStoreSchReportOpenSubs) {
      window.__skoochStoreSchReportOpenSubs = [];
    }
    window.__skoochStoreSchReportOpenSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchReportOpenSubs ? window.__skoochStoreSchReportOpenSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDevMode() {
  try {
    if (((window.__skoochStoreDevMode !== undefined) && (window.__skoochStoreDevMode !== null))) {
      return window.__skoochStoreDevMode;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreDevMode(v) {
  try {
    window.__skoochStoreDevMode = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDevModeSubs) {
      _subs = window.__skoochStoreDevModeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDevMode(cb) {
  try {
    if (!window.__skoochStoreDevModeSubs) {
      window.__skoochStoreDevModeSubs = [];
    }
    window.__skoochStoreDevModeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDevModeSubs ? window.__skoochStoreDevModeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchSidebarMode() {
  try {
    if (((window.__skoochStoreSchSidebarMode !== undefined) && (window.__skoochStoreSchSidebarMode !== null))) {
      return window.__skoochStoreSchSidebarMode;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "parking";
}
function setStoreSchSidebarMode(v) {
  try {
    window.__skoochStoreSchSidebarMode = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchSidebarModeSubs) {
      _subs = window.__skoochStoreSchSidebarModeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchSidebarMode(cb) {
  try {
    if (!window.__skoochStoreSchSidebarModeSubs) {
      window.__skoochStoreSchSidebarModeSubs = [];
    }
    window.__skoochStoreSchSidebarModeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchSidebarModeSubs ? window.__skoochStoreSchSidebarModeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSchFormError() {
  try {
    if (((window.__skoochStoreSchFormError !== undefined) && (window.__skoochStoreSchFormError !== null))) {
      return window.__skoochStoreSchFormError;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSchFormError(v) {
  try {
    window.__skoochStoreSchFormError = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSchFormErrorSubs) {
      _subs = window.__skoochStoreSchFormErrorSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSchFormError(cb) {
  try {
    if (!window.__skoochStoreSchFormErrorSubs) {
      window.__skoochStoreSchFormErrorSubs = [];
    }
    window.__skoochStoreSchFormErrorSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSchFormErrorSubs ? window.__skoochStoreSchFormErrorSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreLegalReturnTo() {
  try {
    if (((window.__skoochStoreLegalReturnTo !== undefined) && (window.__skoochStoreLegalReturnTo !== null))) {
      return window.__skoochStoreLegalReturnTo;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "landing";
}
function setStoreLegalReturnTo(v) {
  try {
    window.__skoochStoreLegalReturnTo = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreLegalReturnToSubs) {
      _subs = window.__skoochStoreLegalReturnToSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreLegalReturnTo(cb) {
  try {
    if (!window.__skoochStoreLegalReturnToSubs) {
      window.__skoochStoreLegalReturnToSubs = [];
    }
    window.__skoochStoreLegalReturnToSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreLegalReturnToSubs ? window.__skoochStoreLegalReturnToSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreLandingPage() {
  try {
    if (((window.__skoochStoreLandingPage !== undefined) && (window.__skoochStoreLandingPage !== null))) {
      return window.__skoochStoreLandingPage;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "home";
}
function setStoreLandingPage(v) {
  try {
    window.__skoochStoreLandingPage = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreLandingPageSubs) {
      _subs = window.__skoochStoreLandingPageSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreLandingPage(cb) {
  try {
    if (!window.__skoochStoreLandingPageSubs) {
      window.__skoochStoreLandingPageSubs = [];
    }
    window.__skoochStoreLandingPageSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreLandingPageSubs ? window.__skoochStoreLandingPageSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreGcalStatus() {
  try {
    if (((window.__skoochStoreGcalStatus !== undefined) && (window.__skoochStoreGcalStatus !== null))) {
      return window.__skoochStoreGcalStatus;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return {"connected": false, "sync_enabled": false, "last_sync_at": "", "last_sync_status": ""};
}
function setStoreGcalStatus(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : {"connected": false, "sync_enabled": false, "last_sync_at": "", "last_sync_status": ""});
  try {
    window.__skoochStoreGcalStatus = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreGcalStatusSubs) {
      _subs = window.__skoochStoreGcalStatusSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreGcalStatus(cb) {
  try {
    if (!window.__skoochStoreGcalStatusSubs) {
      window.__skoochStoreGcalStatusSubs = [];
    }
    window.__skoochStoreGcalStatusSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreGcalStatusSubs ? window.__skoochStoreGcalStatusSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreGcalSyncing() {
  try {
    if (((window.__skoochStoreGcalSyncing !== undefined) && (window.__skoochStoreGcalSyncing !== null))) {
      return window.__skoochStoreGcalSyncing;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreGcalSyncing(v) {
  let _val = (v ? true : false);
  try {
    window.__skoochStoreGcalSyncing = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreGcalSyncingSubs) {
      _subs = window.__skoochStoreGcalSyncingSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreGcalSyncing(cb) {
  try {
    if (!window.__skoochStoreGcalSyncingSubs) {
      window.__skoochStoreGcalSyncingSubs = [];
    }
    window.__skoochStoreGcalSyncingSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreGcalSyncingSubs ? window.__skoochStoreGcalSyncingSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreSaveErrorNotice() {
  try {
    if (((window.__skoochStoreSaveErrorNotice !== undefined) && (window.__skoochStoreSaveErrorNotice !== null))) {
      return window.__skoochStoreSaveErrorNotice;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreSaveErrorNotice(v) {
  try {
    window.__skoochStoreSaveErrorNotice = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreSaveErrorNoticeSubs) {
      _subs = window.__skoochStoreSaveErrorNoticeSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreSaveErrorNotice(cb) {
  try {
    if (!window.__skoochStoreSaveErrorNoticeSubs) {
      window.__skoochStoreSaveErrorNoticeSubs = [];
    }
    window.__skoochStoreSaveErrorNoticeSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreSaveErrorNoticeSubs ? window.__skoochStoreSaveErrorNoticeSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreGcalClock() {
  try {
    if (((window.__skoochStoreGcalClock !== undefined) && (window.__skoochStoreGcalClock !== null))) {
      return window.__skoochStoreGcalClock;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreGcalClock(v) {
  let _val = (v ? v : 0);
  try {
    window.__skoochStoreGcalClock = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreGcalClockSubs) {
      _subs = window.__skoochStoreGcalClockSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreGcalClock(cb) {
  try {
    if (!window.__skoochStoreGcalClockSubs) {
      window.__skoochStoreGcalClockSubs = [];
    }
    window.__skoochStoreGcalClockSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreGcalClockSubs ? window.__skoochStoreGcalClockSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreGcalCalendars() {
  try {
    if (window.__skoochStoreGcalCalendars) {
      return window.__skoochStoreGcalCalendars;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreGcalCalendars(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : []);
  try {
    window.__skoochStoreGcalCalendars = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreGcalCalendarsSubs) {
      _subs = window.__skoochStoreGcalCalendarsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreGcalCalendars(cb) {
  try {
    if (!window.__skoochStoreGcalCalendarsSubs) {
      window.__skoochStoreGcalCalendarsSubs = [];
    }
    window.__skoochStoreGcalCalendarsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreGcalCalendarsSubs ? window.__skoochStoreGcalCalendarsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreGcalReadSelection() {
  try {
    if (window.__skoochStoreGcalReadSelection) {
      return window.__skoochStoreGcalReadSelection;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return [];
}
function setStoreGcalReadSelection(v) {
  let _val = (((v !== null) && (v !== undefined)) ? v : []);
  try {
    window.__skoochStoreGcalReadSelection = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreGcalReadSelectionSubs) {
      _subs = window.__skoochStoreGcalReadSelectionSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreGcalReadSelection(cb) {
  try {
    if (!window.__skoochStoreGcalReadSelectionSubs) {
      window.__skoochStoreGcalReadSelectionSubs = [];
    }
    window.__skoochStoreGcalReadSelectionSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreGcalReadSelectionSubs ? window.__skoochStoreGcalReadSelectionSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreGcalWriteSelection() {
  try {
    if (((window.__skoochStoreGcalWriteSelection !== undefined) && (window.__skoochStoreGcalWriteSelection !== null))) {
      return window.__skoochStoreGcalWriteSelection;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreGcalWriteSelection(v) {
  let _val = String((v || ""));
  try {
    window.__skoochStoreGcalWriteSelection = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreGcalWriteSelectionSubs) {
      _subs = window.__skoochStoreGcalWriteSelectionSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreGcalWriteSelection(cb) {
  try {
    if (!window.__skoochStoreGcalWriteSelectionSubs) {
      window.__skoochStoreGcalWriteSelectionSubs = [];
    }
    window.__skoochStoreGcalWriteSelectionSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreGcalWriteSelectionSubs ? window.__skoochStoreGcalWriteSelectionSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreGcalCalLoading() {
  try {
    if (((window.__skoochStoreGcalCalLoading !== undefined) && (window.__skoochStoreGcalCalLoading !== null))) {
      return window.__skoochStoreGcalCalLoading;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreGcalCalLoading(v) {
  let _val = (v ? true : false);
  try {
    window.__skoochStoreGcalCalLoading = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreGcalCalLoadingSubs) {
      _subs = window.__skoochStoreGcalCalLoadingSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreGcalCalLoading(cb) {
  try {
    if (!window.__skoochStoreGcalCalLoadingSubs) {
      window.__skoochStoreGcalCalLoadingSubs = [];
    }
    window.__skoochStoreGcalCalLoadingSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreGcalCalLoadingSubs ? window.__skoochStoreGcalCalLoadingSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreGcalCalSaveState() {
  try {
    if (((window.__skoochStoreGcalCalSaveState !== undefined) && (window.__skoochStoreGcalCalSaveState !== null))) {
      return window.__skoochStoreGcalCalSaveState;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreGcalCalSaveState(v) {
  let _val = String((v || ""));
  try {
    window.__skoochStoreGcalCalSaveState = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreGcalCalSaveStateSubs) {
      _subs = window.__skoochStoreGcalCalSaveStateSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreGcalCalSaveState(cb) {
  try {
    if (!window.__skoochStoreGcalCalSaveStateSubs) {
      window.__skoochStoreGcalCalSaveStateSubs = [];
    }
    window.__skoochStoreGcalCalSaveStateSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreGcalCalSaveStateSubs ? window.__skoochStoreGcalCalSaveStateSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIsMobileViewport() {
  try {
    if (((window.__skoochStoreIsMobileViewport !== undefined) && (window.__skoochStoreIsMobileViewport !== null))) {
      return window.__skoochStoreIsMobileViewport;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    return window.matchMedia("(max-width: 767px)").matches;
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreIsMobileViewport(v) {
  let _val = (v ? true : false);
  try {
    window.__skoochStoreIsMobileViewport = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIsMobileViewportSubs) {
      _subs = window.__skoochStoreIsMobileViewportSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIsMobileViewport(cb) {
  try {
    if (!window.__skoochStoreIsMobileViewportSubs) {
      window.__skoochStoreIsMobileViewportSubs = [];
    }
    window.__skoochStoreIsMobileViewportSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIsMobileViewportSubs ? window.__skoochStoreIsMobileViewportSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreMobileSchedView() {
  try {
    if (window.__skoochStoreMobileSchedView) {
      return window.__skoochStoreMobileSchedView;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "day";
}
function setStoreMobileSchedView(v) {
  let _val = (((v === "week") || (v === "month")) ? v : "day");
  try {
    window.__skoochStoreMobileSchedView = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreMobileSchedViewSubs) {
      _subs = window.__skoochStoreMobileSchedViewSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreMobileSchedView(cb) {
  try {
    if (!window.__skoochStoreMobileSchedViewSubs) {
      window.__skoochStoreMobileSchedViewSubs = [];
    }
    window.__skoochStoreMobileSchedViewSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreMobileSchedViewSubs ? window.__skoochStoreMobileSchedViewSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreMobileMonthOffset() {
  try {
    if (((window.__skoochStoreMobileMonthOffset !== undefined) && (window.__skoochStoreMobileMonthOffset !== null))) {
      return window.__skoochStoreMobileMonthOffset;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return 0;
}
function setStoreMobileMonthOffset(v) {
  let _val = (((v !== undefined) && (v !== null)) ? v : 0);
  try {
    window.__skoochStoreMobileMonthOffset = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreMobileMonthOffsetSubs) {
      _subs = window.__skoochStoreMobileMonthOffsetSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreMobileMonthOffset(cb) {
  try {
    if (!window.__skoochStoreMobileMonthOffsetSubs) {
      window.__skoochStoreMobileMonthOffsetSubs = [];
    }
    window.__skoochStoreMobileMonthOffsetSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreMobileMonthOffsetSubs ? window.__skoochStoreMobileMonthOffsetSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreMobileMonthDots() {
  try {
    if (window.__skoochStoreMobileMonthDots) {
      return window.__skoochStoreMobileMonthDots;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return {};
}
function setStoreMobileMonthDots(v) {
  let _val = (((v !== undefined) && (v !== null)) ? v : {});
  try {
    window.__skoochStoreMobileMonthDots = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreMobileMonthDotsSubs) {
      _subs = window.__skoochStoreMobileMonthDotsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreMobileMonthDots(cb) {
  try {
    if (!window.__skoochStoreMobileMonthDotsSubs) {
      window.__skoochStoreMobileMonthDotsSubs = [];
    }
    window.__skoochStoreMobileMonthDotsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreMobileMonthDotsSubs ? window.__skoochStoreMobileMonthDotsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreMobileSandboxToast() {
  try {
    if (window.__skoochStoreMobileSandboxToast) {
      return window.__skoochStoreMobileSandboxToast;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return "";
}
function setStoreMobileSandboxToast(v) {
  let _val = (v ? v : "");
  try {
    window.__skoochStoreMobileSandboxToast = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreMobileSandboxToastSubs) {
      _subs = window.__skoochStoreMobileSandboxToastSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreMobileSandboxToast(cb) {
  try {
    if (!window.__skoochStoreMobileSandboxToastSubs) {
      window.__skoochStoreMobileSandboxToastSubs = [];
    }
    window.__skoochStoreMobileSandboxToastSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreMobileSandboxToastSubs ? window.__skoochStoreMobileSandboxToastSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreDemoTourActive() {
  try {
    if (((window.__skoochStoreDemoTourActive !== undefined) && (window.__skoochStoreDemoTourActive !== null))) {
      return window.__skoochStoreDemoTourActive;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return false;
}
function setStoreDemoTourActive(v) {
  let _val = (v ? true : false);
  try {
    window.__skoochStoreDemoTourActive = _val;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreDemoTourActiveSubs) {
      _subs = window.__skoochStoreDemoTourActiveSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(_val);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreDemoTourActive(cb) {
  try {
    if (!window.__skoochStoreDemoTourActiveSubs) {
      window.__skoochStoreDemoTourActiveSubs = [];
    }
    window.__skoochStoreDemoTourActiveSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreDemoTourActiveSubs ? window.__skoochStoreDemoTourActiveSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreNotifPrefs() {
  try {
    if (((window.__skoochStoreNotifPrefs !== undefined) && (window.__skoochStoreNotifPrefs !== null))) {
      return window.__skoochStoreNotifPrefs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    let _raw = localStorage.getItem("skooch_notif_prefs");
    if (_raw) {
      let _parsed = JSON.parse(_raw);
      window.__skoochStoreNotifPrefs = _parsed;
      return _parsed;
    }
  } catch (__jac_e) {
    if (true) {
      let _el = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return {"enabled": false, "leadMins": 10, "quietStart": "22:00", "quietEnd": "07:00", "morningSummary": false, "summaryTime": "08:00"};
}
function setStoreNotifPrefs(v) {
  try {
    window.__skoochStoreNotifPrefs = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    localStorage.setItem("skooch_notif_prefs", JSON.stringify(v));
  } catch (__jac_e) {
    if (true) {
      let _els = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreNotifPrefsSubs) {
      _subs = window.__skoochStoreNotifPrefsSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreNotifPrefs(cb) {
  try {
    if (!window.__skoochStoreNotifPrefsSubs) {
      window.__skoochStoreNotifPrefsSubs = [];
    }
    window.__skoochStoreNotifPrefsSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreNotifPrefsSubs ? window.__skoochStoreNotifPrefsSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
function getStoreIsOnline() {
  try {
    if (((window.__skoochStoreIsOnline !== undefined) && (window.__skoochStoreIsOnline !== null))) {
      return window.__skoochStoreIsOnline;
    }
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  try {
    return navigator.onLine;
  } catch (__jac_e) {
    if (true) {
      let _en = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return true;
}
function setStoreIsOnline(v) {
  try {
    window.__skoochStoreIsOnline = v;
  } catch (__jac_e) {
    if (true) {
      let _e0 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _subs = [];
  try {
    if (window.__skoochStoreIsOnlineSubs) {
      _subs = window.__skoochStoreIsOnlineSubs;
    }
  } catch (__jac_e) {
    if (true) {
      let _e2 = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  for (const _cb of _subs) {
    try {
      _cb(v);
    } catch (__jac_e) {
      if (true) {
        let _cberr = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
}
function subscribeStoreIsOnline(cb) {
  try {
    if (!window.__skoochStoreIsOnlineSubs) {
      window.__skoochStoreIsOnlineSubs = [];
    }
    window.__skoochStoreIsOnlineSubs.push(cb);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return () => {
    try {
      let _subs = (window.__skoochStoreIsOnlineSubs ? window.__skoochStoreIsOnlineSubs : []);
      let _idx = _subs.indexOf(cb);
      if ((_idx >= 0)) {
        _subs.splice(_idx, 1);
      }
    } catch (__jac_e) {
      if (true) {
        let _eu = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
}
export {getStoreAccountClaimed, getStoreAgreedToTerms, getStoreAiActiveChatId, getStoreAiChatBusy, getStoreAiChatError, getStoreAiChatInput, getStoreAiChatMemory, getStoreAiChatMessages, getStoreAiPanelOpen, getStoreAiPendingPlan, getStoreAiSavedChats, getStoreAiShowHistory, getStoreAiTimeSuggestions, getStoreAnonDataBannerDismissed, getStoreAuthActionInProgress, getStoreAuthEmail, getStoreAuthError, getStoreAuthLoading, getStoreAuthMode, getStoreAuthPassword, getStoreAuthPasswordConfirm, getStoreAuthShowPassword, getStoreAuthShowPasswordConfirm, getStoreAuthSuccess, getStoreBufferMins, getStoreCategoryTiers, getStoreChatLearnedPrefs, getStoreChatLimit, getStoreChatMessagesUsedToday, getStoreClaimPending, getStoreClearingSavedDevSnapshot, getStoreCoachMarkStep, getStoreCurrentPage, getStoreDemoTourActive, getStoreDevMode, getStoreDevSyncScheduleAfterOptimize, getStoreDraggedSkillCat, getStoreDrawerCategory, getStoreDrawerDeadline, getStoreDrawerOpen, getStoreDrawerOverride, getStoreDrawerPriority, getStoreDrawerTaskId, getStoreDrawerTitle, getStoreDropTargetCat, getStoreDropTargetTier, getStoreEnergyCurve, getStoreEnergyData, getStoreEnergyShape, getStoreFixedBlocks, getStoreGcalCalLoading, getStoreGcalCalSaveState, getStoreGcalCalendars, getStoreGcalClock, getStoreGcalReadSelection, getStoreGcalStatus, getStoreGcalSyncing, getStoreGcalWriteSelection, getStoreGlobalRules, getStoreIcalImportedCalendars, getStoreIcalLastResult, getStoreIcalModalError, getStoreIcalModalMode, getStoreIcalModalOpen, getStoreIcalModalTab, getStoreIgnoreSavedDevSnapshot, getStoreIsAdmin, getStoreIsAnonymous, getStoreIsLoggedIn, getStoreIsMobileViewport, getStoreIsOnline, getStoreLandingPage, getStoreLearningTick, getStoreLegalReturnTo, getStoreLockPersonalTime, getStoreMobileMonthDots, getStoreMobileMonthOffset, getStoreMobileSandboxToast, getStoreMobileSchedView, getStoreModalBreakdown, getStoreModalCategory, getStoreModalDayFri, getStoreModalDayMon, getStoreModalDaySat, getStoreModalDaySun, getStoreModalDayThu, getStoreModalDayTue, getStoreModalDayWed, getStoreModalDeadline, getStoreModalEndTime, getStoreModalError, getStoreModalIsEvent, getStoreModalOpen, getStoreModalPriority, getStoreModalStartTime, getStoreModalSubtasks, getStoreModalTitle, getStoreNewRuleCategory, getStoreNewRuleEnd, getStoreNewRuleFri, getStoreNewRuleMaxHours, getStoreNewRuleMon, getStoreNewRuleSat, getStoreNewRuleStart, getStoreNewRuleSun, getStoreNewRuleThu, getStoreNewRuleTue, getStoreNewRuleType, getStoreNewRuleWed, getStoreNotifPrefs, getStoreOnboardingStep, getStoreOverflow, getStorePrefAiPanelDefault, getStorePrefBlockLength, getStorePrefEnergyDefault, getStorePrefFallBehind, getStorePrefFocusPeak, getStorePrefWorkStyle, getStoreProfileDirty, getStoreProfileLoaded, getStoreProfileSaveError, getStoreProfileSaveSuccess, getStoreProfileSaving, getStoreProfileSkipped, getStoreRememberMe, getStoreRunOptimizeInFlight, getStoreSaveErrorNotice, getStoreSchConCat, getStoreSchConEnd, getStoreSchConFri, getStoreSchConMax, getStoreSchConMon, getStoreSchConSat, getStoreSchConStart, getStoreSchConSun, getStoreSchConThu, getStoreSchConTue, getStoreSchConType, getStoreSchConWed, getStoreSchConstraints, getStoreSchDevScenarioActive, getStoreSchDirty, getStoreSchEditCat, getStoreSchEditDL, getStoreSchEditDate, getStoreSchEditDay, getStoreSchEditDays, getStoreSchEditDur, getStoreSchEditEnd, getStoreSchEditLoc, getStoreSchEditPri, getStoreSchEditStart, getStoreSchEditTitle, getStoreSchEvCat, getStoreSchEvDate, getStoreSchEvEnd, getStoreSchEvFri, getStoreSchEvIsAllDay, getStoreSchEvIsRecurring, getStoreSchEvLoc, getStoreSchEvMon, getStoreSchEvName, getStoreSchEvRecurrenceInterval, getStoreSchEvRecurrenceUnit, getStoreSchEvSat, getStoreSchEvStart, getStoreSchEvSun, getStoreSchEvThu, getStoreSchEvTue, getStoreSchEvWed, getStoreSchFormError, getStoreSchLog, getStoreSchOptimizeInputTotal, getStoreSchOptimized, getStoreSchOverflowExpandedIds, getStoreSchPopupBlock, getStoreSchPopupDirty, getStoreSchPopupOpen, getStoreSchPopupSaveState, getStoreSchPrefAfter, getStoreSchPrefBlock, getStoreSchPrefBuf, getStoreSchPrefPeak, getStoreSchPrefStrat, getStoreSchReasoningTarget, getStoreSchReportOpen, getStoreSchSidebarMode, getStoreSchTaskCat, getStoreSchTaskDL, getStoreSchTaskDur, getStoreSchTaskDurH, getStoreSchTaskDurM, getStoreSchTaskIsRecurring, getStoreSchTaskName, getStoreSchTaskPri, getStoreSchTaskRecurrenceInterval, getStoreSchTaskRecurrenceUnit, getStoreSchTaskRecurrenceWeekday, getStoreSchZoom, getStoreSchedule, getStoreScheduleHydrationEpoch, getStoreSchedulerTasks, getStoreSelectedDay, getStoreShowEulaModal, getStoreSleepImportance, getStoreSuggestions, getStoreTasks, getStoreThemeDark, getStoreToastCategory, getStoreToastDimension, getStoreToastFamiliarity, getStoreToastMCQOptions, getStoreToastPlannedMins, getStoreToastQuestionId, getStoreToastQuestionType, getStoreToastResponse, getStoreToastTargetEntityId, getStoreToastTargetSlot, getStoreToastVisible, getStoreToastWhyLine, getStoreUserId, getStoreViewMode, getStoreWeekOffset, getStoreWorkLifeBalance, setStoreAccountClaimed, setStoreAgreedToTerms, setStoreAiActiveChatId, setStoreAiChatBusy, setStoreAiChatError, setStoreAiChatInput, setStoreAiChatMemory, setStoreAiChatMessages, setStoreAiPanelOpen, setStoreAiPendingPlan, setStoreAiSavedChats, setStoreAiShowHistory, setStoreAiTimeSuggestions, setStoreAnonDataBannerDismissed, setStoreAuthActionInProgress, setStoreAuthEmail, setStoreAuthError, setStoreAuthLoading, setStoreAuthMode, setStoreAuthPassword, setStoreAuthPasswordConfirm, setStoreAuthShowPassword, setStoreAuthShowPasswordConfirm, setStoreAuthSuccess, setStoreBufferMins, setStoreCategoryTiers, setStoreChatLearnedPrefs, setStoreChatLimit, setStoreChatMessagesUsedToday, setStoreClaimPending, setStoreClearingSavedDevSnapshot, setStoreCoachMarkStep, setStoreCurrentPage, setStoreDemoTourActive, setStoreDevMode, setStoreDevSyncScheduleAfterOptimize, setStoreDraggedSkillCat, setStoreDrawerCategory, setStoreDrawerDeadline, setStoreDrawerOpen, setStoreDrawerOverride, setStoreDrawerPriority, setStoreDrawerTaskId, setStoreDrawerTitle, setStoreDropTargetCat, setStoreDropTargetTier, setStoreEnergyCurve, setStoreEnergyData, setStoreEnergyShape, setStoreFixedBlocks, setStoreGcalCalLoading, setStoreGcalCalSaveState, setStoreGcalCalendars, setStoreGcalClock, setStoreGcalReadSelection, setStoreGcalStatus, setStoreGcalSyncing, setStoreGcalWriteSelection, setStoreGlobalRules, setStoreIcalImportedCalendars, setStoreIcalLastResult, setStoreIcalModalError, setStoreIcalModalMode, setStoreIcalModalOpen, setStoreIcalModalTab, setStoreIgnoreSavedDevSnapshot, setStoreIsAdmin, setStoreIsAnonymous, setStoreIsLoggedIn, setStoreIsMobileViewport, setStoreIsOnline, setStoreLandingPage, setStoreLearningTick, setStoreLegalReturnTo, setStoreLockPersonalTime, setStoreMobileMonthDots, setStoreMobileMonthOffset, setStoreMobileSandboxToast, setStoreMobileSchedView, setStoreModalBreakdown, setStoreModalCategory, setStoreModalDayFri, setStoreModalDayMon, setStoreModalDaySat, setStoreModalDaySun, setStoreModalDayThu, setStoreModalDayTue, setStoreModalDayWed, setStoreModalDeadline, setStoreModalEndTime, setStoreModalError, setStoreModalIsEvent, setStoreModalOpen, setStoreModalPriority, setStoreModalStartTime, setStoreModalSubtasks, setStoreModalTitle, setStoreNewRuleCategory, setStoreNewRuleEnd, setStoreNewRuleFri, setStoreNewRuleMaxHours, setStoreNewRuleMon, setStoreNewRuleSat, setStoreNewRuleStart, setStoreNewRuleSun, setStoreNewRuleThu, setStoreNewRuleTue, setStoreNewRuleType, setStoreNewRuleWed, setStoreNotifPrefs, setStoreOnboardingStep, setStoreOverflow, setStorePrefAiPanelDefault, setStorePrefBlockLength, setStorePrefEnergyDefault, setStorePrefFallBehind, setStorePrefFocusPeak, setStorePrefWorkStyle, setStoreProfileDirty, setStoreProfileLoaded, setStoreProfileSaveError, setStoreProfileSaveSuccess, setStoreProfileSaving, setStoreProfileSkipped, setStoreRememberMe, setStoreRunOptimizeInFlight, setStoreSaveErrorNotice, setStoreSchConCat, setStoreSchConEnd, setStoreSchConFri, setStoreSchConMax, setStoreSchConMon, setStoreSchConSat, setStoreSchConStart, setStoreSchConSun, setStoreSchConThu, setStoreSchConTue, setStoreSchConType, setStoreSchConWed, setStoreSchConstraints, setStoreSchDevScenarioActive, setStoreSchDirty, setStoreSchEditCat, setStoreSchEditDL, setStoreSchEditDate, setStoreSchEditDay, setStoreSchEditDays, setStoreSchEditDur, setStoreSchEditEnd, setStoreSchEditLoc, setStoreSchEditPri, setStoreSchEditStart, setStoreSchEditTitle, setStoreSchEvCat, setStoreSchEvDate, setStoreSchEvEnd, setStoreSchEvFri, setStoreSchEvIsAllDay, setStoreSchEvIsRecurring, setStoreSchEvLoc, setStoreSchEvMon, setStoreSchEvName, setStoreSchEvRecurrenceInterval, setStoreSchEvRecurrenceUnit, setStoreSchEvSat, setStoreSchEvStart, setStoreSchEvSun, setStoreSchEvThu, setStoreSchEvTue, setStoreSchEvWed, setStoreSchFormError, setStoreSchLog, setStoreSchOptimizeInputTotal, setStoreSchOptimized, setStoreSchOverflowExpandedIds, setStoreSchPopupBlock, setStoreSchPopupDirty, setStoreSchPopupOpen, setStoreSchPopupSaveState, setStoreSchPrefAfter, setStoreSchPrefBlock, setStoreSchPrefBuf, setStoreSchPrefPeak, setStoreSchPrefStrat, setStoreSchReasoningTarget, setStoreSchReportOpen, setStoreSchSidebarMode, setStoreSchTaskCat, setStoreSchTaskDL, setStoreSchTaskDur, setStoreSchTaskDurH, setStoreSchTaskDurM, setStoreSchTaskIsRecurring, setStoreSchTaskName, setStoreSchTaskPri, setStoreSchTaskRecurrenceInterval, setStoreSchTaskRecurrenceUnit, setStoreSchTaskRecurrenceWeekday, setStoreSchZoom, setStoreSchedule, setStoreScheduleHydrationEpoch, setStoreSchedulerTasks, setStoreSelectedDay, setStoreShowEulaModal, setStoreSleepImportance, setStoreSuggestions, setStoreTasks, setStoreThemeDark, setStoreToastCategory, setStoreToastDimension, setStoreToastFamiliarity, setStoreToastMCQOptions, setStoreToastPlannedMins, setStoreToastQuestionId, setStoreToastQuestionType, setStoreToastResponse, setStoreToastTargetEntityId, setStoreToastTargetSlot, setStoreToastVisible, setStoreToastWhyLine, setStoreUserId, setStoreViewMode, setStoreWeekOffset, setStoreWorkLifeBalance, subscribeStoreAccountClaimed, subscribeStoreAgreedToTerms, subscribeStoreAiActiveChatId, subscribeStoreAiChatBusy, subscribeStoreAiChatError, subscribeStoreAiChatInput, subscribeStoreAiChatMemory, subscribeStoreAiChatMessages, subscribeStoreAiPanelOpen, subscribeStoreAiPendingPlan, subscribeStoreAiSavedChats, subscribeStoreAiShowHistory, subscribeStoreAiTimeSuggestions, subscribeStoreAnonDataBannerDismissed, subscribeStoreAuthActionInProgress, subscribeStoreAuthEmail, subscribeStoreAuthError, subscribeStoreAuthLoading, subscribeStoreAuthMode, subscribeStoreAuthPassword, subscribeStoreAuthPasswordConfirm, subscribeStoreAuthShowPassword, subscribeStoreAuthShowPasswordConfirm, subscribeStoreAuthSuccess, subscribeStoreBufferMins, subscribeStoreCategoryTiers, subscribeStoreChatLearnedPrefs, subscribeStoreChatLimit, subscribeStoreChatMessagesUsedToday, subscribeStoreClaimPending, subscribeStoreClearingSavedDevSnapshot, subscribeStoreCoachMarkStep, subscribeStoreCurrentPage, subscribeStoreDemoTourActive, subscribeStoreDevMode, subscribeStoreDevSyncScheduleAfterOptimize, subscribeStoreDraggedSkillCat, subscribeStoreDrawerCategory, subscribeStoreDrawerDeadline, subscribeStoreDrawerOpen, subscribeStoreDrawerOverride, subscribeStoreDrawerPriority, subscribeStoreDrawerTaskId, subscribeStoreDrawerTitle, subscribeStoreDropTargetCat, subscribeStoreDropTargetTier, subscribeStoreEnergyCurve, subscribeStoreEnergyData, subscribeStoreEnergyShape, subscribeStoreFixedBlocks, subscribeStoreGcalCalLoading, subscribeStoreGcalCalSaveState, subscribeStoreGcalCalendars, subscribeStoreGcalClock, subscribeStoreGcalReadSelection, subscribeStoreGcalStatus, subscribeStoreGcalSyncing, subscribeStoreGcalWriteSelection, subscribeStoreGlobalRules, subscribeStoreIcalImportedCalendars, subscribeStoreIcalLastResult, subscribeStoreIcalModalError, subscribeStoreIcalModalMode, subscribeStoreIcalModalOpen, subscribeStoreIcalModalTab, subscribeStoreIgnoreSavedDevSnapshot, subscribeStoreIsAdmin, subscribeStoreIsAnonymous, subscribeStoreIsLoggedIn, subscribeStoreIsMobileViewport, subscribeStoreIsOnline, subscribeStoreLandingPage, subscribeStoreLearningTick, subscribeStoreLegalReturnTo, subscribeStoreLockPersonalTime, subscribeStoreMobileMonthDots, subscribeStoreMobileMonthOffset, subscribeStoreMobileSandboxToast, subscribeStoreMobileSchedView, subscribeStoreModalBreakdown, subscribeStoreModalCategory, subscribeStoreModalDayFri, subscribeStoreModalDayMon, subscribeStoreModalDaySat, subscribeStoreModalDaySun, subscribeStoreModalDayThu, subscribeStoreModalDayTue, subscribeStoreModalDayWed, subscribeStoreModalDeadline, subscribeStoreModalEndTime, subscribeStoreModalError, subscribeStoreModalIsEvent, subscribeStoreModalOpen, subscribeStoreModalPriority, subscribeStoreModalStartTime, subscribeStoreModalSubtasks, subscribeStoreModalTitle, subscribeStoreNewRuleCategory, subscribeStoreNewRuleEnd, subscribeStoreNewRuleFri, subscribeStoreNewRuleMaxHours, subscribeStoreNewRuleMon, subscribeStoreNewRuleSat, subscribeStoreNewRuleStart, subscribeStoreNewRuleSun, subscribeStoreNewRuleThu, subscribeStoreNewRuleTue, subscribeStoreNewRuleType, subscribeStoreNewRuleWed, subscribeStoreNotifPrefs, subscribeStoreOnboardingStep, subscribeStoreOverflow, subscribeStorePrefAiPanelDefault, subscribeStorePrefBlockLength, subscribeStorePrefEnergyDefault, subscribeStorePrefFallBehind, subscribeStorePrefFocusPeak, subscribeStorePrefWorkStyle, subscribeStoreProfileDirty, subscribeStoreProfileLoaded, subscribeStoreProfileSaveError, subscribeStoreProfileSaveSuccess, subscribeStoreProfileSaving, subscribeStoreProfileSkipped, subscribeStoreRememberMe, subscribeStoreRunOptimizeInFlight, subscribeStoreSaveErrorNotice, subscribeStoreSchConCat, subscribeStoreSchConEnd, subscribeStoreSchConFri, subscribeStoreSchConMax, subscribeStoreSchConMon, subscribeStoreSchConSat, subscribeStoreSchConStart, subscribeStoreSchConSun, subscribeStoreSchConThu, subscribeStoreSchConTue, subscribeStoreSchConType, subscribeStoreSchConWed, subscribeStoreSchConstraints, subscribeStoreSchDevScenarioActive, subscribeStoreSchDirty, subscribeStoreSchEditCat, subscribeStoreSchEditDL, subscribeStoreSchEditDate, subscribeStoreSchEditDay, subscribeStoreSchEditDays, subscribeStoreSchEditDur, subscribeStoreSchEditEnd, subscribeStoreSchEditLoc, subscribeStoreSchEditPri, subscribeStoreSchEditStart, subscribeStoreSchEditTitle, subscribeStoreSchEvCat, subscribeStoreSchEvDate, subscribeStoreSchEvEnd, subscribeStoreSchEvFri, subscribeStoreSchEvIsAllDay, subscribeStoreSchEvIsRecurring, subscribeStoreSchEvLoc, subscribeStoreSchEvMon, subscribeStoreSchEvName, subscribeStoreSchEvRecurrenceInterval, subscribeStoreSchEvRecurrenceUnit, subscribeStoreSchEvSat, subscribeStoreSchEvStart, subscribeStoreSchEvSun, subscribeStoreSchEvThu, subscribeStoreSchEvTue, subscribeStoreSchEvWed, subscribeStoreSchFormError, subscribeStoreSchLog, subscribeStoreSchOptimizeInputTotal, subscribeStoreSchOptimized, subscribeStoreSchOverflowExpandedIds, subscribeStoreSchPopupBlock, subscribeStoreSchPopupDirty, subscribeStoreSchPopupOpen, subscribeStoreSchPopupSaveState, subscribeStoreSchPrefAfter, subscribeStoreSchPrefBlock, subscribeStoreSchPrefBuf, subscribeStoreSchPrefPeak, subscribeStoreSchPrefStrat, subscribeStoreSchReasoningTarget, subscribeStoreSchReportOpen, subscribeStoreSchSidebarMode, subscribeStoreSchTaskCat, subscribeStoreSchTaskDL, subscribeStoreSchTaskDur, subscribeStoreSchTaskDurH, subscribeStoreSchTaskDurM, subscribeStoreSchTaskIsRecurring, subscribeStoreSchTaskName, subscribeStoreSchTaskPri, subscribeStoreSchTaskRecurrenceInterval, subscribeStoreSchTaskRecurrenceUnit, subscribeStoreSchTaskRecurrenceWeekday, subscribeStoreSchZoom, subscribeStoreSchedule, subscribeStoreScheduleHydrationEpoch, subscribeStoreSchedulerTasks, subscribeStoreSelectedDay, subscribeStoreShowEulaModal, subscribeStoreSleepImportance, subscribeStoreSuggestions, subscribeStoreTasks, subscribeStoreThemeDark, subscribeStoreToastCategory, subscribeStoreToastDimension, subscribeStoreToastFamiliarity, subscribeStoreToastMCQOptions, subscribeStoreToastPlannedMins, subscribeStoreToastQuestionId, subscribeStoreToastQuestionType, subscribeStoreToastResponse, subscribeStoreToastTargetEntityId, subscribeStoreToastTargetSlot, subscribeStoreToastVisible, subscribeStoreToastWhyLine, subscribeStoreUserId, subscribeStoreViewMode, subscribeStoreWeekOffset, subscribeStoreWorkLifeBalance, useStoreSlice};
//# sourceMappingURL=store.js.map
